import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as d3 from 'd3'; // D3.js for chart visualization
// Updated lucide-react imports to include all necessary icons
import { XCircle, Info, Plus, Minus, Download, Rocket, Brain, Trash2, RotateCcw, LayoutDashboard, BarChart2, DollarSign, Eye, EyeOff, GitCompare, ArrowLeft, PlusCircle, List, TrendingDown, TrendingUp, Calculator, Shield, Percent, ClipboardList, ChartColumn, BanknoteArrowUp, OctagonAlert, Receipt, PieChart, Wallet, Clock, CheckCircle, AlertCircle, FolderOpen, } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, ComposedChart, Area, Bar } from 'recharts'; // Import Recharts components


// --- Global Constants & Variables ---
// All top-level constants are initialized here to ensure they are defined before use.
const FINNHUB_API_KEY_DEFAULT = "d4eji5pr01qrumpfpfegd4eji5pr01qrumpfpff0";
const MASSIVE_API_KEY_DEFAULT = "v5sYbEB5aWNCcfTrElPaakxb0h0p4HVo";
const GEMINI_API_KEY_DEFAULT = "AIzaSyCiQy5K_NXirPNWRJFWBtud2zdEKpIIF9U"; // Placeholder for your Gemini API Key

// API Base URLs
const FINNHUB_BASE_URL = "https://finnhub.io/api/v1";
const MASSIVE_BASE_URL = "https://api.massive.com"; // Adjusted for Massive API

const CACHE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';


// --- Local Storage Helper Functions ---
/**
 * Loads data from localStorage for a given key.
 * @param {string} key - The key for the localStorage item.
 * @param {any} defaultValue - The value to return if the key is not found or parsing fails.
 * @returns {any} The parsed data or defaultValue.
 */
const loadFromLocalStorage = (key, defaultValue) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
        console.error(`Error loading from localStorage for key "${key}":`, error);
        return defaultValue;
    }
};

/**
 * Saves data to localStorage for a given key.
 * @param {string} key - The key for the localStorage item.
 * @param {any} data - The data to store.
*/
const saveToLocalStorage = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
        console.error(`Error saving to localStorage for key "${key}":`, error);
    }
};


// --- Global Helper Functions ---
/**
 * Generates a reasonably unique ID string.
 * This function replaces crypto.randomUUID() for broader browser compatibility.
 * @returns {string} A unique ID string.
 */
const generateUniqueId = () => {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomPart}`;
};


// Formats a numerical value as USD currency (e.g., $1,234.56). Returns 'N/A' for invalid inputs.
const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

/**
 * Calculates the percentage change of a dividend trend line using linear regression.
 * @param {Array} historicalDividends - An array of dividend objects with 'date' and 'dividend' properties.
 * @returns {number|null} The percentage change of the trend line, or null if not calculable.
 */
const calculateDividendTrendPercentage = (historicalDividends) => {
    // A trend line requires at least two data points to calculate a slope.
    if (!historicalDividends || historicalDividends.length < 2) {
        return null;
    }

    // This is the corrected code
    const parsedData = historicalDividends.map(d => ({
        date: new Date(d.date),
        dividend: +d.dividend
    })).sort((a, b) => a.date - b.date);

    // Perform linear regression (logic adapted from HistoricalDividendsChart)
    const n = parsedData.length;
    const xSeries = d3.range(n); // Use indices for x-values
    const ySeries = parsedData.map(d => d.dividend);
    const sumX = d3.sum(xSeries);
    const sumY = d3.sum(ySeries);
    const sumXY = d3.sum(xSeries, (d, i) => d * ySeries[i]);
    const sumX2 = d3.sum(xSeries, d => d * d);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // The start and end values of the trend line
    const startValue = intercept; // At x=0
    const endValue = slope * (n - 1) + intercept; // At x=n-1

    if (startValue === 0 || isNaN(startValue) || isNaN(endValue)) {
        return null; // Cannot calculate percentage change if the starting point is zero or values are invalid
    }

    const percentageChange = ((endValue - startValue) / startValue);
    return percentageChange;
};




// Advanced Price Projection using Linear Regression and Confidence Intervals
const calculatePriceProjection = (historicalPrices, monthsToProject, currentPrice) => {
    if (!historicalPrices || historicalPrices.length < 2) {
        return { estimate: currentPrice, lowerBound: null, upperBound: null };
    }


    const sortedPrices = [...historicalPrices].sort((a, b) => new Date(a.date) - new Date(b.date));
    const lastKnownPrice = sortedPrices[sortedPrices.length - 1].close;


    const regressionData = sortedPrices.map((d, i) => ({ x: i, y: d.close }));
    const n = regressionData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    for (const d of regressionData) {
        sumX += d.x;
        sumY += d.y;
        sumXY += d.x * d.y;
        sumX2 += d.x * d.x;
    }
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const daysToProject = monthsToProject * 30.44;
    const estimatedPrice = lastKnownPrice + (slope * daysToProject);


    const dailyChanges = [];
    for (let i = 1; i < sortedPrices.length; i++) {
        dailyChanges.push(sortedPrices[i].close - sortedPrices[i - 1].close);
    }
    const stdDev = d3.deviation(dailyChanges) || 0;
    const projectedVolatility = stdDev * Math.sqrt(daysToProject);
    const lowerBound = estimatedPrice - (1.96 * projectedVolatility);
    const upperBound = estimatedPrice + (1.96 * projectedVolatility);




    return {
        estimate: estimatedPrice > 0 ? Number(estimatedPrice.toFixed(2)) : lastKnownPrice,
        lowerBound: lowerBound > 0 ? Number(lowerBound.toFixed(2)) : null,
        upperBound: upperBound > 0 ? Number(upperBound.toFixed(2)) : null,
    };
};

/**
 * Calculates a price projection using an Exponential Decay model based on EMA NAV decay method.
 * Fits an exponential curve to historical prices for better modeling of NAV erosion in ETFs like SVOL.
 * @param {Array} historicalPrices - Array of objects with 'date' and 'close' properties (assumed monthly ends, sorted).
 * @param {number} monthsToProject - The number of months to project into the future.
 * @param {number} currentPrice - The current price of the stock (used as fallback).
 * @returns {object} An object containing the estimated price and confidence bounds.
 */
const calculatePriceProjection_EMA = (historicalPrices, monthsToProject, currentPrice) => {
    // Require at least 12 data points for a meaningful fit (e.g., 1 year of monthly data).
    if (!historicalPrices || historicalPrices.length < 12) {
        return { estimate: currentPrice, lowerBound: null, upperBound: null };
    }
    const sortedPrices = [...historicalPrices].sort((a, b) => new Date(a.date) - new Date(b.date));
    const closes = sortedPrices.map(p => p.close);
    const n = closes.length;
    // Assume times are monthly indices (0 to n-1); for daily, could compute days from dates.
    let sum_t = 0, sum_ln = 0, sum_t_ln = 0, sum_t2 = 0;
    const ln_closes = [];
    for (let i = 0; i < n; i++) {
        const t = i; // Monthly time index.
        const ln = Math.log(closes[i]);
        ln_closes.push(ln);
        sum_t += t;
        sum_ln += ln;
        sum_t_ln += t * ln;
        sum_t2 += t * t;
    }
    // Least-squares fit: b = slope (monthly log decay rate), a = intercept.
    const denominator = (n * sum_t2 - sum_t * sum_t);
    if (denominator === 0) {
        return { estimate: currentPrice, lowerBound: null, upperBound: null };
    }
    const b = (n * sum_t_ln - sum_t * sum_ln) / denominator;
    const a = (sum_ln - b * sum_t) / n;
    // Project to future time (last time is n-1, add monthsToProject).
    const projected_time = (n - 1) + monthsToProject;
    const projected_ln = a + b * projected_time;
    const estimatedPrice = Math.exp(projected_ln);
    // Calculate residuals for volatility on log scale.
    let sum_sq_res = 0;
    for (let i = 0; i < n; i++) {
        const predicted_ln = a + b * i;
        const res = ln_closes[i] - predicted_ln;
        sum_sq_res += res * res;
    }
    const variance = sum_sq_res / (n - 1);
    const std = Math.sqrt(variance);
    // Projected volatility scales with sqrt(monthsToProject) for confidence interval.
    const projectedVolatility = std * Math.sqrt(monthsToProject);
    const lower_ln = projected_ln - (1.96 * projectedVolatility);
    const upper_ln = projected_ln + (1.96 * projectedVolatility);
    return {
        estimate: estimatedPrice > 0 ? Number(estimatedPrice.toFixed(2)) : currentPrice,
        lowerBound: Math.exp(lower_ln) > 0 ? Number(Math.exp(lower_ln).toFixed(2)) : null,
        upperBound: Math.exp(upper_ln) > 0 ? Number(Math.exp(upper_ln).toFixed(2)) : null,
    };
};



// Formats a numerical value as a percentage (e.g., 12.34%). Rounds to 2 decimal places.
const formatPercentage = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value); // Corrected: Removed the "/ 100"
};


// Formats a numerical value as an exact percentage (e.g., 5.75%). Does not round.
const formatExactPercentage = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }
    return `${(value * 100).toFixed(4).replace(/\.0+$/, '')}%`;
};




// Formats a numerical value as a decimal to a specified number of decimal places.
const formatRatio = (value, decimals = 4) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }
    return value.toFixed(decimals);
};


// Formats large numbers for AUM display (e.g., $1.23B, $45.67M)
const formatLargeNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000_000_000) return `${(value / 1_000_000_000_000).toFixed(2)}T`;
    if (absValue >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
    if (absValue >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (absValue >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
    return value.toFixed(2);
};


// Calculates the Unix timestamp (milliseconds UTC) representing 00:00:00 PST for the current calendar day.
const getStartOfDayPST = () => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZone: 'America/Los_Angeles',
        hour12: false
    });




    const parts = formatter.formatToParts(now);
    let year, month, day;
    for (const part of parts) {
        if (part.type === 'year') year = parseInt(part.value);
        if (part.type === 'month') month = parseInt(part.value);
        if (part.type === 'day') day = parseInt(part.value);
    }




    const localMidnightInPST = new Date(`${month}/${day}/${year} 00:00:00 America/Los_Angeles`);
    return localMidnightInPST.getTime();
};




// Calculates shares, dividends, dividendPerDollar, and yieldValue based on stock data
const calculateStockMetrics = (stock) => {
    let shares = null;
    let dividends = null;
    let dividendPerDollar = null;
    let yieldValue = null;




    // This logic now reads the dividendFrequency property from the stock object.
    let dividendMultiplier;
    switch (stock.dividendFrequency) {
        case 'weekly':
            dividendMultiplier = 52;
            break;
        case 'quarterly':
            dividendMultiplier = 4;
            break;
        case 'monthly':
        default: // Defaults to monthly if the frequency isn't set
            dividendMultiplier = 12;
            break;
    }




    if (stock.amountInvested > 0 && stock.price > 0) {
        shares = stock.amountInvested / stock.price;
    }
    if (shares !== null && stock.lastDividend > 0) {
        // Calculates the total annual dividend based on the correct multiplier.
        dividends = shares * stock.lastDividend * dividendMultiplier;
    }
    if (stock.amountInvested > 0 && dividends !== null) {
        dividendPerDollar = dividends / stock.amountInvested;
    }
    if (stock.price > 0 && stock.lastDividend > 0) {
        yieldValue = (stock.lastDividend * dividendMultiplier) / stock.price;
    }




    return { shares, dividends, dividendPerDollar, yieldValue };
};




// Calculates margin interest based on Robinhood's tiered rates
const calculateMarginInterest = (balance, isGoldMember) => {
    let initialBalance = balance;




    const tiers = [
        // The first $1,000 for Gold members is still free.
        // Non-Gold rate updated.
        { threshold: 0, limit: 1000, rateNonGold: 0.0675, rateGold: 0, displayRange: 'Up to $1,000' },
        // New rate for the $1k - $50k tier: Gold is 5% (0.05), Non-Gold is 6.75% (0.0675)
        { threshold: 1000, limit: 50000, rateNonGold: 0.0675, rateGold: 0.0500, displayRange: '$1,000 - $50,000' },
        // New rate for the $50k - $100k tier: Gold is 4.8% (0.048), Non-Gold is 6.55% (0.0655)
        { threshold: 50000, limit: 100000, rateNonGold: 0.0655, rateGold: 0.0480, displayRange: '$50,000 - $100,000' },
        // New rate for the $100k - $1M tier: Gold is 4.5% (0.045), Non-Gold is 6.25% (0.0625)
        { threshold: 100000, limit: 1000000, rateNonGold: 0.0625, rateGold: 0.0450, displayRange: '$100,000 - $1,000,000' },
        // New rate for the $1M - $10M tier: Gold is 4.25% (0.0425), Non-Gold is 6.00% (0.06)
        { threshold: 1000000, limit: 10000000, rateNonGold: 0.0600, rateGold: 0.0425, displayRange: '$1,000,000 - $10,000,000' },
        // New rate for the $10M - $50M tier: Gold is 4.2% (0.042), Non-Gold is 5.95% (0.0595)
        { threshold: 10000000, limit: 50000000, rateNonGold: 0.0595, rateGold: 0.0420, displayRange: '$10,000,000 - $50,000,000' },
        // New rate for balances over $50M: Gold is 3.95% (0.0395), Non-Gold is 5.70% (0.057)
        { threshold: 50000000, limit: Infinity, rateNonGold: 0.0570, rateGold: 0.0395, displayRange: 'Over $50,000,000' }
    ]




    let totalAnnualInterest = 0;
    let remainingForTierCalculation = initialBalance;
    const interestPerTier = [];


    if (initialBalance <= 0) {
        return { daily: 0, annual: 0, monthly: 0, effectiveAnnualRate: 0, interestPerTier: [] };
    }


    tiers.forEach((tier) => {
        if (remainingForTierCalculation > 0) {
            let tierRate = isGoldMember ? tier.rateGold : tier.rateNonGold;
            let amountInThisTier = 0;




            if (isGoldMember && tier.threshold === 0) {
                if (initialBalance > 0) {
                    amountInThisTier = Math.min(initialBalance, 1000);
                    interestPerTier.push({
                        range: tier.displayRange,
                        borrowedAmountInTier: amountInThisTier,
                        annualInterestInTier: 0,
                        monthlyInterestInTier: 0,
                        rate: '0%',
                        isGoldFree: true
                    });
                    remainingForTierCalculation = Math.max(0, remainingForTierCalculation - 1000);
                }
            } else {
                const currentTierMaxAmount = tier.limit - tier.threshold;
                amountInThisTier = Math.min(remainingForTierCalculation, currentTierMaxAmount);


                if (amountInThisTier > 0) {
                    const annualInterestInTier = amountInThisTier * tierRate;
                    totalAnnualInterest += annualInterestInTier;


                    interestPerTier.push({
                        range: tier.displayRange,
                        borrowedAmountInTier: amountInThisTier,
                        annualInterestInTier: annualInterestInTier,
                        monthlyInterestInTier: annualInterestInTier / 12,
                        rate: formatExactPercentage(tierRate),
                        isGoldFree: false
                    });
                    remainingForTierCalculation -= amountInThisTier;
                }
            }
        }
    });




    const dailyInterest = totalAnnualInterest / 360;
    const monthlyInterest = totalAnnualInterest / 12;
    const effectiveAnnualRate = initialBalance > 0 ? totalAnnualInterest / initialBalance : 0;


    return { daily: dailyInterest, annual: totalAnnualInterest, monthly: monthlyInterest, effectiveAnnualRate, interestPerTier };
};


// --- Reusable UI Component: Modal ---
const Modal = ({ show, title, message, onClose, children }) => {
    if (!show) {
        return null;
    }


    return (
        <div className="fixed-overlay">
            <div className="modal-content card-expressive">
                <button onClick={onClose} className="modal-close-button">
                    <XCircle size={24} />
                </button>
                <h3 className="modal-title">{title}</h3>
                {message && <p className="modal-message custom-scrollbar">{message}</p>}
                <div className="modal-body custom-scrollbar">
                    {children}
                </div>
            </div>
        </div>
    );
};


// --- D3 Chart Components (extracted to top-level) ---
const HistoricalDividendsChart = ({ data }) => {
    const svgRef = useRef();

    useEffect(() => {
        // Ensure there are at least two data points to draw a trend line
        if (!data || data.length < 2) return;
        const svg = d3.select(svgRef.current);
        const container = d3.select(svgRef.current.parentNode);
        // Clear previous chart elements before redrawing
        svg.selectAll("*").remove();

        const containerWidth = container.node().clientWidth;
        const containerHeight = container.node().clientHeight;

        // Define chart margins and dimensions
        const margin = { top: 40, right: 40, bottom: 120, left: 100 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        // Create the main SVG group element
        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Parse and sort the data
        const parsedData = data.map(d => ({
            date: new Date(d.date),
            dividend: +d.dividend
        })).sort((a, b) => a.date - b.date);

        // Set up the X scale (for dates)
        const x = d3.scaleBand()
            .rangeRound([0, width])
            .padding(0.1)
            .domain(parsedData.map(d => d.date));




        // Set up the Y scale (for dividend amounts)
        const y = d3.scaleLinear()
            .rangeRound([height, 0])
            .domain([0, d3.max(parsedData, d => d.dividend) * 1.1]);




        // Render the X axis
        g.append("g")
            .attr("class", "axis axis--x")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(x).tickFormat(d3.timeFormat("%b %Y"))
                .tickValues(x.domain().filter((d, i) => !(i % Math.ceil(parsedData.length / 5)))))
            .selectAll("text")
            .attr("transform", "rotate(-45)")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .style("font-size", "12px");




        // Render the Y axis
        g.append("g")
            .attr("class", "axis axis--y")
            .call(d3.axisLeft(y).ticks(5).tickFormat(formatCurrency))
            .append("text")
            .attr("fill", "#000")
            .attr("transform", "rotate(-90)")
            .attr("y", -60)
            .attr("x", - (height / 2))
            .attr("dy", "0.71em")
            .style("text-anchor", "middle")
            .text("Dividend ($)")
            .style("font-size", "14px");




        // Render the bars for the bar chart
        g.selectAll(".bar")
            .data(parsedData)
            .enter().append("rect")
            .attr("class", "bar-chart-bar")
            .attr("x", d => x(d.date))
            .attr("y", d => y(d.dividend))
            .attr("width", x.bandwidth())
            .attr("height", d => height - y(d.dividend));




        // --- START: Trend Line Calculation ---
        // Perform linear regression to find the line of best fit.
        const n = parsedData.length;
        const xSeries = d3.range(n); // Use indices as the x-values for regression
        const ySeries = parsedData.map(d => d.dividend);




        const sumX = d3.sum(xSeries);
        const sumY = d3.sum(ySeries);
        const sumXY = d3.sum(xSeries, (d, i) => d * ySeries[i]);
        const sumX2 = d3.sum(xSeries, d => d * d);




        // Calculate the slope (m) and y-intercept (b) of the trend line
        const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;




        // Determine the start and end points of the trend line based on the regression
        const trendStart = { x: 0, y: slope * 0 + intercept };
        const trendEnd = { x: n - 1, y: slope * (n - 1) + intercept };
        // --- END: Trend Line Calculation ---




        // --- START: Draw Trend Line ---
        // Append a line element to the SVG for the trend line
        g.append("line")
            .attr("class", "trend-line")
            // The x-coordinates are the center of the first and last bars
            .attr("x1", x(parsedData[trendStart.x].date) + x.bandwidth() / 2)
            .attr("y1", y(trendStart.y))
            .attr("x2", x(parsedData[trendEnd.x].date) + x.bandwidth() / 2)
            .attr("y2", y(trendEnd.y))
            // Style the trend line to be visible
            .attr("stroke", "var(--color-red-500)")
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "5,5"); // Dashed line for visual distinction
        // --- END: Draw Trend Line ---




    }, [data]);




    return <svg ref={svgRef} id="historical-dividend-chart" className="chart-svg-full"></svg>;
};

const HistoricalPriceChart = ({ data }) => {
    const svgRef = useRef();

    useEffect(() => {
        if (!data || data.length < 2) return;

        const svg = d3.select(svgRef.current);
        const container = d3.select(svgRef.current.parentNode);
        svg.selectAll("*").remove();

        const containerWidth = container.node().clientWidth;
        const containerHeight = container.node().clientHeight;

        const margin = { top: 40, right: 40, bottom: 120, left: 100 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const parsedData = data.map(d => ({
            date: new Date(d.date),
            close: +d.close
        })).sort((a, b) => a.date - b.date);

        const x = d3.scaleTime()
            .rangeRound([0, width])
            .domain(d3.extent(parsedData, d => d.date));

        const y = d3.scaleLinear()
            .rangeRound([height, 0])
            .domain([d3.min(parsedData, d => d.close) * 0.95, d3.max(parsedData, d => d.close) * 1.05]);

        g.append("g")
            .attr("class", "axis axis--x")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(x).tickFormat(d3.timeFormat("%b %Y")))
            .selectAll("text")
            .attr("transform", "rotate(-45)")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .style("font-size", "12px");

        g.append("g")
            .attr("class", "axis axis--y")
            .call(d3.axisLeft(y).ticks(5).tickFormat(formatCurrency))
            .append("text")
            .attr("fill", "#000")
            .attr("transform", "rotate(-90)")
            .attr("y", -60)
            .attr("x", - (height / 2))
            .attr("dy", "0.71em")
            .style("text-anchor", "middle")
            .text("Price ($)")
            .style("font-size", "14px");

        const line = d3.line()
            .x(d => x(d.date))
            .y(d => y(d.close));

        g.append("path")
            .datum(parsedData)
            .attr("fill", "none")
            .attr("stroke", "var(--color-blue-600)")
            .attr("stroke-linejoin", "round")
            .attr("stroke-linecap", "round")
            .attr("stroke-width", 2)
            .attr("d", line);

    }, [data]);

    return <svg ref={svgRef} id="historical-price-chart" className="chart-svg-full"></svg>;
};


// --- Total Return Calculator Component ---
const TSRCalculator = ({ initialInvestmentCapital, tickerData, getAiSummaryProp, isAiSummaryLoadingProp, setModalTitleProp, setModalMessageProp, setShowModalProp, totalReturnPercentageProp, totalReturnInUSDProp, selectedTimePeriodProp }) => {
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('1Yr');
    const [selectedReinvestmentPercentage, setSelectedReinvestmentPercentage] = useState(0);
    const [selectedTaxOption, setSelectedTaxOption] = useState('none');
    const [totalReturnPercentage, setTotalReturnPercentage] = useState(null);
    const [totalReturnInUSD, setTotalReturnInUSD] = useState(null);
    const [totalDividendsPaidOut, setTotalDividendsPaidOut] = useState(null);
    const [capitalGainLoss, setCapitalGainLoss] = useState(null);
    const [capitalGainLossPercentage, setCapitalGainLossPercentage] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');


    const calculateTSR = useCallback(() => {
        if (initialInvestmentCapital <= 0 || !tickerData || !tickerData.ticker || !tickerData.historicalPrices || tickerData.historicalPrices.length === 0) {
            setErrorMessage('Please enter a valid initial investment and ensure stock has historical price data.');
            setTotalReturnPercentage(null);
            setTotalReturnInUSD(null);
            setTotalDividendsPaidOut(null);
            setCapitalGainLoss(null);
            setCapitalGainLossPercentage(null);
            return;
        }


        setErrorMessage('');


        const prices = tickerData.historicalPrices
            .map(d => ({ date: new Date(d.date), price: +d.close }))
            .sort((a, b) => a.date - b.date);


        const dividends = tickerData.historicalDividends
            ? tickerData.historicalDividends.map(d => ({ date: new Date(d.date), dividendPerShare: +d.dividend }))
                .sort((a, b) => a.date - b.date)
            : [];


        const currentDate = prices[prices.length - 1].date;
        let startDate;


        if (selectedTimePeriod === '1Yr') {
            startDate = new Date(currentDate);
            startDate.setFullYear(startDate.getFullYear() - 1);
        } else if (selectedTimePeriod === '3Yr') {
            startDate = new Date(currentDate);
            startDate.setFullYear(startDate.getFullYear() - 3);
        } else if (selectedTimePeriod === '5Yr') {
            startDate = new Date(currentDate);
            startDate.setFullYear(startDate.getFullYear() - 5);
        } else {
            setErrorMessage('Invalid time period selected.');
            setTotalReturnPercentage(null);
            setTotalReturnInUSD(null);
            setTotalDividendsPaidOut(null);
            setCapitalGainLoss(null);
            setCapitalGainLossPercentage(null);
            return;
        }


        const startPriceDataPoint = prices.find(d => d.date >= startDate);




        if (!startPriceDataPoint || startPriceDataPoint.price === null || startPriceDataPoint.price <= 0) {
            setErrorMessage(`No valid starting price data found for the selected period (${selectedTimePeriod}).`);
            setTotalReturnPercentage(null);
            setTotalReturnInUSD(null);
            setTotalDividendsPaidOut(null);
            setCapitalGainLoss(null);
            setCapitalGainLossPercentage(null);
            return;
        }




        let currentShares = initialInvestmentCapital / startPriceDataPoint.price;
        const initialInvested = initialInvestmentCapital;
        let accumulatedCashDividendsReceived = 0;


        const initialSharesForCapitalGain = initialInvestmentCapital / startPriceDataPoint.price;


        const calculatedCapitalGainLoss = (prices[prices.length - 1].price - startPriceDataPoint.price) * initialSharesForCapitalGain;
        setCapitalGainLoss(calculatedCapitalGainLoss);




        const calculatedCapitalGainLossPercentage = (calculatedCapitalGainLoss / initialInvested) * 100;
        setCapitalGainLossPercentage(calculatedCapitalGainLossPercentage);


        const relevantPrices = prices.filter(d => d.date >= startPriceDataPoint.date && d.date <= currentDate);




        let taxRateMultiplier = 1;
        if (selectedTaxOption === 'ordinary') {
            taxRateMultiplier = 0.80;
        } else if (selectedTaxOption === 'longTerm') {
            taxRateMultiplier = 0.90;
        }


        for (let i = 0; i < relevantPrices.length; i++) {
            const pricePoint = relevantPrices[i];
            const dividendForDate = dividends.find(d => d.date.toDateString() === pricePoint.date.toDateString());


            if (dividendForDate && dividendForDate.dividendPerShare) {
                const grossDividendsEarned = currentShares * dividendForDate.dividendPerShare;
                const netDividendsAvailableForDecision = grossDividendsEarned * taxRateMultiplier;
                const reinvestmentAmountThisPeriod = netDividendsAvailableForDecision * (selectedReinvestmentPercentage / 100);
                const cashPayoutThisPeriod = netDividendsAvailableForDecision - reinvestmentAmountThisPeriod;


                accumulatedCashDividendsReceived += cashPayoutThisPeriod;


                if (pricePoint.price > 0) {
                    const newSharesFromReinvestment = reinvestmentAmountThisPeriod / pricePoint.price;
                    currentShares += newSharesFromReinvestment;
                }
            }
        }




        const finalPortfolioValue = currentShares * prices[prices.length - 1].price;
        const calculatedTotalReturnInUSD = finalPortfolioValue - initialInvested + accumulatedCashDividendsReceived;
        const calculatedTotalReturnPercentage = (calculatedTotalReturnInUSD / initialInvested) * 100;




        setTotalReturnPercentage(calculatedTotalReturnPercentage);
        setTotalReturnInUSD(calculatedTotalReturnInUSD);
        setTotalDividendsPaidOut(accumulatedCashDividendsReceived);




    }, [initialInvestmentCapital, selectedTimePeriod, selectedReinvestmentPercentage, selectedTaxOption, tickerData]);


    useEffect(() => {
        calculateTSR();
    }, [calculateTSR]);
































    return (
        <div className="spacing-y-6">
            <p className="text-gray-700 text-sm">
                Calculate Total Shareholder Return for <span className="font-semibold">{tickerData?.ticker || 'N/A'}</span>.
            </p>
















            <div className="flex-wrap-center gap-2">
                {['1Yr', '3Yr', '5Yr'].map(period => (
                    <button
                        key={period}
                        onClick={() => setSelectedTimePeriod(period)}
                        className={`button-period ${selectedTimePeriod === period ? 'button-period-active' : ''}`}
                    >
                        {period}
                    </button>
                ))}
            </div>
















            <div className="flex-wrap-center gap-2 margin-top-4">
                {[0, 25, 50, 75, 100].map(percentage => (
                    <button
                        key={percentage}
                        onClick={() => setSelectedReinvestmentPercentage(percentage)}
                        className={`button-reinvest ${selectedReinvestmentPercentage === percentage ? 'button-reinvest-active' : ''}`}
                    >
                        {percentage}% Reinvest
                    </button>
                ))}
            </div>
















            <div className="flex-wrap-center gap-2 margin-top-4">
                {[{ label: 'No Tax', value: 'none' }, { label: 'Ordinary (20%)', value: 'ordinary' }, { label: 'Long Term (10%)', value: 'longTerm' }].map(taxOption => (
                    <button
                        key={taxOption.value}
                        onClick={() => setSelectedTaxOption(taxOption.value)}
                        className={`button-tax ${selectedTaxOption === taxOption.value ? 'button-tax-active' : ''}`}
                    >
                        {taxOption.label}
                    </button>
                ))}
            </div>
































            <div className="margin-top-8 text-center">
                {errorMessage && (
                    <p className="text-red-500 font-semibold margin-bottom-4">{errorMessage}</p>
                )}
                {totalReturnPercentage !== null && (
                    <>
                        <p className="text-xl margin-top-2">
                            Capital Gain/Loss:
                            <span className={`margin-left-2 ${capitalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(capitalGainLoss)}
                            </span>
                        </p>
                        <p className="text-lg margin-top-2">
                            Capital Gain/Loss %:
                            <span className={`margin-left-2 ${capitalGainLossPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {capitalGainLossPercentage !== null ? capitalGainLossPercentage.toFixed(2) + '%' : 'N/A'}
                            </span>
                        </p>
                        <p className="text-lg margin-top-2">
                            Total Dividends Paid Out (After Tax): {formatCurrency(totalDividendsPaidOut)}
                        </p>
                        <p className="text-2xl font-bold margin-top-2">
                            Total Return:
                            <span className={`margin-left-2 ${totalReturnPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {totalReturnPercentage.toFixed(2)}%
                            </span>
                        </p>
                        <p className="text-lg margin-top-2">
                            Total Return ($):
                            <span className={`margin-left-2 ${totalReturnInUSD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(totalReturnInUSD)}
                            </span>
                        </p>
















                        <div className="margin-top-6 flex-wrap-center">
                            <button
                                onClick={() => getAiSummaryProp(
                                    {
                                        ticker: tickerData?.ticker,
                                        companyName: tickerData?.companyName,
                                        totalReturnPercentage: totalReturnPercentage,
                                        totalReturnInUSD: totalReturnInUSD,
                                        timePeriod: selectedTimePeriod,
                                        initialInvestment: initialInvestmentCapital
                                    },
                                    true
                                )}
                                className="action-button button-pink"
                                disabled={isAiSummaryLoadingProp || !totalReturnPercentage || !tickerData?.ticker}
                                title="Generate AI Summary for TSR"
                            >
                                {isAiSummaryLoadingProp ? (
                                    <span className="animate-spin button-icon-margin-right-sm">🌀</span>
                                ) : (
                                    <Brain size={16} className="button-icon-margin-right-sm" />
                                )}
                                <span>Explain TSR with AI</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};


// --- Reusable UI Component: Custom Tooltip for Charts ---
const CustomChartTooltip = ({ active, payload, label, tickers, valueFormatter }) => {
    if (active && payload && payload.length) {
        const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00bcd4'];
        const tooltipLabelFormatter = (timestamp) => {
            return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        };

        return (
            <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-md text-sm">
                <p className="font-bold text-gray-800 mb-1">{tooltipLabelFormatter(label)}</p>
                {tickers.map((ticker, index) => {
                    const entry = payload.find(p => p.dataKey === ticker);
                    // Ensure the entry has a value before trying to format it
                    if (entry && entry.value !== null && entry.value !== undefined) {
                        const color = COLORS[index % COLORS.length];
                        return (
                            <p key={`item-${ticker}`} style={{ color: color }}>
                                {`${ticker}: ${valueFormatter(entry.value)}`}
                            </p>
                        );
                    }
                    return null;
                })}
            </div>
        );
    }
    return null;
};













// --- Helper Component  ---
const UITooltip = ({ children, content }) => {
    const [isVisible, setIsVisible] = useState(false);
    return (
        <div className="relative inline-block">
            <div
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                className="cursor-pointer"
            >
                {children}
            </div>
            {isVisible && (
                <div className="absolute z-50 p-2 text-xs text-white bg-gray-800 rounded-md shadow-lg tooltip-text-style"
                    style={{ top: '-8px', left: '50%', transform: 'translateX(-50%) translateY(-100%)', width: 'max-content', maxWidth: '200px' }}>
                    {content}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-800"></div>
                </div>
            )}
        </div>
    );
};
















// Infographic Display Component to show a visual summary of calculations
const InfographicDisplay = ({ data, onBack }) => {
    const {
        shares, buyPrice, calculatedCost, dividendAveragePerShare,
        calculatedDividendAnnual, dividendFrequency, netDividendAnr,
        insurancePut, putStrike, sellPutCredit, taxRate, calculatedTaxAmount,
        oneYrStockPriceEst, capAfter1Yr, calculatedOneYearLossGain,
        calculatedCapitalLoss, ttlAfter1Yr, gainPercentage, capitalGain,
        initialCostPct, lossesPct, gainsPct
    } = data;

    const getTextColorClass = (value) => {
        if (value > 0) return 'text-green-400';
        if (value < 0) return 'text-red-400';
        return 'text-e2e8f0';
    };

    const getPercentageColorClass = (value) => {
        if (value >= 0) return 'text-emerald-400';
        return 'text-red-400';
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 sm:p-6 lg:p-8 font-inter antialiased">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-7xl flex flex-col lg:flex-row gap-8">
                <div className="flex-1 lg:pr-4">
                    <div className="flex items-center justify-between mb-8">
                        <h1 className="text-4xl font-extrabold text-gray-900 flex items-center gap-3">
                            <Calculator className="h-10 w-10 text-blue-600" /> Price Graph
                        </h1>
                        <button
                            onClick={onBack}
                            className="p-3 rounded-full bg-blue-500 text-white shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition-all duration-200 ease-in-out flex items-center gap-2"
                            aria-label="Back to Calculator"
                        >
                            <ArrowLeft className="h-6 w-6" />
                            <span className="hidden md:inline">Back to Calculator</span>
                        </button>
                    </div>

                    <div className="infographic-container-wrapper">
                        <h1 className="infographic-main-title">Price Graph Overview</h1>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
                            <div className="infographic-section-card border-indigo-500 flex items-center space-x-8">
                                <svg className="icon-large text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.592 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.592-1M12 8h2.592m-2.592 0A2.995 2.095 0 0112 7.5m0 0H9.408m-2.592 1A2.995 2.095 0 0112 16.5m0 0H14.592m-2.592 0A2.995 2.095 0 0112 17.5m0 0H9.408"></path></svg>
                                <div>
                                    <p className={`value-text ${getPercentageColorClass(ttlAfter1Yr)}`}>${(Number(ttlAfter1Yr) || 0).toFixed(2)}</p>
                                    <p className="label-text">Total After 1 Year</p>
                                </div>
                            </div>
                            <div className="infographic-section-card border-emerald-500 flex items-center space-x-8">
                                <svg className="icon-large text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
                                <div>
                                    <p className={`value-text ${getPercentageColorClass(gainPercentage)}`}>{(Number(gainPercentage) || 0).toFixed(2)}%</p>
                                    <p className="label-text">Gain/Loss Percentage</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            <div className="infographic-section-card border-fuchsia-500">
                                <h2 className="infographic-sub-title text-fuchsia-300">
                                    <svg className="icon-medium mr-3 text-fuchsia-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                                    Stock & Dividend Details
                                </h2>
                                <div className="space-y-4">
                                    <div className="detail-item">
                                        <span className="detail-label">Shares Purchased:</span>
                                        <span className="detail-value">{shares}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Buy Price:</span>
                                        <span className="detail-value">${(Number(buyPrice) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Initial Cost:</span>
                                        <span className={`detail-value ${getTextColorClass(calculatedCost)}`}>${(Number(calculatedCost) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Dividend Annual Avg:</span>
                                        <span className="detail-value">${(Number(calculatedDividendAnnual) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Dividend Frequency:</span>
                                        <span className="detail-value">{dividendFrequency === 'monthly' ? 'Monthly' : 'Quarterly'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Net Dividend Annual:</span>
                                        <span className={`detail-value ${getTextColorClass(netDividendAnr)}`}>${(Number(netDividendAnr) || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="infographic-section-card border-orange-500">
                                <h2 className="infographic-sub-title text-orange-300">
                                    <svg className="icon-medium mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.001 12.001 0 002.92 12c0 3.072 1.88 5.727 4.505 7.154L12 22.016l4.575-2.862C20.12 17.727 22 15.072 22 12c0-3.328-1.594-6.42-4.382-8.016z"></path></svg>
                                    Options & Taxation
                                </h2>
                                <div className="space-y-4">
                                    <div className="detail-item">
                                        <span className="detail-label">Insurance Put:</span>
                                        <span className="detail-value">${(Number(insurancePut) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Put Strike:</span>
                                        <span className="detail-value">${(Number(putStrike) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Sold Put Credits:</span>
                                        <span className="detail-value text-green-400">${(Number(sellPutCredit) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Tax Rate:</span>
                                        <span className="detail-value">{(Number(taxRate) * 100).toFixed(0)}%</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Tax Amount:</span>
                                        <span className="detail-value text-red-400">${(Number(calculatedTaxAmount) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">1 Year Stock Price Est:</span>
                                        <span className="detail-value">${(Number(oneYrStockPriceEst) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Capital After 1 Year:</span>
                                        <span className="detail-value">${(Number(capAfter1Yr) || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="infographic-section-card border-red-500">
                                <h2 className="infographic-sub-title text-red-300">
                                    <svg className="icon-medium mr-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"></path></svg>
                                    Losses
                                </h2>
                                <div className="space-y-4">
                                    <div className="detail-item">
                                        <span className="detail-label">Capital Loss:</span>
                                        <span className="detail-value text-red-400">${(Number(calculatedCapitalLoss) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Tax:</span>
                                        <span className="detail-value text-red-400">${(Number(calculatedTaxAmount) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Put Insurance Cost:</span>
                                        <span className="detail-value text-red-400">${(Number(insurancePut) || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="infographic-section-card border-green-500">
                                <h2 className="infographic-sub-title text-green-300">
                                    <svg className="icon-medium mr-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
                                    Gains
                                </h2>
                                <div className="space-y-4">
                                    <div className="detail-item">
                                        <span className="detail-label">Capital Gain:</span>
                                        <span className="detail-value text-green-400">${(Number(capitalGain) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Dividends:</span>
                                        <span className="detail-value text-green-400">${(Number(calculatedDividendAnnual) || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Sold Put Credits:</span>
                                        <span className="detail-value text-green-400">${(Number(sellPutCredit) || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="infographic-chart-section">
                            <h3 className="infographic-chart-title-sub">Financial Component Distribution</h3>
                            <div className="stacked-bar">
                                <div className="bg-blue-600" style={{ width: `${initialCostPct}%` }}>Initial Cost ({initialCostPct}%)</div>
                                <div className="bg-red-600" style={{ width: `${lossesPct}%` }}>Losses ({lossesPct}%)</div>
                                <div className="bg-green-600" style={{ width: `${gainsPct}%` }}>Gains ({gainsPct}%)</div>
                            </div>
                            <p className="text-lg text-gray-300 max-w-3xl mx-auto mt-6">
                                This bar illustrates the proportional distribution of the initial investment, total losses, and total gains within the overall financial activity.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


// Initial template for a new scenario
const initialScenarioTemplate = {
    shares: 400,
    dividendAveragePerShare: 1.87,
    insurancePut: 900,
    timeInDays: 198,
    putStrike: 18,
    puts: 2,
    taxRate: 0.30,
    buyPrice: 21,
    sellPutCredit: 700,
    oneYrStockPriceEst: 17.86,
    dividendFrequency: 'monthly',
};


// Function to calculate all outputs for a given scenario
const calculateScenarioOutputs = (inputs) => {
    const shares = Number(inputs.shares) || 0;
    const dividendAveragePerShare = Number(inputs.dividendAveragePerShare) || 0;
    const insurancePut = Number(inputs.insurancePut) || 0;
    const timeInDays = Number(inputs.timeInDays) || 0;
    const putStrike = Number(inputs.putStrike) || 0;
    const puts = Number(inputs.puts) || 0;
    const taxRate = Number(inputs.taxRate) || 0;
    const buyPrice = Number(inputs.buyPrice) || 0;
    const sellPutCredit = Number(inputs.sellPutCredit) || 0;
    const oneYrStockPriceEst = Number(inputs.oneYrStockPriceEst) || 0;
    const dividendFrequency = inputs.dividendFrequency || 'monthly';

    const cost = shares * buyPrice;
    const dividendAnnual = shares * dividendAveragePerShare * (dividendFrequency === 'monthly' ? 12 : 4);
    const taxes = dividendAnnual * taxRate;
    const netDividendAnnual = dividendAnnual - taxes;
    const netPeriodicDiv = netDividendAnnual / (dividendFrequency === 'monthly' ? 12 : 4);
    const putSellCapitol = puts * 100 * putStrike;
    const noPutCapitol = Math.max(0, (shares - (puts * 100)) * (oneYrStockPriceEst));
    const capitalAfter1Year = putSellCapitol + noPutCapitol;
    const oneYearLossGain = cost - capitalAfter1Year;
    const capitolLoss = (oneYrStockPriceEst === 0 || oneYearLossGain > 0) ? oneYearLossGain : 0;
    const capitalGain = (oneYearLossGain < 0) ? Math.abs(oneYearLossGain) : 0;

    const totalAfter1Year = (dividendAnnual + sellPutCredit + capitalGain) - (capitolLoss + taxes + insurancePut);
    const gainLossPercentage = (cost + insurancePut) > 0 ? (totalAfter1Year / (cost + insurancePut)) * 100 : 0;

    let maxInsurancePutFor15Gain = 0;
    const numerator = (dividendAnnual + sellPutCredit + capitalGain) - (capitolLoss + taxes) - (0.15 * cost);
    const denominator = 1.15;
    if (denominator > 0) {
        const calculatedMax = numerator / denominator;
        if (!isNaN(calculatedMax) && calculatedMax > 0) {
            maxInsurancePutFor15Gain = calculatedMax;
        }
    }

    return {
        cost,
        dividendAnnual,
        taxes,
        netDividendAnnual,
        netPeriodicDiv,
        putSellCapitol,
        noPutCapitol,
        capitalAfter1Year,
        oneYearLossGain,
        capitolLoss,
        capitalGain,
        totalAfter1Year,
        gainLossPercentage,
        maxInsurancePutFor15Gain,
    };
};

function calculateCompoundGrowth(startingAmount, annualPercentage, annualContribution, numberOfYears) {
    const results = [];
    let currentAmount = startingAmount;
    let highlightedOnce = false;

    for (let year = 1; year <= numberOfYears; year++) {
        const growthAmount = currentAmount * (annualPercentage / 100);
        const amountBeforeContribution = currentAmount + growthAmount;
        const endingAmount = amountBeforeContribution + annualContribution;
















        const yearData = {
            year: year,
            startingAmount: parseFloat(currentAmount.toFixed(2)),
            growthAmount: parseFloat(growthAmount.toFixed(2)),
            annualContribution: parseFloat(annualContribution.toFixed(2)),
            endingAmount: parseFloat(endingAmount.toFixed(2))
        };
















        if (!highlightedOnce && endingAmount >= 1000000) {
            yearData.highlightStartingAmount = true;
            highlightedOnce = true;
        }
















        results.push(yearData);
        currentAmount = endingAmount;
    }
















    return results;
}






// --- Calculated Risk Calculator Component---
const CalculatedRiskCalculator = ({ isAuthReady, userId, appId, fetchFmpData }) => {
    const lsKey = (field) => `${appId}_users_${userId}_cr_${field}`;
    // --- State for Ticker Input and API Data ---
    const [ticker, setTicker] = useState(() => loadFromLocalStorage(lsKey('ticker'), ''));
    const [isFetching, setIsFetching] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [historicalDividends, setHistoricalDividends] = useState([]);
    const [historicalPrices, setHistoricalPrices] = useState([]);
    // To store fetched price data for recalculation

    // State variables for all input fields, adapted for namespaced local storage
    const [shares, setShares] = useState(() => loadFromLocalStorage(lsKey('shares'), 400));
    const [dividendAveragePerShare, setDividendAveragePerShare] = useState(() => loadFromLocalStorage(lsKey('dividendAveragePerShare'), 1.87));
    const [insurancePut, setInsurancePut] = useState(() => loadFromLocalStorage(lsKey('insurancePut'), 900));
    const [timeInDays, setTimeInDays] = useState(() => loadFromLocalStorage(lsKey('timeInDays'), 198));
    const [putStrike, setPutStrike] = useState(() => loadFromLocalStorage(lsKey('putStrike'), 18));
    const [puts, setPuts] = useState(() => loadFromLocalStorage(lsKey('puts'), 2));
    const [taxRate, setTaxRate] = useState(() => loadFromLocalStorage(lsKey('taxRate'), 0.30));
    const [buyPrice, setBuyPrice] = useState(() => loadFromLocalStorage(lsKey('buyPrice'), 21));
    const [sellPutCredit, setSellPutCredit] = useState(() => loadFromLocalStorage(lsKey('sellPutCredit'), 700));
    const [timelineStockPriceEst, setTimelineStockPriceEst] = useState(() => loadFromLocalStorage(lsKey('timelineStockPriceEst'), 17.86));
    const [dividendFrequency, setDividendFrequency] = useState(() => loadFromLocalStorage(lsKey('dividendFrequency'), 'monthly'));
    const [timelineInMonths, setTimelineInMonths] = useState(() => loadFromLocalStorage(lsKey('timelineInMonths'), 12));
    const [useT12mDividends, setUseT12mDividends] = useState(() => loadFromLocalStorage(lsKey('useT12mDividends'), false));
    // Calculated output states
    const [calculatedCost, setCalculatedCost] = useState(0);
    const [projectedDividendIncome, setProjectedDividendIncome] = useState(0);
    const [projectedTaxAmount, setProjectedTaxAmount] = useState(0);
    const [projectedNetDividend, setProjectedNetDividend] = useState(0);
    const [projectedInsuranceCost, setProjectedInsuranceCost] = useState(0);
    const [projectedSellPutCredit, setProjectedSellPutCredit] = useState(0);
    const [capitolBalance, setCapitolBalance] = useState(0);
    const [capitalChange, setCapitalChange] = useState(0);
    const [capitalLoss, setCapitalLoss] = useState(0);
    const [capitalGain, setCapitalGain] = useState(0);
    const [totalPL, setTotalPL] = useState(0);
    const [gainPercentage, setGainPercentage] = useState(0);
    const [putSellCapitol, setPutSellCapitol] = useState(0);
    const [noPutCapitol, setNoPutCapitol] = useState(0);
    const [maxInsurancePutFor15Gain, setMaxInsurancePutFor15Gain] = useState(0);

    // UI control state
    const [showInfographic, setShowInfographic] = useState(false);
    const [infographicData, setInfographicData] = useState(null);
    const [projectedPriceLowerBound, setProjectedPriceLowerBound] = useState(null);
    const [projectedPriceUpperBound, setProjectedPriceUpperBound] = useState(null);
    // --- Function to fetch ticker data from FMP API ---
    const handleFetchTickerData = async () => {
        if (!ticker) {
            setFetchError("Please enter a ticker symbol.");
            return;
        }
        setIsFetching(true);
        setFetchError('');

        const data = await fetchFmpData(ticker);
        if (data && data.price) {
            setBuyPrice(data.price);
            saveToLocalStorage(lsKey('buyPrice'), data.price);
            setHistoricalPrices(data.historicalPrices || []);
            setHistoricalDividends(data.historicalDividends || []);

            if (data.lastDividend) {
                setDividendAveragePerShare(data.lastDividend);
                saveToLocalStorage(lsKey('dividendAveragePerShare'), data.lastDividend);
            } else {
                setDividendAveragePerShare(0);
                saveToLocalStorage(lsKey('dividendAveragePerShare'), 0);
                setFetchError('No dividend data found for this ticker.');
            }
        } else {
            setFetchError(`Could not fetch data for "${ticker}". Please check the symbol.`);
        }
        setIsFetching(false);
    };
    // Helper to handle number changes and save to local storage
    const handleNumberChange = (setter, field) => (e) => {
        const value = e.target.value;
        setter(value === '' ? '' : Number(value));
        if (isAuthReady && userId) {
            saveToLocalStorage(lsKey(field), value);
        }
    };

    // useEffects for saving data and calculations
    useEffect(() => {
        if (isAuthReady && userId) {
            saveToLocalStorage(lsKey('ticker'), ticker);
            saveToLocalStorage(lsKey('useT12mDividends'), useT12mDividends);
        }
    }, [ticker, useT12mDividends, isAuthReady, userId]);

    useEffect(() => {
        if (useT12mDividends) {
            if (historicalDividends && historicalDividends.length > 0) {
                const last12Payments = historicalDividends.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 12).map(d => d.dividend);
                if (last12Payments.length > 0) {
                    const avgDividend = last12Payments.reduce((sum, div) => sum + div, 0) / last12Payments.length;
                    setDividendAveragePerShare(avgDividend);
                } else {
                    setFetchError("Not enough historical data for TTM average.");
                }
            }
        } else {
            if (historicalDividends && historicalDividends.length > 0) {
                const lastDividend = historicalDividends.sort((a, b) => new Date(b.date) - new Date(a.date))[0].dividend;
                setDividendAveragePerShare(lastDividend || 0);
            }
        }
    }, [useT12mDividends, historicalDividends]);

    useEffect(() => {
        const numShares = Number(shares) || 0;
        const numDividendAveragePerShare = Number(dividendAveragePerShare) || 0;
        const numBuyPrice = Number(buyPrice) || 0;
        const numSellPutCredit = Number(sellPutCredit) || 0;
        const numInsurancePut = Number(insurancePut) || 0;
        const numPutStrike = Number(putStrike) || 0;
        const numPuts = Number(puts) || 0;
        const numTaxRate = Number(taxRate) || 0;
        const numTimelineStockPriceEst = Number(timelineStockPriceEst) || 0;
        const numTimeInDays = Number(timeInDays) || 1;

        const initialCost = numShares * numBuyPrice;
        setCalculatedCost(initialCost);
        const putSellCap = numPuts * 100 * numPutStrike;
        setPutSellCapitol(putSellCap);
        const noPutCap = Math.max(0, (numShares - (numPuts * 100)) * (numTimelineStockPriceEst));
        setNoPutCapitol(noPutCap);
        const optionsTradeMultiplier = (timelineInMonths * 30.44) / numTimeInDays;
        const timelineMultiplier = timelineInMonths / 12;

        let annualDivMultiplier;
        switch (dividendFrequency) {
            case 'weekly': annualDivMultiplier = 52; break;
            case 'monthly': annualDivMultiplier = 12; break;
            case 'quarterly': annualDivMultiplier = 4; break;
            default: annualDivMultiplier = 0;
        }
        const annualDiv = numShares * numDividendAveragePerShare * annualDivMultiplier;
        const timelineDividends = annualDiv * timelineMultiplier;
        setProjectedDividendIncome(timelineDividends);
        const timelineInsuranceCost = numInsurancePut;
        setProjectedInsuranceCost(timelineInsuranceCost);
        const timelineSellPutCredit = numSellPutCredit;
        setProjectedSellPutCredit(timelineSellPutCredit);
        const timelineTaxAmount = timelineDividends * numTaxRate;
        setProjectedTaxAmount(timelineTaxAmount);
        const timelineNetDividend = timelineDividends - timelineTaxAmount;
        setProjectedNetDividend(timelineNetDividend);
        const finalCapitalValue = (putSellCap + noPutCap);
        setCapitolBalance(finalCapitalValue);
        const capChange = finalCapitalValue - initialCost;
        setCapitalChange(capChange);
        const capLoss = capChange < 0 ? Math.abs(capChange) : 0;
        setCapitalLoss(capLoss);
        const capGain = capChange > 0 ? capChange : 0;
        setCapitalGain(capGain);
        const totalProfitAndLoss = (timelineNetDividend + timelineSellPutCredit + capGain) - (timelineInsuranceCost + capLoss);
        setTotalPL(totalProfitAndLoss);
        const totalInvestment = initialCost + timelineInsuranceCost;
        if (totalInvestment > 0) {
            const percentage = (totalProfitAndLoss / totalInvestment) * 100;
            setGainPercentage(percentage);
        } else {
            setGainPercentage(0);
        }

        const totalGains = (timelineNetDividend + timelineSellPutCredit + capGain);
        const totalLossesWithoutInsurance = capLoss;
        const initialInvestment = initialCost;
        const numerator = totalGains - totalLossesWithoutInsurance - (0.15 * initialInvestment);
        const denominator = 1.15;
        let calculatedMax = 0;

        if (denominator > 0) {
            const result = numerator / denominator;
            if (!isNaN(result) && result > 0) {
                calculatedMax = result;
            }
        }
        setMaxInsurancePutFor15Gain(calculatedMax);
    }, [shares, buyPrice, dividendAveragePerShare, insurancePut, sellPutCredit, dividendFrequency, taxRate, putStrike, puts, timelineStockPriceEst, timeInDays, timelineInMonths]);

    useEffect(() => {
        if (historicalPrices && historicalPrices.length > 0) {
            const lastKnownPrice = historicalPrices[historicalPrices.length - 1].close;
            const projection = calculatePriceProjection_EMA(historicalPrices, timelineInMonths, lastKnownPrice);
            setTimelineStockPriceEst(projection.estimate);
            saveToLocalStorage(lsKey('timelineStockPriceEst'), projection.estimate);
            setProjectedPriceLowerBound(projection.lowerBound);
            setProjectedPriceUpperBound(projection.upperBound);
        }
    }, [timelineInMonths, historicalPrices]);

    const fieldExplanations = {
        shares: "The total number of stock shares purchased.",
        buyPrice: "The price paid per share when the stock was initially acquired.",
        calculatedCost: "The total cost of purchasing all shares (Shares × Buy Price).",
        dividendAveragePerShare: "The average dividend amount received per share, per dividend frequency period (weekly/monthly/quarterly).",
        projectedDividendIncome: `The total estimated dividend income over the selected ${timelineInMonths}-month timeline, before taxes.`,
        projectedNetDividend: `The projected dividend income over the timeline after deducting taxes.`,
        dividendFrequency: "Select whether dividends are calculated weekly, monthly, or quarterly.",
        insurancePut: "Cost for purchasing put options for a single trade cycle (defined by 'Time in Days').",
        timeInDays: "The duration in days for a single options trade cycle.",
        putStrike: "The price at which the put option holder can sell the underlying asset.",
        puts: "The number of put option contracts held (1 contract typically represents 100 shares).",
        sellPutCredit: "Income received from selling put options for a single trade cycle.",
        taxRate: "The percentage tax rate applied to dividend income.",
        projectedTaxAmount: `The calculated amount of tax on the projected dividend income over the ${timelineInMonths}-month timeline.`,
        timelineStockPriceEst: `Your estimated stock price per share after the selected ${timelineInMonths}-month timeline.`,
        projectedInsuranceCost: `Total projected cost for insurance puts over the ${timelineInMonths}-month timeline, based on repeating the trade cycle.`,
        projectedSellPutCredit: `Total projected income from selling put options over the ${timelineInMonths}-month timeline.`,
        capitolBalance: `The estimated value of your capital after the ${timelineInMonths}-month timeline, based on the projected stock price.`,
        capitalChange: `The change in your capital's value over the timeline (Capital Balance - Initial Cost).`,
        capitalLoss: "The portion of capital change that represents a loss.",
        capitalGain: "The portion of capital change that represents a gain.",
        totalPL: `The comprehensive total profit or loss after the ${timelineInMonths}-month timeline, considering all projected income and expenses.`,
        gainPercentage: `The percentage return on the total investment (initial cost + projected insurance) over the ${timelineInMonths}-month timeline.`,
        timelineSelector: "Select the projection timeframe. All calculations in the 'Summary', 'Losses', and 'Gains' sections will be scaled to this period.",
        putSellCapitol: "Capital covered by your put options (Put Quantity × 100 × Put Strike).",
        noPutCapitol: "Value change of shares not covered by put options, based on your stock price estimate.",
        maxInsurancePutFor15Gain: `The maximum amount that can be spent on put insurance for the trade cycle to achieve a minimum 15% gain on the total investment over the selected ${timelineInMonths}-month timeline.`
    };

    const renderLabel = (htmlFor, text, explanation) => (
        <label htmlFor={htmlFor} className="rc-label">
            {text}
            {explanation && (
                <UITooltip content={explanation}>
                    <Info className="rc-info-icon" />
                </UITooltip>
            )}
        </label>
    );

    const showInfographicView = () => {
        const data = {
            shares, buyPrice, calculatedCost, dividendAveragePerShare,
            projectedDividendIncome, dividendFrequency, projectedNetDividend,
            projectedInsuranceCost, putStrike, projectedSellPutCredit, taxRate, projectedTaxAmount,
            timelineStockPriceEst, capitolBalance, capitalChange,
            capitalLoss, totalPL, gainPercentage, capitalGain,
            timelineInMonths
        };
        const totalLossesBar = (Number(data.capitalLoss) || 0) + (Number(data.projectedTaxAmount) || 0) + (Number(data.projectedInsuranceCost) || 0);
        const totalGainsBar = (Number(data.projectedDividendIncome) || 0) + (Number(data.projectedSellPutCredit) || 0) + (Number(data.capitalGain) || 0);
        const sumForBar = (Number(data.calculatedCost) || 0) + totalLossesBar + totalGainsBar;

        data.initialCostPct = sumForBar > 0 ? ((data.calculatedCost / sumForBar) * 100).toFixed(2) : "0.00";
        data.lossesPct = sumForBar > 0 ? ((totalLossesBar / sumForBar) * 100).toFixed(2) : "0.00";
        data.gainsPct = sumForBar > 0 ? ((totalGainsBar / sumForBar) * 100).toFixed(2) : "0.00";

        setInfographicData(data);
        setShowInfographic(true);
    };

    const isTtmDisabled = dividendFrequency === 'weekly';

    if (showInfographic && infographicData) {
        return <InfographicDisplay data={infographicData} onBack={() => setShowInfographic(false)} />;
    }

    return (
        <div className="rc-container">
            <div className="rc-layout-container">
                <div className="rc-left-column">
                    <div className="rc-header">
                        <h1 className="rc-title">
                            <Calculator className="rc-title-icon" /> Calculated Risk
                        </h1>
                        <div className="rc-header-actions">
                            <button onClick={showInfographicView} className="rc-infographic-button" aria-label="Show Infographic">
                                <LayoutDashboard className="rc-infographic-button-icon" />
                            </button>
                        </div>
                    </div>

                    {/* Stock Purchase Details Section */}
                    <div className="rc-section-card">
                        <h2 className="rc-section-title">
                            <DollarSign className="rc-section-icon" /> Stock Purchase Details
                        </h2>
                        <div className="rc-ticker-group">
                            {renderLabel("ticker", "Stock Ticker", fieldExplanations.ticker)}
                            <div className="rc-input-with-button">
                                <input type="text" id="ticker" className="input-expressive" value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} onKeyPress={(e) => { if (e.key === 'Enter') handleFetchTickerData(); }} placeholder="e.g., AAPL" disabled={isFetching} />
                                <button onClick={handleFetchTickerData} className="rc-fetch-button" disabled={isFetching}>
                                    {isFetching ? <span className="rc-spinner"></span> : "Fetch"}
                                </button>
                            </div>
                            {fetchError && <p className="rc-error-message">{fetchError}</p>}
                        </div>
                        <div className="rc-grid-container-3-col">
                            <div>
                                {renderLabel("shares", "Shares", fieldExplanations.shares)}
                                <input type="number" id="shares" className="input-expressive w-full" value={shares} onChange={handleNumberChange(setShares, 'shares')} />
                            </div>
                            <div>
                                {renderLabel("buyPrice", "Buy Price", fieldExplanations.buyPrice)}
                                <input type="number" id="buyPrice" className="input-expressive w-full" value={buyPrice} onChange={handleNumberChange(setBuyPrice, 'buyPrice')} />
                            </div>
                            <div>
                                {renderLabel("calculatedCost", "Initial Cost", fieldExplanations.calculatedCost)}
                                <div className="rc-result-box"><span className="rc-result-box-value">${(Number(calculatedCost) || 0).toFixed(2)}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Dividend Information Section */}
                    <div className="rc-section-card">
                        <h2 className="rc-section-title">
                            <PieChart className="rc-section-icon" /> Dividend Information
                        </h2>
                        <div className="rc-grid-container-2-col">
                            <div>
                                {renderLabel("dividendAveragePerShare", "Dividend Avg Per Share", fieldExplanations.dividendAveragePerShare)}
                                <input type="number" id="dividendAveragePerShare" className="input-expressive w-full" value={dividendAveragePerShare} onChange={handleNumberChange(setDividendAveragePerShare, 'dividendAveragePerShare')} />
                            </div>
                            <div>
                                {renderLabel("taxRate", "Tax Rate", fieldExplanations.taxRate)}
                                <select id="taxRate" className="input-expressive w-full" value={taxRate} onChange={(e) => { const newRate = Number(e.target.value); setTaxRate(newRate); if (isAuthReady && userId) { saveToLocalStorage(lsKey('taxRate'), newRate); } }}>
                                    {[0, 0.15, 0.20, 0.30].map((rate) => (<option key={rate} value={rate}>{`${rate * 100}% Tax`}</option>))}
                                </select>
                            </div>
                            <div className="rc-dividend-controls">
                                <div>
                                    {renderLabel("dividendFrequency", "Dividend Frequency", fieldExplanations.dividendFrequency)}
                                    <div className="rc-toggle-group">
                                        {['Weekly', 'Monthly', 'Quarterly'].map(freq => {
                                            const frequencyValue = freq.toLowerCase();
                                            return (
                                                <button key={frequencyValue} onClick={() => { setDividendFrequency(frequencyValue); if (isAuthReady && userId) { saveToLocalStorage(lsKey('dividendFrequency'), frequencyValue); } if (frequencyValue === 'weekly') { setUseT12mDividends(false); if (isAuthReady && userId) { saveToLocalStorage(lsKey('useT12mDividends'), false); } } }} className={`rc-toggle-button ${dividendFrequency === frequencyValue ? 'active' : ''}`}>
                                                    {freq}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="rc-ttm-control">
                                    {renderLabel("useT12mDividends", "TTM Average", fieldExplanations.useT12mDividends)}
                                    <div className={`rc-switch-container ${isTtmDisabled ? 'disabled' : ''}`} onClick={() => !isTtmDisabled && setUseT12mDividends(!useT12mDividends)}>
                                        <div className={`rc-switch-track ${useT12mDividends && !isTtmDisabled ? 'active' : ''}`}>
                                            <span className={`rc-switch-thumb ${useT12mDividends && !isTtmDisabled ? 'active' : ''}`}></span>
                                        </div>
                                        <span className="rc-switch-label">{useT12mDividends && !isTtmDisabled ? 'On' : 'Off'}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                {renderLabel("projectedDividendIncome", "Dividend Total", fieldExplanations.projectedDividendIncome)}
                                <div className="rc-result-box"><span className="rc-result-box-value">${(Number(projectedDividendIncome) || 0).toFixed(2)}</span></div>
                            </div>
                            <div>
                                {renderLabel("projectedNetDividend", "Net Dividend Total", fieldExplanations.projectedNetDividend)}
                                <div className="rc-result-box"><span className="rc-result-box-value">${(Number(projectedNetDividend) || 0).toFixed(2)}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Options Trade Cycle Section */}
                    <div className="rc-section-card">
                        <h2 className="rc-section-title">
                            <Shield className="rc-section-icon" /> Options Trade Cycle Inputs
                        </h2>
                        <div className="rc-grid-container-3-col">
                            <div>
                                {renderLabel("insurancePut", "Insurance Put Cost", fieldExplanations.insurancePut)}
                                <input type="number" id="insurancePut" className="input-expressive w-full" value={insurancePut} onChange={handleNumberChange(setInsurancePut, 'insurancePut')} />
                            </div>
                            <div>
                                {renderLabel("sellPutCredit", "Sold Put Credits", fieldExplanations.sellPutCredit)}
                                <input type="number" id="sellPutCredit" className="input-expressive w-full" value={sellPutCredit} onChange={handleNumberChange(setSellPutCredit, 'sellPutCredit')} />
                            </div>
                            <div>
                                {renderLabel("maxInsurancePut", "Max Spend 15% return", fieldExplanations.maxInsurancePutFor15Gain)}
                                <div className="rc-result-box">
                                    <span className="rc-result-box-value rc-value-positive">
                                        {formatCurrency(maxInsurancePutFor15Gain)}
                                    </span>
                                </div>
                            </div>
                            <div>
                                {renderLabel("timeInDays", "Time in Days", fieldExplanations.timeInDays)}
                                <div className="input-wrapper">
                                    <input type="number" id="timeInDays" className="input-expressive w-full" value={timeInDays} onChange={handleNumberChange(setTimeInDays, 'timeInDays')} />
                                    {Number(timeInDays) > 0 && (Number(timeInDays) >= (timelineInMonths * 30.44) ?
                                        (<CheckCircle size={20} className="input-icon rc-icon-positive" />) :
                                        (<AlertCircle size={20} className="input-icon rc-icon-negative" />)
                                    )}
                                </div>
                                {Number(timeInDays) > 0 && (Number(timeInDays) >= (timelineInMonths * 30.44) ?
                                    (<p className="rc-helper-text rc-helper-text-positive">Sufficient for the selected timeline.</p>) :
                                    (<p className="rc-helper-text rc-helper-text-negative">Less than the selected timeline.</p>)
                                )}
                            </div>
                            <div>
                                {renderLabel("putStrike", "Put Strike", fieldExplanations.putStrike)}
                                <input type="number" id="putStrike" className="input-expressive w-full" value={putStrike} onChange={handleNumberChange(setPutStrike, 'putStrike')} />
                            </div>
                            <div>
                                {renderLabel("puts", "Put Quantity", fieldExplanations.puts)}
                                <input type="number" id="puts" className="input-expressive w-full" value={puts} onChange={handleNumberChange(setPuts, 'puts')} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="rc-right-column">
                    <div className="rc-section-card">
                        <h2 className="rc-section-title">
                            <Clock className="rc-section-icon" /> Projection Timeline
                        </h2>
                        {renderLabel("timelineSelector", "Select Timeline", fieldExplanations.timelineSelector)}
                        <div className="rc-toggle-group">
                            {[6, 12].map(months => (
                                <button key={months} onClick={() => { setTimelineInMonths(months); if (isAuthReady && userId) saveToLocalStorage(lsKey('timelineInMonths'), months); }} className={`rc-toggle-button ${timelineInMonths === months ? 'active' : ''}`}>
                                    {months} Months
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="rc-section-card rc-summary-card">
                        <h2 className="rc-section-title">
                            <ClipboardList className="rc-section-icon" /> Summary for {timelineInMonths} Months
                        </h2>
                        <div className="rc-v-stack">
                            <div>
                                {renderLabel("timelineStockPriceEst", `${timelineInMonths} Month Stock Price Est*`, fieldExplanations.timelineStockPriceEst)}
                                <input type="number" id="timelineStockPriceEst" className="input-expressive w-full" value={timelineStockPriceEst} onChange={handleNumberChange(setTimelineStockPriceEst, 'timelineStockPriceEst')} />
                                {projectedPriceLowerBound && projectedPriceUpperBound && (
                                    <div className="rc-confidence-text">
                                        95% Confidence Range: <strong>{formatCurrency(projectedPriceLowerBound)}</strong> - <strong>{formatCurrency(projectedPriceUpperBound)}</strong>
                                    </div>
                                )}
                            </div>
                            <div>
                                {renderLabel("putSellCapitol", "Capitol covered by Puts", fieldExplanations.putSellCapitol)}
                                <div className="rc-result-box"><span className="rc-result-box-value">${(Number(putSellCapitol) || 0).toFixed(2)}</span></div>
                            </div>
                            <div>
                                {renderLabel("noPutCapitol", "Capitol Not covered by Puts", fieldExplanations.noPutCapitol)}
                                <div className="rc-result-box"><span className="rc-result-box-value">${(Number(noPutCapitol) || 0).toFixed(2)}</span></div>
                            </div>
                            <div>
                                {renderLabel("capitalChange", "Capital Gain/Loss", fieldExplanations.capitalChange)}
                                <div className={`rc-result-box ${capitalChange >= 0 ? 'rc-value-positive' : 'rc-value-negative'}`}>
                                    <span className="rc-result-box-value">${(Number(capitalChange) || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rc-grid-container-2-col">
                        <div className="rc-section-card">
                            <h2 className="rc-section-title">
                                <TrendingDown className="rc-section-icon rc-icon-negative" /> Projected Losses
                            </h2>
                            <div className="rc-v-stack">
                                <div>
                                    {renderLabel("capitalLoss", "Capital Loss", fieldExplanations.capitalLoss)}
                                    <div className="rc-result-box"><span className="rc-result-box-value">${(Number(capitalLoss) || 0).toFixed(2)}</span></div>
                                </div>
                                <div>
                                    {renderLabel("projectedTaxAmount", "Taxes", fieldExplanations.projectedTaxAmount)}
                                    <div className="rc-result-box"><span className="rc-result-box-value">${(Number(projectedTaxAmount) || 0).toFixed(2)}</span></div>
                                </div>
                                <div>
                                    {renderLabel("projectedInsuranceCost", "Put Insurance Cost", fieldExplanations.projectedInsuranceCost)}
                                    <div className="rc-result-box"><span className="rc-result-box-value">${(Number(projectedInsuranceCost) || 0).toFixed(2)}</span></div>
                                </div>
                            </div>
                        </div>

                        <div className="rc-section-card">
                            <h2 className="rc-section-title">
                                <TrendingUp className="rc-section-icon rc-icon-positive" /> Projected Gains
                            </h2>
                            <div className="rc-v-stack">
                                <div>
                                    {renderLabel("capitalGain", "Capital Gain", fieldExplanations.capitalGain)}
                                    <div className="rc-result-box"><span className="rc-result-box-value">${(Number(capitalGain) || 0).toFixed(2)}</span></div>
                                </div>
                                <div>
                                    {renderLabel("projectedDividendIncome", "Dividends", fieldExplanations.projectedDividendIncome)}
                                    <div className="rc-result-box"><span className="rc-result-box-value">${(Number(projectedDividendIncome) || 0).toFixed(2)}</span></div>
                                </div>
                                <div>
                                    {renderLabel("projectedSellPutCredit", "Sold Put Credits", fieldExplanations.projectedSellPutCredit)}
                                    <div className="rc-result-box"><span className="rc-result-box-value">${(Number(projectedSellPutCredit) || 0).toFixed(2)}</span></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rc-section-card rc-totals-card">
                        <div className="rc-totals-row">
                            {renderLabel("totalPL", `Total P/L after ${timelineInMonths} months:`, fieldExplanations.totalPL)}
                            <span id="totalPL" className={`rc-totals-value ${totalPL >= 0 ? 'rc-value-positive' : 'rc-value-negative'}`}>
                                ${(Number(totalPL) || 0).toFixed(2)}
                            </span>
                        </div>
                        <div className="rc-totals-row">
                            {renderLabel("capitolBalance", `Capitol Balance after ${timelineInMonths} months:`, fieldExplanations.capitolBalance)}
                            <span id="capitolBalance" className={`rc-totals-value ${capitolBalance > calculatedCost ? 'rc-value-positive' : capitolBalance < calculatedCost ? 'rc-value-negative' : 'rc-value-neutral'}`}>
                                ${(Number(capitolBalance) || 0).toFixed(2)}
                            </span>
                        </div>
                        <div className="rc-totals-row">
                            {renderLabel("gainPercentage", "Gain/Loss %:", fieldExplanations.gainPercentage)}
                            <span id="gainPercentage" className={`rc-totals-value ${gainPercentage >= 0 ? 'rc-value-positive' : 'rc-value-negative'}`}>
                                {(Number(gainPercentage) || 0).toFixed(2)}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Corrected Price Graph Component ---
const PriceGraph = ({ isAuthReady, userId, fetchFmpData, stockCache }) => {
    const [tickers, setTickers] = useState(() => loadFromLocalStorage(`${appId}_users_${userId}_priceGraph_tickers`, []));
    const [activeTickerInput, setActiveTickerInput] = useState('');
    const [timeFrame, setTimeFrame] = useState('1Y');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00bcd4'];

    useEffect(() => {
        if (isAuthReady && userId) {
            saveToLocalStorage(`${appId}_users_${userId}_priceGraph_tickers`, tickers);
        }
    }, [tickers, isAuthReady, userId]);

    useEffect(() => {
        const loadAllStockData = async () => {
            setLoading(true);
            setError('');
            const fetchPromises = tickers.map(ticker => fetchFmpData(ticker));
            await Promise.all(fetchPromises);
            setLoading(false);
        };

        if (tickers.length > 0) {
            loadAllStockData();
        } else {
            setLoading(false);
            setError('');
        }
    }, [tickers]);

    const combinedChartData = useMemo(() => {
        if (tickers.length === 0) return [];
        const priceMaps = new Map();
        const allDateStrings = new Set();

        tickers.forEach(ticker => {
            const historicalPrices = stockCache[ticker]?.data?.historicalPrices;
            if (historicalPrices) {
                const tickerMap = new Map();
                historicalPrices.forEach(d => {
                    if (d.date && d.close !== null) {
                        tickerMap.set(d.date, d.close);
                        allDateStrings.add(d.date);
                    }
                });
                priceMaps.set(ticker, tickerMap);
            }
        });

        const sortedDateStrings = Array.from(allDateStrings).sort();
        if (sortedDateStrings.length === 0) return [];

        let filteredDateStrings = sortedDateStrings;

        if (timeFrame !== 'MAX') {
            const now = new Date();
            const startDateFilter = new Date();
            switch (timeFrame) {
                case '1D': startDateFilter.setDate(now.getDate() - 1); break;
                case '5D': startDateFilter.setDate(now.getDate() - 5); break;
                case '1M': startDateFilter.setMonth(now.getMonth() - 1); break;
                case '6M': startDateFilter.setMonth(now.getMonth() - 6); break;
                case 'YTD': startDateFilter.setFullYear(now.getFullYear(), 0, 1); break;
                case '1Y': startDateFilter.setFullYear(now.getFullYear() - 1); break;
                case '5Y': startDateFilter.setFullYear(now.getFullYear() - 5); break;
                default: break;
            }
            const startDateString = startDateFilter.toISOString().split('T')[0];
            filteredDateStrings = sortedDateStrings.filter(dateStr => dateStr >= startDateString);
        }

        const chartData = filteredDateStrings.map(dateStr => {
            const dataPoint = { date: new Date(dateStr + 'T00:00:00Z').getTime() };
            tickers.forEach(ticker => {
                const tickerMap = priceMaps.get(ticker);
                dataPoint[ticker] = tickerMap ? (tickerMap.get(dateStr) || null) : null;
            });
            return dataPoint;
        });

        return chartData;
    }, [stockCache, tickers, timeFrame]);

    const handleAddTicker = () => {
        const ticker = activeTickerInput.trim().toUpperCase();
        if (ticker && !tickers.includes(ticker) && tickers.length < 5) {
            setTickers([...tickers, ticker]);
            setActiveTickerInput('');
            setError('');
        } else if (tickers.includes(ticker)) {
            setError('Ticker already added.');
        } else if (tickers.length >= 5) {
            setError('You can compare up to 5 tickers only.');
        } else {
            setError('Please enter a valid ticker symbol.');
        }
    };

    const handleRemoveTicker = (tickerToRemove) => {
        setTickers(tickers.filter(ticker => ticker !== tickerToRemove));
    };

    const dateFormatter = useCallback((timestamp) => {
        return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }, []);

    return (
        <div className="spacing-y-8">
            <h2 className="section-title">Price Graph</h2>
            <div className="card-expressive">
                <div className="price-graph-input-group">
                    <input type="text" className="input-expressive" placeholder="Enter ticker symbol (e.g., AAPL)" value={activeTickerInput} onChange={(e) => setActiveTickerInput(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') handleAddTicker(); }} />
                    <button onClick={handleAddTicker} className="m3-button filled-blue">Add Ticker</button>
                </div>
                {error && <p className="text-red-500 text-sm margin-bottom-4">{error}</p>}
                <div className="ticker-list">
                    {tickers.map((ticker) => (
                        <span key={ticker} className="ticker-badge">
                            {ticker}
                            <button onClick={() => handleRemoveTicker(ticker)}><XCircle size={16} /></button>
                        </span>
                    ))}
                </div>
                <div className="timeframe-buttons">
                    {['1D', '5D', '1M', '6M', 'YTD', '1Y', '5Y', 'MAX'].map((frame) => (
                        <button key={frame} onClick={() => setTimeFrame(frame)} className={timeFrame === frame ? 'timeframe-button-active' : 'timeframe-button'}>{frame}</button>
                    ))}
                </div>
                {loading && (<div className="text-center text-blue-600 font-semibold margin-bottom-4">Loading price data...</div>)}
                {tickers.length === 0 && !loading && (<div className="empty-message">Please add a ticker to view its price history.</div>)}
                {tickers.length > 0 && !loading && (
                    <div className="relative-chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={combinedChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis dataKey="date" tickFormatter={dateFormatter} type="number" domain={['dataMin', 'dataMax']} />
                                <YAxis domain={['auto', 'auto']} tickFormatter={formatCurrency} />
                                <RechartsTooltip content={<CustomChartTooltip tickers={tickers} valueFormatter={formatCurrency} />} />
                                <Legend />
                                {tickers.map((ticker, index) => (
                                    <Line key={ticker} type="monotone" dataKey={ticker} stroke={COLORS[index % COLORS.length]} strokeWidth={2} dot={false} name={ticker} connectNulls={true} />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Rewritten Dividend History Graph Component ---
const DividendHistoryGraph = ({ isAuthReady, userId, fetchFmpData, stockCache }) => {
    const [tickers, setTickers] = useState(() => loadFromLocalStorage(`${appId}_users_${userId}_dividendGraph_tickers`, []));
    const [activeTickerInput, setActiveTickerInput] = useState('');
    const [timeFrame, setTimeFrame] = useState('1Y');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedTickerForList, setSelectedTickerForList] = useState('');
    const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00bcd4'];

    useEffect(() => {
        if (isAuthReady && userId) {
            saveToLocalStorage(`${appId}_users_${userId}_dividendGraph_tickers`, tickers);
        }
    }, [tickers, isAuthReady, userId]);

    useEffect(() => {
        const loadAllStockData = async () => {
            setLoading(true);
            setError('');
            const fetchedStockDataPromises = tickers.map(ticker => fetchFmpData(ticker));
            await Promise.all(fetchedStockDataPromises);
            setLoading(false);
        };

        if (tickers.length > 0) {
            loadAllStockData();
            if (!selectedTickerForList || !tickers.includes(selectedTickerForList)) {
                setSelectedTickerForList(tickers[0]);
            }
        } else {
            setLoading(false);
            setError('');
            setSelectedTickerForList('');
        }
    }, [tickers]);

    const combinedChartData = useMemo(() => {
        const allDates = new Set();
        tickers.forEach(ticker => {
            stockCache[ticker]?.data?.historicalDividends?.forEach(d => {
                // CORRECTED: Parse dates as UTC for consistency, matching PriceGraph
                if (d.date) allDates.add(new Date(d.date + 'T00:00:00Z').getTime());
            });
        });

        const sortedDates = Array.from(allDates).sort((a, b) => a - b);
        if (sortedDates.length === 0) return [];

        let filteredDates = sortedDates;

        // CORRECTED: More robust date filtering logic
        if (timeFrame !== 'MAX') {
            const now = new Date();
            const startDateFilter = new Date();
            switch (timeFrame) {
                case '1Y': startDateFilter.setFullYear(now.getFullYear() - 1); break;
                case '5Y': startDateFilter.setFullYear(now.getFullYear() - 5); break;
                case '10Y': startDateFilter.setFullYear(now.getFullYear() - 10); break;
                default: break;
            }
            filteredDates = sortedDates.filter(date => date >= startDateFilter.getTime());
        }

        const chartData = filteredDates.map(date => {
            const dataPoint = { date: date };
            tickers.forEach(ticker => {
                const dataForTicker = stockCache[ticker]?.data?.historicalDividends;
                if (dataForTicker) {
                    const found = dataForTicker.find(d => new Date(d.date + 'T00:00:00Z').getTime() === date);
                    dataPoint[ticker] = found ? found.dividend : null;
                } else {
                    dataPoint[ticker] = null;
                }
            });
            return dataPoint;
        });
        return chartData;
    }, [stockCache, tickers, timeFrame]);

    const handleAddTicker = () => {
        const ticker = activeTickerInput.trim().toUpperCase();
        if (ticker && !tickers.includes(ticker) && tickers.length < 5) {
            setTickers([...tickers, ticker]);
            setActiveTickerInput('');
            setError('');
        } else if (tickers.includes(ticker)) {
            setError('Ticker already added.');
        } else if (tickers.length >= 5) {
            setError('You can compare up to 5 tickers only.');
        } else {
            setError('Please enter a valid ticker symbol.');
        }
    };

    const handleRemoveTicker = (tickerToRemove) => {
        setTickers(tickers.filter(ticker => ticker !== tickerToRemove));
    };

    const dateFormatter = useCallback((timestamp) => {
        return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }, []);

    const dividendValueFormatter = useCallback((val) => `$${Number(val).toFixed(4)}`, []);

    const tickerDistributions = useMemo(() => {
        const distributions = {};
        tickers.forEach(ticker => {
            const stock = stockCache[ticker]?.data;
            if (stock && stock.historicalDividends) {
                distributions[ticker] = [...stock.historicalDividends]
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map(d => ({
                        date: new Date(d.date).toLocaleDateString(),
                        ticker: stock.ticker,
                        dividend: d.dividend
                    }));
            }
        });
        return distributions;
    }, [stockCache, tickers]);

    return (
        <div className="spacing-y-8">
            <h2 className="section-title">Dividend History Graph</h2>
            <div className="card-expressive">
                <div className="price-graph-input-group">
                    <input type="text" className="input-expressive" placeholder="Enter ticker symbol (e.g., AAPL)" value={activeTickerInput} onChange={(e) => setActiveTickerInput(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') handleAddTicker(); }} />
                    <button onClick={handleAddTicker} className="m3-button filled-blue">Add Ticker</button>
                </div>
                {error && <p className="text-red-500 text-sm margin-bottom-4">{error}</p>}
                <div className="ticker-list">
                    {tickers.map((ticker) => (
                        <span key={ticker} className="ticker-badge">
                            {ticker}
                            <button onClick={() => handleRemoveTicker(ticker)}><XCircle size={16} /></button>
                        </span>
                    ))}
                </div>
                <div className="timeframe-buttons">
                    {['1Y', '5Y', '10Y', 'MAX'].map((frame) => (
                        <button key={frame} onClick={() => setTimeFrame(frame)} className={timeFrame === frame ? 'timeframe-button-active' : 'timeframe-button'}>{frame}</button>
                    ))}
                </div>
                {loading && (<div className="text-center text-blue-600 font-semibold margin-bottom-4">Loading dividend data...</div>)}
                {tickers.length === 0 && !loading && (<div className="empty-message">Please add a ticker to view its dividend history.</div>)}
                {tickers.length > 0 && !loading && (
                    <>
                        <div className="relative-chart-container">
                            <h3 className="section-title text-center">Combined Dividend Performance</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={combinedChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <XAxis dataKey="date" tickFormatter={dateFormatter} type="number" domain={['dataMin', 'dataMax']} />
                                    <YAxis domain={['auto', 'auto']} tickFormatter={formatCurrency} />
                                    <RechartsTooltip content={<CustomChartTooltip tickers={tickers} valueFormatter={dividendValueFormatter} />} />
                                    <Legend />
                                    {tickers.map((ticker, index) => (
                                        <Line key={ticker} type="monotone" dataKey={ticker} stroke={COLORS[index % COLORS.length]} strokeWidth={2} dot={false} name={ticker} connectNulls={true} />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="dividend-table-wrapper">
                            <h3 className="dividend-table-title">Individual Dividend Distributions</h3>
                            {tickers.length > 0 && (
                                <div className="dividend-table-selector-group">
                                    <label htmlFor="tickerSelect">Select Ticker:</label>
                                    <select id="tickerSelect" value={selectedTickerForList} onChange={(e) => setSelectedTickerForList(e.target.value)}>
                                        <option value="">-- Select a Ticker --</option>
                                        {tickers.map(ticker => (<option key={ticker} value={ticker}>{ticker}</option>))}
                                    </select>
                                </div>
                            )}
                            {selectedTickerForList && tickerDistributions[selectedTickerForList] && tickerDistributions[selectedTickerForList].length > 0 ? (
                                <div className="dividend-table-container">
                                    <table className="dividend-table">
                                        <thead><tr><th>Date</th><th>Ticker</th><th>Amount</th></tr></thead>
                                        <tbody>
                                            {tickerDistributions[selectedTickerForList].map((dist, index) => (
                                                <tr key={index}><td>{dist.date}</td><td>{dist.ticker}</td><td>{formatCurrency(dist.dividend)}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (<p className="text-center text-gray-500">{tickers.length > 0 ? "Please select a ticker from the dropdown to view its distributions." : "Add tickers to the graph to view their distributions."}</p>)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// --- Select Stocks Component ---
const SelectStocks = ({
    selectStocks,
    isAuthReady,
    isLoading,
    isAiSummaryLoading,
    handleTickerEnter,
    handleStockInputChange,
    handleAmountInvestedSave,
    showHistoricalPriceGraph,
    showHistoricalDividendsGraph,
    getAiSummary,
    deleteStockRow,
    addStockRow,
    resetTabData,
    setModalTitle,
    setModalMessage,
    setModalContent,
    setShowModal
}) => {
    return (
        <div className="spacing-y-8">
            <h2 className="section-title">Individual Stock Comparison</h2>
            {selectStocks.length === 0 && (
                <p className="empty-message stock-card-blue opacity-70">No stocks added yet. Click "Add Stock Row" to begin!</p>
            )}
            {selectStocks.map((stock) => {
                const metrics = calculateStockMetrics(stock);
                return (
                    <div key={stock.id} className="card-expressive stock-card-blue opacity-70 stock-card-container">
                        <div className="stock-info-grid">
                            <div className="stock-info-item">
                                <label className="stock-label">Ticker</label>
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    handleTickerEnter(stock.id, e.target.elements.tickerInput.value, 'select');
                                }}>
                                    <input
                                        type="text"
                                        name="tickerInput"
                                        value={stock.ticker || ''}
                                        onChange={(e) => handleStockInputChange(stock.id, 'ticker', e.target.value, 'select')}
                                        className="input-expressive"
                                        placeholder="e.g., AAPL"
                                        disabled={!isAuthReady || isLoading}
                                    />
                                    <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
                                </form>
                                <span className="stock-company-name">{stock.companyName || 'Company Name'}</span>
                            </div>

                            <div className="stock-info-item">
                                <label className="stock-label">Amount Invested</label>
                                <input
                                    type="number"
                                    value={stock.amountInvested === null ? '' : stock.amountInvested}
                                    onChange={(e) => handleStockInputChange(stock.id, 'amountInvested', e.target.value, 'select')}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleAmountInvestedSave(stock.id, e.target.value, 'select', stock.ticker); }}
                                    onBlur={(e) => handleAmountInvestedSave(stock.id, e.target.value, 'select', stock.ticker)}
                                    className="input-expressive"
                                    placeholder="e.g., 1000"
                                    disabled={!isAuthReady || isLoading}
                                />
                            </div>

                            <div className="stock-info-item">
                                <span className="stock-label">Price</span>
                                <span className="stock-value blue">{formatCurrency(stock.price)}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Last Dividend (Q)</span>
                                <span className="stock-value purple">{formatCurrency(stock.lastDividend)}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Shares</span>
                                <span className="stock-value">{metrics.shares !== null ? metrics.shares.toFixed(4) : 'N/A'}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Annual Dividends</span>
                                <span className="stock-value green">{formatCurrency(metrics.dividends)}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Market Cap</span>
                                <span className="stock-value">{formatLargeNumber(stock.marketCap)}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Div/$ Invested</span>
                                <span className="stock-value">{metrics.dividendPerDollar !== null ? formatRatio(metrics.dividendPerDollar) : 'N/A'}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Yield</span>
                                <span className="stock-value">{metrics.yieldValue !== null ? formatPercentage(metrics.yieldValue) : 'N/A'}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">52-Wk Range</span>
                                <span className="stock-value">
                                    {stock.yearLow !== null && stock.yearHigh !== null
                                        ? `${formatCurrency(stock.yearLow)} - ${formatCurrency(stock.yearHigh)}`
                                        : 'N/A'
                                    }
                                </span>
                            </div>
                        </div>

                        <div className="action-buttons-group">
                            <button
                                onClick={() => showHistoricalPriceGraph(stock.ticker)}
                                className="action-button button-blue"
                                disabled={!stock.historicalPrices || stock.historicalPrices.length === 0 || !isAuthReady}
                                title="View Historical Price Graph"
                            >
                                <BarChart2 size={16} className="button-icon-margin-right-sm" />
                                <span>Price Chart</span>
                            </button>
                            <button
                                onClick={() => showHistoricalDividendsGraph(stock.ticker)}
                                className="action-button button-purple"
                                disabled={!stock.historicalDividends || stock.historicalDividends.length === 0 || !isAuthReady}
                                title="View Historical Dividends Graph"
                            >
                                <BarChart2 size={16} className="button-icon-margin-right-sm" />
                                <span>Div Chart</span>
                            </button>
                            <button
                                onClick={() => {
                                    setModalTitle('Total Shareholder Return (TSR)');
                                    setModalMessage(null);
                                    setModalContent(
                                        <TSRCalculator
                                            initialInvestmentCapital={stock.amountInvested || 0}
                                            tickerData={stock}
                                            getAiSummaryProp={getAiSummary}
                                            isAiSummaryLoadingProp={isAiSummaryLoading}
                                            setModalTitleProp={setModalTitle}
                                            setModalMessageProp={setModalMessage}
                                            setShowModalProp={setShowModal}
                                            totalReturnPercentageProp={metrics.totalReturnPercentage}
                                            totalReturnInUSDProp={metrics.totalReturnInUSD}
                                            selectedTimePeriodProp={'N/A'}
                                        />
                                    );
                                    setShowModal(true);
                                }}
                                className="action-button button-yellow"
                                disabled={!isAuthReady || !stock.amountInvested || stock.amountInvested <= 0}
                                title="View Total Shareholder Return"
                            >
                                <DollarSign size={16} className="button-icon-margin-right-sm" />
                                <span>TSR</span>
                            </button>
                            <button
                                onClick={() => getAiSummary(stock)}
                                className="action-button button-pink"
                                disabled={isAiSummaryLoading || !stock.ticker || !stock.companyName || !isAuthReady}
                                title="Generate AI Summary"
                            >
                                {isAiSummaryLoading ? (
                                    <span className="animate-spin button-icon-margin-right-sm">🌀</span>
                                ) : (
                                    <Brain size={16} className="button-icon-margin-right-sm" />
                                )}
                                <span>AI Summary</span>
                            </button>
                            <button
                                onClick={() => deleteStockRow(stock.id, 'select')}
                                className="button-delete"
                                disabled={!isAuthReady}
                                title="Delete Stock"
                            >
                                <Trash2 size={20} />
                            </button>
                        </div>
                    </div>
                );
            })}
            <div className="global-action-buttons">
                <button
                    onClick={() => addStockRow('select')}
                    className="m3-button filled-blue"
                    disabled={!isAuthReady || selectStocks.length >= 10}
                >
                    <PlusCircle size={20} />
                    <span>Add Stock Row</span>
                </button>
                <button
                    onClick={() => resetTabData('select')}
                    className="m3-button filled-danger"
                    disabled={!isAuthReady || selectStocks.length === 0}
                >
                    <RotateCcw size={20} />
                    <span>Reset All</span>
                </button>
            </div>
        </div>
    );
};

// --- Margin Calculator Component ---
const MarginCalculator = ({
    isAuthReady,
    marginBalance,
    setMarginBalance,
    isGoldMember,
    setIsGoldMember,
    calculatedInterest,
    setModalTitle,
    setModalMessage,
    setModalContent,
    setShowModal
}) => {
    return (
        <div className="spacing-y-6">
            <div className="flex-between margin-bottom-6">
                <h2 className="section-title">Margin Interest Calculator</h2>
                <button
                    onClick={() => {
                        setModalTitle('Robinhood Margin Calculation Details');
                        setModalMessage(null);
                        setModalContent(
                            <div className="text-gray-700 spacing-y-4">
                                <p className="font-bold text-lg">Daily Interest Calculation Equation:</p>
                                <p className="font-mono text-sm bg-gray-100 padding-3 rounded-md overflow-x-auto">
                                    Daily Interest = Settled Margin Balance &times; (Annual Interest Rate / 360)
                                </p>
                                <p className="text-sm">
                                    (Robinhood calculates daily interest based on a 360-day year.)
                                </p>
                                <p className="font-bold text-lg margin-top-4">Annual Interest Rates by Tier (Illustrative):</p>
                                {/* This list is updated with the new non-Gold rates */}
                                <ul className="list-disc list-inside margin-left-4 spacing-y-1 text-sm">
                                    <li>Balance up to $1,000: 6.75% (Non-Gold), 0% (Gold - first $1,000 free)</li>
                                    <li>Balance $1,000 - $50,000: 6.75% (Non-Gold), 5.25% (Gold)</li>
                                    <li>Balance $50,000 - $100,000: 6.55% (Non-Gold), 5.05% (Gold)</li>
                                    <li>Balance $100,000 - $1,000,000: 6.25% (Non-Gold), 4.75% (Gold)</li>
                                    <li>Balance $1,000,000 - $10,000,000: 6.00% (Non-Gold), 4.5% (Gold)</li>
                                    <li>Balance $10,000,000 - $50,000,000: 5.95% (Non-Gold), 4.45% (Gold)</li>
                                    <li>Balance over $50,000,000: 5.70% (Non-Gold), 4.2% (Gold)</li>
                                </ul>
                                <p className="text-xs text-gray-500 margin-top-4">
                                    Note: These rates are illustrative based on common Robinhood margin tier examples and may not reflect current or exact rates. Consult Robinhood's official documentation for precise and up-to-date information.
                                </p>
                            </div>
                        );
                        setShowModal(true);
                    }}
                    className="padding-2 border-radius-full bg-gray-100 hover-bg-gray-200 transition-all-200 transform-hover-110 active-90"
                    title="Margin Calculation Details"
                >
                    <Info size={20} />
                </button>
            </div>
            <div className="card-expressive stock-card-indigo opacity-70 max-w-lg margin-x-auto">
                <p className="text-gray-700 margin-bottom-6 text-base">Estimate your margin interest based on Robinhood's tiered rates. Rates are illustrative and subject to change.</p>
                <div className="margin-bottom-6">
                    <label htmlFor="marginBalanceCalc" className="block text-lg font-medium text-gray-700 margin-bottom-2">Settled Margin Balance:</label>
                    <input
                        type="number"
                        id="marginBalanceCalc"
                        value={marginBalance === 0 && !String(marginBalance).includes('.') ? '' : marginBalance}
                        onChange={(e) => setMarginBalance(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        className="input-expressive input-expressive-margin"
                        placeholder="0"
                        disabled={!isAuthReady}
                    />
                </div>
                <div className="margin-bottom-8 flex-align-center">
                    <label htmlFor="isGoldMemberCalc" className="mu-switch-wrapper">
                        <input
                            type="checkbox"
                            id={`goldMemberMonthly`}
                            checked={isGoldMember}
                            onChange={(e) => setIsGoldMember(e.target.checked)}
                            className="mu-switch-input"
                            disabled={!isAuthReady}
                        />
                        <span className="mu-switch-track">
                            <span className="mu-switch-thumb"></span>
                        </span>
                        <span className="margin-left-3 block text-base text-gray-700 select-none">Robinhood Gold Member?</span>
                    </label>
                </div>
                <div className="spacing-y-4 text-xl">
                    <p><span className="font-medium text-gray-600">Daily Interest:</span> <span className="font-semibold text-indigo-800">{formatCurrency(calculatedInterest.daily)}</span></p>
                    <p><span className="font-medium text-gray-600">Monthly Interest:</span> <span className="font-semibold text-indigo-800">{formatCurrency(calculatedInterest.monthly)}</span></p>
                    <p><span className="font-medium text-gray-600">Annual Interest:</span> <span className="font-semibold text-indigo-800">{formatCurrency(calculatedInterest.annual)}</span></p>
                </div>
                <div className="tiered-interest-section">
                    <h4 className="tiered-interest-title">Interest Paid Per Tier:</h4>
                    {calculatedInterest.interestPerTier && calculatedInterest.interestPerTier.length > 0 && marginBalance > 0 ? (
                        <div className="tiered-interest-list">
                            {calculatedInterest.interestPerTier.map((tier, index) => (
                                <div key={index} className="tiered-interest-item">
                                    <p className="tiered-interest-range">
                                        {tier.range}:
                                        {tier.isGoldFree ? (
                                            <span className="margin-left-2 text-sm text-gray-500">(Gold Free Tier)</span>
                                        ) : (
                                            <span className="margin-left-2 text-gray-600 font-medium">({formatCurrency(tier.borrowedAmountInTier)} at {tier.rate})</span>
                                        )}
                                    </p>
                                    <ul className="tiered-interest-detail">
                                        {tier.borrowedAmountInTier > 0 && !tier.isGoldFree && (
                                            <li><span className="label">Amount Borrowed in Tier:</span> {formatCurrency(tier.borrowedAmountInTier)}</li>
                                        )}
                                        <li><span className="label">Rate Applied:</span> {tier.rate}</li>
                                        <li><span className="label">Annual Interest for this Tier:</span> {formatCurrency(tier.annualInterestInTier)}</li>
                                        <li><span className="label">Monthly Interest for this Tier:</span> {formatCurrency(tier.monthlyInterestInTier)}</li>
                                    </ul>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">No interest calculated for tiers (balance is 0 or within free tier).</p>
                    )}
                    <p className="tiered-interest-note">
                        Note: These calculations are illustrative. Consult Robinhood's official documentation for precise information.
                    </p>
                </div>
            </div>
        </div>
    );
};


// --- Monthly Income Calculator Component ---

const MonthlyIncomeCalculator = ({
    isAuthReady,
    userId,
    monthlyStocks,
    showMonthlyMarginCalc,
    setShowMonthlyMarginCalc,
    marginBalance,
    setMarginBalance,
    isGoldMember,
    setIsGoldMember,
    calculatedInterest,
    isLoading,
    handleStockInputChange,
    handleTickerEnter,
    handleAmountInvestedSave,
    showHistoricalDividendsGraph,
    deleteStockRow,
    addStockRow,
    resetTabData,
    updateStocksInLocalStorage,
    getAiSummary,
    isOptimizeOn,
    handleOptimizeToggle,
    setShowModal,
    setModalTitle,
    setModalMessage
}) => {

    const [openTaxDropdown, setOpenTaxDropdown] = useState(null);
    const taxDropdownRef = useRef(null);
    const [isPortfolioSummaryLoading, setIsPortfolioSummaryLoading] = useState(false);
    const [isOptimize2Loading, setIsOptimize2Loading] = useState(false);
    const [aiPortfolioSummary, setAiPortfolioSummary] = useState('');
    const [includeDividendsInReturn, setIncludeDividendsInReturn] = useState(true);
    const [includeDividendsInCagr, setIncludeDividendsInCagr] = useState(false);
    const holdTimeoutRef = useRef(null);
    const wasHeld = useRef(false);

    // --- Projection Graph State ---
    const [showProjectionGraph, setShowProjectionGraph] = useState(false);
    const [projectionTimeframe, setProjectionTimeframe] = useState(5);
    const [monthlyContribution, setMonthlyContribution] = useState(0);

    // --- NEW: Portfolio Save/Load System ---
    const [showPortfolioMenu, setShowPortfolioMenu] = useState(false);
    const [portfolioMode, setPortfolioMode] = useState('load'); // 'load' | 'save'
    const [existingSlots, setExistingSlots] = useState({ 1: false, 2: false, 3: false });
    const portfolioMenuRef = useRef(null);

    // Check Local Storage for existing saves on mount
    useEffect(() => {
        const checkSlots = () => {
            const slots = {};
            [1, 2, 3].forEach(num => {
                const data = localStorage.getItem(`mi_saved_portfolio_${num}`);
                slots[num] = !!data;
            });
            setExistingSlots(slots);
        };
        checkSlots();
        if (showPortfolioMenu) checkSlots();
    }, [showPortfolioMenu]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (portfolioMenuRef.current && !portfolioMenuRef.current.contains(event.target)) {
                setShowPortfolioMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlePortfolioAction = (slotNum) => {
        const key = `mi_saved_portfolio_${slotNum}`;

        if (portfolioMode === 'save') {
            // SAVE LOGIC
            const dataToSave = {
                stocks: monthlyStocks,
                margin: marginBalance,
                savedAt: new Date().toISOString()
            };
            localStorage.setItem(key, JSON.stringify(dataToSave));
            setExistingSlots(prev => ({ ...prev, [slotNum]: true }));

            setModalTitle('Portfolio Saved');
            setModalMessage(`Current portfolio saved to Slot ${slotNum}.`);
            setShowModal(true);
        } else {
            // LOAD LOGIC
            const rawData = localStorage.getItem(key);
            if (!rawData) {
                setModalTitle('Slot Empty');
                setModalMessage(`Slot ${slotNum} is empty. Save a portfolio here first.`);
                setShowModal(true);
                return;
            }

            try {
                const data = JSON.parse(rawData);

                // 1. Update Stocks
                if (data.stocks) {
                    updateStocksInLocalStorage('monthly', data.stocks);
                }

                // 2. Update Margin
                if (data.margin !== undefined) {
                    setMarginBalance(Number(data.margin));
                }

                setModalTitle('Portfolio Loaded');
                setModalMessage(`Successfully loaded portfolio from Slot ${slotNum}.`);
                setShowModal(true);
                setShowPortfolioMenu(false);
            } catch (e) {
                console.error("Error loading portfolio", e);
                setModalTitle('Load Error');
                setModalMessage('Could not load portfolio data. File might be corrupted.');
                setShowModal(true);
            }
        }
    };

    // --- Portfolio Metrics with Margin Adjustment ---
    const portfolioMetrics = useMemo(() => {
        let totalGrossDividends = 0;
        let totalTaxesOwed = 0;
        let totalAmountInvested = 0;
        let totalCurrentValue = 0;
        const allDividendsByDate = {};

        monthlyStocks.forEach(stock => {
            const stockMetrics = calculateStockMetrics(stock);
            const grossDividends = stockMetrics.dividends || 0;
            const taxRate = stock.taxRate || 0;

            totalGrossDividends += grossDividends;
            totalTaxesOwed += grossDividends * taxRate;
            totalAmountInvested += Number(stock.amountInvested) || 0;

            if (stockMetrics.shares && stock.price) {
                totalCurrentValue += stockMetrics.shares * stock.price;
            }

            if (stock.historicalDividends) {
                stock.historicalDividends.forEach(div => {
                    if (div.date && div.dividend > 0) {
                        allDividendsByDate[div.date] = (allDividendsByDate[div.date] || 0) + div.dividend;
                    }
                });
            }
        });

        const aggregatedDividends = Object.keys(allDividendsByDate).map(date => ({ date, dividend: allDividendsByDate[date] }));
        const portfolioDividendTrend = calculateDividendTrendPercentage(aggregatedDividends);
        const blendedTaxRate = totalGrossDividends > 0 ? (totalTaxesOwed / totalGrossDividends) : 0;

        // --- MARGIN ADJUSTMENTS ---
        const annualMarginCost = marginBalance > 0 ? calculatedInterest.annual : 0;

        // 1. Net Annual Income
        const netAnnualIncome = (totalGrossDividends - totalTaxesOwed) - annualMarginCost;

        // 2. Adjusted Yield (Net Yield)
        const adjustedYield = totalAmountInvested > 0 ?
            ((totalGrossDividends - annualMarginCost) / totalAmountInvested) : 0;

        return {
            totalAmountInvested,
            totalCurrentValue,
            totalTaxesOwed,
            totalGrossDividends,
            blendedTaxRate,
            netAnnualIncome,
            portfolioDividendTrend,
            adjustedYield
        };
    }, [monthlyStocks, marginBalance, calculatedInterest.annual]);

    // --- One Year Total Return with Margin Adjustment ---
    const oneYearTotalReturn = useMemo(() => {
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

        let totalValue12MonthsAgo = 0;
        let totalCapitalGain1Yr = 0;
        let totalHistoricalNetDividends = 0;
        let totalProjectedNetDividends = 0;
        let totalProjectedNavChange = 0;

        const eligibleStocks = monthlyStocks.filter(stock =>
            stock.ticker && stock.amountInvested > 0
            && stock.price > 0 && stock.historicalPrices && stock.historicalPrices.length > 0);

        eligibleStocks.forEach(stock => {
            const sortedPrices = [...stock.historicalPrices].sort((a, b) => new Date(a.date) - new Date(b.date));
            const priceData12MonthsAgo = sortedPrices.find(p => new Date(p.date) >= twelveMonthsAgo);

            if (priceData12MonthsAgo && priceData12MonthsAgo.close > 0) {
                const price12MonthsAgo = priceData12MonthsAgo.close;

                const impliedShares = Number(stock.amountInvested) / price12MonthsAgo;
                const currentValue = impliedShares * stock.price;

                totalCapitalGain1Yr += (currentValue - Number(stock.amountInvested));
                const historicalDividendsInPeriod = stock.historicalDividends
                    .filter(d => new Date(d.date) >= twelveMonthsAgo)
                    .reduce((sum, d) => sum + d.dividend, 0);
                totalHistoricalNetDividends += (historicalDividendsInPeriod * impliedShares) * (1 - (stock.taxRate || 0));

                let dividendMultiplier = 12;
                if (stock.dividendFrequency === 'quarterly') dividendMultiplier = 4;
                if (stock.dividendFrequency === 'weekly') dividendMultiplier = 52;
                const projectedGrossDividends = impliedShares * (stock.lastDividend || 0) * dividendMultiplier;
                totalProjectedNetDividends += projectedGrossDividends * (1 - (stock.taxRate || 0));
                const navDecayProjection = calculatePriceProjection(stock.historicalPrices, 12, stock.price);
                totalProjectedNavChange += (navDecayProjection.estimate - stock.price) * impliedShares;

                totalValue12MonthsAgo += Number(stock.amountInvested);
            }
        });

        if (totalValue12MonthsAgo === 0) {
            return {
                historicalCapitalGain: 0, historicalTotalReturn: 0,
                historicalCapitalGainPercentage: 0, historicalTotalReturnPercentage: 0,
                projectedGainLoss: 0, projectedPercentage: 0
            };
        }

        const annualMarginCost = marginBalance > 0 ? calculatedInterest.annual : 0;
        const historicalCapitalGain = totalCapitalGain1Yr;
        const historicalTotalReturn = totalCapitalGain1Yr + (totalHistoricalNetDividends - annualMarginCost);
        const projectedGainLoss = totalProjectedNavChange + (totalProjectedNetDividends - annualMarginCost);

        return {
            historicalCapitalGain,
            historicalTotalReturn,
            historicalCapitalGainPercentage: historicalCapitalGain / totalValue12MonthsAgo,
            historicalTotalReturnPercentage: historicalTotalReturn / totalValue12MonthsAgo,
            projectedGainLoss,
            projectedPercentage: projectedGainLoss / totalValue12MonthsAgo
        };
    }, [monthlyStocks, marginBalance, calculatedInterest.annual]);

    // --- Projection Calculation Logic (Strict Math Fixes) ---
    const projectionMetrics = useMemo(() => {
        if (!monthlyStocks.length || !oneYearTotalReturn || portfolioMetrics.totalAmountInvested <= 0) {
            return { data: [], summary: {} };
        }

        // 1. Define Growth Rates (Strictly Capped)
        let rawDivGrowth = portfolioMetrics.portfolioDividendTrend || 0;
        const safeDivGrowth = Math.min(Math.max(rawDivGrowth, -0.05), 0.10);

        const totalReturnRate = oneYearTotalReturn.projectedPercentage || 0;
        const currentNetYield = portfolioMetrics.adjustedYield || 0;
        let rawPriceGrowth = totalReturnRate - currentNetYield;
        const safePriceGrowth = Math.min(Math.max(rawPriceGrowth, -0.05), 0.12);

        // 2. Setup Simulation
        const months = projectionTimeframe * 12;
        const data = [];
        const annualMarginCost = marginBalance > 0 ? calculatedInterest.annual : 0;
        const averageTaxRate = portfolioMetrics.blendedTaxRate || 0;

        let currentGrossDividends = portfolioMetrics.totalGrossDividends;
        let currentPrincipal = portfolioMetrics.totalAmountInvested;
        let currentPortfolioValue = portfolioMetrics.totalCurrentValue;

        const baseGrossYield = portfolioMetrics.totalCurrentValue > 0 ?
            (portfolioMetrics.totalGrossDividends / portfolioMetrics.totalCurrentValue) : 0;

        const monthlyPriceMultiplier = Math.pow(1 + safePriceGrowth, 1 / 12);
        const monthlyDivMultiplier = Math.pow(1 + safeDivGrowth, 1 / 12);

        const startNetIncome = (currentGrossDividends * (1 - averageTaxRate)) - annualMarginCost;
        data.push({
            name: 'Start',
            principal: Math.round(currentPrincipal),
            value: Math.round(currentPortfolioValue),
            dividends: Math.round(startNetIncome / 12)
        });

        // 3. Simulation Loop (Non-DRIP)
        for (let i = 1; i <= months; i++) {
            currentPortfolioValue = currentPortfolioValue * monthlyPriceMultiplier;
            currentPortfolioValue += monthlyContribution;
            currentPrincipal += monthlyContribution;
            currentGrossDividends = currentGrossDividends * monthlyDivMultiplier;

            const growthRatio = Math.pow((1 + safeDivGrowth) / (1 + safePriceGrowth), i / 12);
            const yieldOnNewMoney = baseGrossYield * growthRatio;

            currentGrossDividends += (monthlyContribution * yieldOnNewMoney);

            const monthlyNetAnnualIncome = (currentGrossDividends * (1 - averageTaxRate)) - annualMarginCost;

            if (i % 12 === 0) {
                data.push({
                    name: `Year ${i / 12}`,
                    principal: Math.round(currentPrincipal),
                    value: Math.round(currentPortfolioValue),
                    dividends: Math.round(monthlyNetAnnualIncome / 12)
                });
            }
        }

        return {
            data,
            summary: {
                finalValue: currentPortfolioValue,
                totalPrincipal: currentPrincipal,
                totalProfit: currentPortfolioValue - currentPrincipal,
                finalMonthlyIncome: (currentGrossDividends * (1 - averageTaxRate) - annualMarginCost) / 12,
                ratesUsed: {
                    priceGrowth: safePriceGrowth,
                    divGrowth: safeDivGrowth,
                    totalReturn: safePriceGrowth + safeDivGrowth
                }
            }
        };
    }, [monthlyStocks, portfolioMetrics, oneYearTotalReturn, projectionTimeframe, monthlyContribution, marginBalance, calculatedInterest.annual]);

    // --- Portfolio CAGR ---
    const portfolioCagrMetrics = useMemo(() => {
        let totalValue3YrAgo = 0;
        let totalValue1YrAgo = 0;
        let totalCurrentValueForCagr = 0;
        let totalNetDividends3Yr = 0;
        let totalNetDividends1Yr = 0;
        let has3YrData = false;
        let has1YrData = false;

        const threeYearsAgo = new Date();
        threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

        monthlyStocks.forEach(stock => {
            if (!stock.ticker || !(stock.amountInvested > 0) || !stock.price || !stock.historicalPrices || stock.historicalPrices.length === 0) {
                return;
            }

            const sortedPrices = [...stock.historicalPrices].sort((a, b) => new Date(a.date) - new Date(b.date));
            const price3YrData = sortedPrices.find(p => new Date(p.date) >= threeYearsAgo);
            const price1YrData = sortedPrices.find(p => new Date(p.date) >= oneYearAgo);

            let timeframe = 0;
            let priceToUse = 0;

            if (price3YrData && price3YrData.close > 0) {
                timeframe = 3;
                priceToUse = price3YrData.close;
                has3YrData = true;
            }
            else if (price1YrData && price1YrData.close > 0) {
                timeframe = 1;
                priceToUse = price1YrData.close;
                has1YrData = true;
            }

            if (timeframe > 0) {
                const impliedShares = Number(stock.amountInvested) / priceToUse;
                const currentValue = impliedShares * stock.price;
                totalCurrentValueForCagr += currentValue;

                const startDate = (timeframe === 3) ? threeYearsAgo : oneYearAgo;
                const historicalDividendsInPeriod = stock.historicalDividends
                    .filter(d => new Date(d.date) >= startDate)
                    .reduce((sum, d) => sum + d.dividend, 0);
                const netDividends = (historicalDividendsInPeriod * impliedShares) * (1 - (stock.taxRate || 0));

                if (timeframe === 3) {
                    totalValue3YrAgo += Number(stock.amountInvested);
                    totalNetDividends3Yr += netDividends;
                } else {
                    totalValue1YrAgo += Number(stock.amountInvested);
                    totalNetDividends1Yr += netDividends;
                }
            }
        });

        const annualMarginCost = marginBalance > 0 ? calculatedInterest.annual : 0;
        let priceCagr = null;
        let totalReturnCagr = null;
        let cagrTimeframe = null;

        if (has3YrData && totalValue3YrAgo > 0) {
            cagrTimeframe = 3;
            const totalReturnValue = totalCurrentValueForCagr + (totalNetDividends3Yr - (annualMarginCost * 3));
            priceCagr = Math.pow(totalCurrentValueForCagr / totalValue3YrAgo, 1 / 3) - 1;
            totalReturnCagr = Math.pow(totalReturnValue / totalValue3YrAgo, 1 / 3) - 1;
        }
        else if (has1YrData && totalValue1YrAgo > 0) {
            cagrTimeframe = 1;
            const totalReturnValue = totalCurrentValueForCagr + (totalNetDividends1Yr - annualMarginCost);
            priceCagr = Math.pow(totalCurrentValueForCagr / totalValue1YrAgo, 1 / 1) - 1;
            totalReturnCagr = Math.pow(totalReturnValue / totalValue1YrAgo, 1 / 1) - 1;
        }

        return { priceCagr, totalReturnCagr, cagrTimeframe };
    }, [monthlyStocks, marginBalance, calculatedInterest.annual]);

    const handleOptimize2 = async () => {
        setIsOptimize2Loading(true);
        const totalInvestment = portfolioMetrics.totalAmountInvested;
        if (totalInvestment === 0) {
            setIsOptimize2Loading(false);
            return;
        }

        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        let excludedStocks = [];
        let maxPossibleYield = 0;

        const stocksWithMetrics = monthlyStocks.map(stock => {
            let dividendMultiplier = 12;
            if (stock.dividendFrequency === 'quarterly') dividendMultiplier = 4;
            if (stock.dividendFrequency === 'weekly') dividendMultiplier = 52;
            const yieldVal = (stock.lastDividend * dividendMultiplier) / stock.price;

            if (yieldVal > maxPossibleYield) maxPossibleYield = yieldVal;

            let ttmReturn = 0;
            let hasHistory = false;

            if (stock.historicalPrices && stock.historicalPrices.length > 0 && stock.price > 0) {
                const sortedPrices = [...stock.historicalPrices].sort((a, b) => new Date(a.date) - new Date(b.date));
                const price1YrData = sortedPrices.find(p => new Date(p.date) >= oneYearAgo);

                if (price1YrData && price1YrData.close > 0) {
                    ttmReturn = (stock.price - price1YrData.close) / price1YrData.close;
                    hasHistory = true;
                }
            }

            let score = 0;
            if (hasHistory && ttmReturn < -0.01) {
                score = 0;
                excludedStocks.push(`${stock.ticker} (${(ttmReturn * 100).toFixed(2)}%)`);
            } else {
                const taxBoost = (1 - stock.taxRate);
                score = (Math.pow(yieldVal * 100, 2)) * taxBoost * (1 + ttmReturn);
            }

            return { ...stock, score, yieldVal, ttmReturn, hasHistory };
        });

        const optimizableStocks = stocksWithMetrics.filter(s => s.score > 0);
        const totalScore = optimizableStocks.reduce((sum, s) => sum + s.score, 0);
        const newAllocations = new Map();

        optimizableStocks.forEach(stock => {
            const weight = stock.score / totalScore;
            newAllocations.set(stock.id, totalInvestment * weight);
        });
        let projectedAnnualIncome = 0;
        let projectedTaxPaid = 0;
        let resultingTotalInvested = 0;
        const optimizedStocks = monthlyStocks.map(stock => {
            const newAmount = newAllocations.get(stock.id) || 0;

            if (newAmount > 0) {
                const metricData = stocksWithMetrics.find(s => s.id === stock.id);
                if (metricData) {
                    const annualDiv = newAmount * metricData.yieldVal;
                    projectedAnnualIncome += annualDiv;
                    projectedTaxPaid += annualDiv * stock.taxRate;
                    resultingTotalInvested += newAmount;
                }
            }

            return {
                ...stock,
                amountInvested: newAmount.toFixed(2)
            };
        });
        updateStocksInLocalStorage('monthly', optimizedStocks);
        setIsOptimize2Loading(false);

        const resultingYield = resultingTotalInvested > 0 ? (projectedAnnualIncome / resultingTotalInvested) : 0;
        const resultingTaxRate = projectedAnnualIncome > 0 ? (projectedTaxPaid / projectedAnnualIncome) : 0;
        let report = `--- OPTIMIZATION REPORT ---\n`;
        report += `Objective 1 (Yield > 13%): ${resultingYield >= 0.13 ? "MET" : "MISSED"}. Actual: ${(resultingYield * 100).toFixed(2)}%.
        (Max yield available in stocks: ${(maxPossibleYield * 100).toFixed(2)}%).\n`;
        report += `Objective 2 (TTM Return > -1%): ENFORCED.
        Excluded Tickers: ${excludedStocks.length > 0 ? excludedStocks.join(', ') : "None"}.\n`;
        report += `Objective 3 (Tax Rate 7-9%): ${resultingTaxRate >= 0.07 && resultingTaxRate <= 0.09 ? "MET" : "MISSED"}.
        Actual: ${(resultingTaxRate * 100).toFixed(2)}%.\n`;

        if (optimizableStocks.length === 0) {
            report += `\nCRITICAL: No stocks met the criteria (specifically the -1% TTM Return floor).
            Allocation set to $0.`;
        } else {
            report += `\nAction: Reallocated capital to ${optimizableStocks.length} qualified stocks to maximize objectives.`;
        }

        await getAiSummary({ analysisReport: report }, false, false, true, true);
    };

    const downloadPortfolioSummary = useCallback(async () => {
        const invalidStock = monthlyStocks.find(stock =>
            (stock.ticker && Number(stock.amountInvested) > 0) &&
            (stock.taxRate === null || stock.taxRate === undefined || !stock.dividendFrequency)
        );

        if (invalidStock) {
            setModalTitle('Incomplete Stock Information');
            setModalMessage(`Please select a Tax Rate and Dividend Frequency for all of your stocks before downloading. The stock with ticker "${invalidStock.ticker}" is missing required information.`);
            setShowModal(true);
            return;
        }
        if (portfolioMetrics.totalAmountInvested <= 0) {
            setModalTitle('Cannot Generate Summary');
            setModalMessage('Please add at least one ticker and enter an amount invested to generate a summary.');
            setShowModal(true);
            return;
        }

        if (!oneYearTotalReturn) {
            setModalTitle('Cannot Generate Summary');
            setModalMessage('Return metrics are still calculating. Please ensure all stock data has loaded and try again.');
            setShowModal(true);
            return;
        }

        let summaryToDownload = aiPortfolioSummary;
        if (!summaryToDownload) {
            const summaryData = {
                ...portfolioMetrics,
                numberOfStocks: monthlyStocks.length,
                tickers: monthlyStocks.map(s => s.ticker).join(', ')
            };
            const newSummary = await getAiSummary(summaryData, false, true, false);
            if (newSummary) {
                setAiPortfolioSummary(newSummary);
                summaryToDownload = newSummary;
            }
        }

        const overallYield = portfolioMetrics.totalAmountInvested > 0 ?
            formatPercentage(monthlyStocks.reduce((sum, s) => sum + (calculateStockMetrics(s).dividends || 0), 0) / portfolioMetrics.totalAmountInvested) : 'N/A';
        const ttmReturnText = includeDividendsInReturn ?
            `TTM Total Return (w/ Dividends): ${formatCurrency(oneYearTotalReturn.historicalTotalReturn)} (${formatPercentage(oneYearTotalReturn.historicalTotalReturnPercentage)})` :
            `TTM Capital Gain (no Dividends): ${formatCurrency(oneYearTotalReturn.historicalCapitalGain)} (${formatPercentage(oneYearTotalReturn.historicalCapitalGainPercentage)})`;
        let summaryText = `--- Portfolio Summary ---\n`;
        summaryText += `Date: ${new Date().toLocaleString()}\n`;
        if (summaryToDownload) {
            summaryText += `\n--- AI-Generated Summary ---\n${summaryToDownload}\n`;
        }

        summaryText += `\n--- Aggregated Portfolio Metrics ---\n`;
        summaryText += `Total Amount Invested: ${formatCurrency(portfolioMetrics.totalAmountInvested)}\n`;
        summaryText += `Projected Net Annual Income: ${formatCurrency(portfolioMetrics.netAnnualIncome)}\n`;
        summaryText += `Estimated Net Monthly Dividends: ${formatCurrency(portfolioMetrics.netAnnualIncome / 12)}\n\n`;
        summaryText += `${ttmReturnText}\n`;
        summaryText += `Projected 1-Yr Total Return: ${formatCurrency(oneYearTotalReturn.projectedGainLoss)} (${formatPercentage(oneYearTotalReturn.projectedPercentage)})\n\n`;
        summaryText += `Overall Yield: ${overallYield}\n`;
        summaryText += `Blended Tax Rate: ${formatPercentage(portfolioMetrics.blendedTaxRate)}\n`;
        summaryText += `Portfolio Dividend Trend: ${formatPercentage(portfolioMetrics.portfolioDividendTrend)}\n`;
        if (showMonthlyMarginCalc && marginBalance > 0) {
            summaryText += `Margin Balance Used: ${formatCurrency(marginBalance)}\n`;
            summaryText += `Total Annual Margin Interest: ${formatCurrency(calculatedInterest.annual)}\n`;
        }

        summaryText += `\n\n--- Individual Stock Details ---\n`;
        monthlyStocks.forEach(stock => {
            const metrics = calculateStockMetrics(stock);
            const netAnnualDividends = (metrics.dividends || 0) * (1 - (stock.taxRate || 0));

            const threeYearsAgo = new Date();
            threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            let cagrText = "Too New";
            if (stock.historicalPrices && stock.historicalPrices.length > 0 && stock.price > 0) {
                const sortedPrices = [...stock.historicalPrices].sort((a, b) => new Date(a.date) - new Date(b.date));
                const price3YrData = sortedPrices.find(p => new Date(p.date) >= threeYearsAgo);
                const price1YrData = sortedPrices.find(p => new Date(p.date) >= oneYearAgo);
                const endingPrice = stock.price;

                if (price3YrData && price3YrData.close > 0) {
                    const startingPrice = price3YrData.close;
                    const cagr = Math.pow(endingPrice / startingPrice, 1 / 3) - 1;
                    cagrText = `${formatPercentage(cagr)} (3-Yr)`;
                } else if (price1YrData && price1YrData.close > 0) {
                    const startingPrice = price1YrData.close;
                    const cagr = Math.pow(endingPrice / startingPrice, 1 / 1) - 1;
                    cagrText = `${formatPercentage(cagr)} (1-Yr)`;
                }
            }

            const twelveMonthsAgo = new Date();
            twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
            const last12MonthDividends = stock.historicalDividends.filter(d => new Date(d.date) >= twelveMonthsAgo);
            const dividendTrend = calculateDividendTrendPercentage(last12MonthDividends);
            summaryText += `\n[${stock.ticker}] - ${stock.companyName}\n`;
            summaryText += `  - Amount Invested: ${formatCurrency(stock.amountInvested)}\n`;
            summaryText += `  - Current Price: ${formatCurrency(stock.price)}\n`;
            summaryText += `  - Last Dividend: ${formatCurrency(stock.lastDividend)}\n`;
            summaryText += `  - Shares: ${metrics.shares ? metrics.shares.toFixed(4) : 'N/A'}\n`;
            summaryText += `  - Yield: ${formatPercentage(metrics.yieldValue)}\n`;
            summaryText += `  - Net Annual Dividends: ${formatCurrency(netAnnualDividends)}\n`;
            summaryText += `  - Tax Rate: ${formatPercentage(stock.taxRate)}\n`;
            summaryText += `  - Dividend Frequency: ${stock.dividendFrequency ?
                stock.dividendFrequency.charAt(0).toUpperCase() + stock.dividendFrequency.slice(1) : 'N/A'}\n`;
            summaryText += `  - Price CAGR: ${cagrText}\n`;
            summaryText += `  - Distribution Trend Line (12-Mo): ${formatPercentage(dividendTrend)}\n`;
        });
        const blob = new Blob([summaryText], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'portfolio_summary.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [monthlyStocks, portfolioMetrics, oneYearTotalReturn, aiPortfolioSummary, getAiSummary, showMonthlyMarginCalc, marginBalance, calculatedInterest, includeDividendsInReturn, setModalMessage, setModalTitle, setShowModal]);

    const handlePortfolioSummary = async () => {
        setIsPortfolioSummaryLoading(true);
        const summaryData = {
            ...portfolioMetrics,
            numberOfStocks: monthlyStocks.length,
            tickers: monthlyStocks.map(s => s.ticker).join(', ')
        };
        const summaryText = await getAiSummary(summaryData, false, true, true);
        if (summaryText) {
            setAiPortfolioSummary(summaryText);
        }
        setIsPortfolioSummaryLoading(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (taxDropdownRef.current && !taxDropdownRef.current.contains(event.target)) {
                setOpenTaxDropdown(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const displayedTtmValue = includeDividendsInReturn ?
        oneYearTotalReturn.historicalTotalReturn : oneYearTotalReturn.historicalCapitalGain;
    const displayedTtmPercentage = includeDividendsInReturn ? oneYearTotalReturn.historicalTotalReturnPercentage : oneYearTotalReturn.historicalCapitalGainPercentage;

    const handleMouseDown = () => {
        wasHeld.current = false;
        holdTimeoutRef.current = setTimeout(() => {
            wasHeld.current = true;
            setModalTitle('What is "Optimize?"');
            setModalMessage(
                'The Optimize feature automatically reallocates your total invested capital across your current stocks. ' +
                'It calculates a score for each stock based on its historical price performance and recent dividend yield, ' +
                'then distributes your investment to favor the stocks with higher scores. This is intended to maximize a combination of growth and income potential.'
            );
            setShowModal(true);
        }, 750);
    };

    const handleMouseUp = () => {
        clearTimeout(holdTimeoutRef.current);
        if (!wasHeld.current) {
            handleOptimizeToggle();
        }
    };

    const handleMouseLeave = () => {
        clearTimeout(holdTimeoutRef.current);
    };

    return (
        <div className="spacing-y-8">
            <h2 className="section-title">Monthly Income Stocks</h2>

            <div className="card-expressive portfolio-summary-card margin-bottom-8">
                <div className="flex-between" style={{ marginBottom: 'var(--spacing-4)' }}>
                    <h3 className="portfolio-summary-title">Portfolio Summary</h3>

                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 'var(--spacing-4)' }}>
                        {/* --- NEW: Portfolio Save/Load Button --- */}
                        <div className="relative" ref={portfolioMenuRef}>
                            <button
                                onClick={() => setShowPortfolioMenu(!showPortfolioMenu)}
                                className={`action-button ${showPortfolioMenu ? 'active' : ''}`}
                                style={{ backgroundColor: showPortfolioMenu ? 'var(--color-green-100)' : undefined }}
                                title="Load/Save Portfolios"
                            >
                                <FolderOpen size={16} className="button-icon-margin-right-sm" />
                                <span>Portfolios</span>
                            </button>

                            {/* --- Portfolio Popup Menu --- */}
                            {showPortfolioMenu && (
                                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-4 animate-in fade-in zoom-in duration-200">
                                    <div className="flex-between mb-4">
                                        <h5 className="font-bold text-gray-700">Actions</h5>
                                        <div className="flex bg-gray-100 rounded-lg p-1">
                                            <button
                                                onClick={() => setPortfolioMode('load')}
                                                className={`px-3 py-1 text-xs rounded-md transition-all ${portfolioMode === 'load' ? 'bg-white shadow-sm font-semibold text-green-700' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                Load
                                            </button>
                                            <button
                                                onClick={() => setPortfolioMode('save')}
                                                className={`px-3 py-1 text-xs rounded-md transition-all ${portfolioMode === 'save' ? 'bg-white shadow-sm font-semibold text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex justify-between gap-2">
                                        {[1, 2, 3].map(num => (
                                            <button
                                                key={num}
                                                onClick={() => handlePortfolioAction(num)}
                                                className={`
                                                    w-16 h-16 rounded-full flex flex-col items-center justify-center transition-all border-2
                                                    ${portfolioMode === 'save'
                                                        ? 'border-blue-100 hover:border-blue-400 hover:bg-blue-50 text-blue-700'
                                                        : existingSlots[num]
                                                            ? 'border-green-100 hover:border-green-400 hover:bg-green-50 text-green-700 cursor-pointer'
                                                            : 'border-gray-100 text-gray-300 cursor-not-allowed'}
                                                `}
                                                title={portfolioMode === 'save' ? `Save to Slot ${num}` : (existingSlots[num] ? `Load Slot ${num}` : 'Empty Slot')}
                                                disabled={portfolioMode === 'load' && !existingSlots[num]}
                                            >
                                                <span className="text-xl font-bold">{num}</span>
                                                {existingSlots[num] ? (
                                                    <span className="text-[10px] uppercase font-bold mt-1">
                                                        {portfolioMode === 'save' ? 'Overwrite' : 'Saved'}
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] text-gray-400 mt-1">Empty</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="mt-4 text-xs text-center text-gray-400 border-t pt-2">
                                        {portfolioMode === 'save'
                                            ? 'Click a circle to SAVE current portfolio.'
                                            : 'Click a circle to LOAD that portfolio.'}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* --- Graph Toggle Button --- */}
                        <button
                            onClick={() => setShowProjectionGraph(prev => !prev)}
                            className={`action-button`}
                            style={{
                                backgroundColor: showProjectionGraph ? 'var(--color-green-600)' : 'var(--color-green-100)',
                                color: showProjectionGraph ? 'white' : 'var(--color-green-700)',
                                border: 'none',
                                boxShadow: showProjectionGraph ? 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                            title="Toggle Projection Graph"
                        >
                            <TrendingUp size={16} className="button-icon-margin-right-sm" />
                            <span>Graph</span>
                        </button>

                        <button
                            onMouseDown={handleMouseDown}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                            onTouchStart={handleMouseDown}
                            onTouchEnd={handleMouseUp}
                            className={`action-button ${isOptimizeOn ? 'button-pink-active' : 'button-pink'}`}
                            disabled={isPortfolioSummaryLoading || isOptimize2Loading || monthlyStocks.length === 0}
                            title="Click to toggle Optimization. Hold for explanation."
                        >
                            {isOptimizeOn ? <CheckCircle size={16} className="button-icon-margin-right-sm" /> : <Brain size={16} className="button-icon-margin-right-sm" />}
                            <span>Optimize</span>
                        </button>

                        <button
                            onClick={handleOptimize2}
                            className="action-button button-pink"
                            disabled={isPortfolioSummaryLoading || isLoading || isOptimizeOn || isOptimize2Loading || monthlyStocks.length === 0}
                            title="Maximize Income (CAGR >= -3%)"
                        >
                            {isOptimize2Loading ? <span className="rc-spinner"></span> : <Brain size={16} className="button-icon-margin-right-sm" />}
                            <span>Optimize 2</span>
                        </button>

                        <button
                            onClick={handlePortfolioSummary}
                            className="action-button button-pink"
                            disabled={isPortfolioSummaryLoading || isOptimize2Loading || monthlyStocks.length === 0}
                            title="Generate AI Summary for Portfolio"
                        >
                            {isPortfolioSummaryLoading ? <span className="rc-spinner"></span> : <Brain size={16} className="button-icon-margin-right-sm" />}
                            <span>AI Summary</span>
                        </button>
                    </div>
                </div>

                {/* --- Interactive Projection Graph Section --- */}
                {showProjectionGraph && (
                    <div className="margin-bottom-8 padding-4 bg-white rounded-xl border border-gray-200 shadow-sm" style={{ marginBottom: 'var(--spacing-6)' }}>
                        <div className="flex-between margin-bottom-4">
                            <div className="flex-align-center gap-2">
                                <h4 className="text-lg font-bold text-gray-700 flex-align-center gap-2">
                                    <TrendingUp size={20} className="text-green-600" />
                                    Projected Growth & Income
                                </h4>
                            </div>
                            <div className="flex-align-center gap-4">
                                {/* Monthly Contribution Input */}
                                <div className="flex-align-center gap-2">
                                    <label className="text-sm font-medium text-gray-600">Monthly Contribution:</label>
                                    <input
                                        type="number"
                                        className="input-expressive"
                                        style={{ padding: '4px 8px', width: '100px' }}
                                        value={monthlyContribution}
                                        onChange={(e) => setMonthlyContribution(Number(e.target.value))}
                                        placeholder="$0"
                                    />
                                </div>
                                {/* Timeframe Toggles */}
                                <div className="rc-toggle-group">
                                    {[5, 10].map(yr => (
                                        <button
                                            key={yr}
                                            onClick={() => setProjectionTimeframe(yr)}
                                            className={`rc-toggle-button ${projectionTimeframe === yr ? 'active' : ''}`}
                                            style={projectionTimeframe === yr ? { backgroundColor: 'var(--color-green-600)', color: 'white' } : {}}
                                        >
                                            {yr} Years
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Chart Area */}
                        <div className="relative-chart-container" style={{ height: '350px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={projectionMetrics.data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                    <YAxis yAxisId="left" tickFormatter={formatCurrency} tick={{ fontSize: 12 }} />
                                    <YAxis yAxisId="right" orientation="right" tickFormatter={formatCurrency} tick={{ fontSize: 12 }} />
                                    <RechartsTooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                    <Legend />
                                    <Area yAxisId="left" type="monotone" dataKey="value" name="Portfolio Value" fill="var(--color-green-100)" stroke="var(--color-green-600)" strokeWidth={2} />
                                    <Line yAxisId="left" type="monotone" dataKey="principal" name="Principal Invested" stroke="var(--color-gray-500)" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                                    <Bar yAxisId="right" dataKey="dividends" name="Monthly Dividend Income" fill="var(--color-blue-400)" barSize={20} radius={[4, 4, 0, 0]} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Projection Breakdown Text */}
                        <div className="margin-top-6 padding-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex-between margin-bottom-2">
                                <h5 className="font-bold text-gray-800">Projection Results ({projectionTimeframe} Years)</h5>
                                {/* Show the (Capped) rates being applied */}
                                <span className="text-xs text-gray-500">
                                    Projection capped at {formatPercentage(projectionMetrics.summary.ratesUsed?.totalReturn)} Annual Return
                                    & {formatPercentage(projectionMetrics.summary.ratesUsed?.divGrowth)} Div Growth
                                </span>
                            </div>
                            <div className="rc-grid-container-2-col">
                                <div>
                                    <p className="text-sm text-gray-600">Final Portfolio Value:</p>
                                    <p className="text-xl font-bold text-green-700">{formatCurrency(projectionMetrics.summary.finalValue)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Total Principal Invested:</p>
                                    <p className="text-base font-semibold text-gray-800">
                                        {formatCurrency(projectionMetrics.summary.totalPrincipal)}
                                        <span className="text-xs font-normal text-gray-500 margin-left-2">
                                            ({formatCurrency(portfolioMetrics.totalAmountInvested)} initial + {formatCurrency(monthlyContribution)}/mo)
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Total Profit/Growth:</p>
                                    <p className="text-base font-semibold text-green-600">{formatCurrency(projectionMetrics.summary.totalProfit)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Monthly Dividend Income (Year {projectionTimeframe}):</p>
                                    <p className="text-base font-semibold text-blue-600">{formatCurrency(projectionMetrics.summary.finalMonthlyIncome)} per month</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="portfolio-summary-grid">
                    <div><span className="portfolio-summary-label">Total Amount Invested: </span><span className="portfolio-summary-value">{formatCurrency(portfolioMetrics.totalAmountInvested)}</span></div>

                    <div style={!includeDividendsInReturn ? { opacity: 0.5 } : {}} title={!includeDividendsInReturn ? 'Dividends are currently excluded from TTM Total Return view' : 'Projected income based on recent dividend payments'}>
                        <span className="portfolio-summary-label">Projected Net Annual Income: </span>
                        <span className="portfolio-summary-value green">{formatCurrency(portfolioMetrics.netAnnualIncome)}</span>
                    </div>

                    <div><span className="portfolio-summary-label">Net Monthly Total: </span><span className="portfolio-summary-value green">{formatCurrency(portfolioMetrics.netAnnualIncome / 12)}</span></div>

                    {/* Overall Yield now displays the Adjusted Yield */}
                    <div>
                        <span className="portfolio-summary-label">Overall Yield: </span>
                        <span className="portfolio-summary-value">
                            {formatPercentage(portfolioMetrics.adjustedYield)}
                        </span>
                    </div>

                    <div onClick={() => setIncludeDividendsInReturn(prev => !prev)} style={{ cursor: 'pointer' }} title={includeDividendsInReturn ? 'Click to show return WITHOUT dividends' : 'Click to show return WITH dividends'}>
                        <span className="portfolio-summary-label">TTM Total Return ($): </span>
                        <span className={`portfolio-summary-value ${displayedTtmValue >= 0 ? 'green' : 'red'}`}>
                            {formatCurrency(displayedTtmValue)}
                        </span>
                    </div>
                    <div onClick={() => setIncludeDividendsInReturn(prev => !prev)} style={{ cursor: 'pointer' }} title={includeDividendsInReturn ? 'Click to show return WITHOUT dividends' : 'Click to show return WITH dividends'}>
                        <span className="portfolio-summary-label">TTM Total Return (%): </span>
                        <span className={`portfolio-summary-value ${displayedTtmPercentage >= 0 ? 'green' : 'red'}`}>
                            {formatPercentage(displayedTtmPercentage)}
                        </span>
                    </div>

                    <div>
                        <span className="portfolio-summary-label">Projected 1-Yr Return ($): </span>
                        <span className={`portfolio-summary-value ${oneYearTotalReturn.projectedGainLoss >= 0 ? 'green' : 'red'}`}>{formatCurrency(oneYearTotalReturn.projectedGainLoss)}</span>
                    </div>
                    <div>
                        <span className="portfolio-summary-label">Projected 1-Yr Return (%): </span>
                        <span className={`portfolio-summary-value ${oneYearTotalReturn.projectedPercentage >= 0 ? 'green' : 'red'}`}>{formatPercentage(oneYearTotalReturn.projectedPercentage)}</span>
                    </div>

                    <div><span className="portfolio-summary-label">Blended Tax Rate: </span><span className="portfolio-summary-value">{formatPercentage(portfolioMetrics.blendedTaxRate)}</span></div>

                    {(() => {
                        const { priceCagr, totalReturnCagr, cagrTimeframe } = portfolioCagrMetrics;
                        const displayedCagr = includeDividendsInCagr ? totalReturnCagr : priceCagr;
                        const cagrLabel = `Portfolio CAGR (${cagrTimeframe ? `${cagrTimeframe}-Yr` : '...'})`;

                        let cagrText;
                        if (cagrTimeframe === null) {
                            cagrText = "Too New";
                        } else {
                            cagrText = formatPercentage(displayedCagr);
                        }

                        const tooltipContent = includeDividendsInCagr
                            ? "Total Return CAGR: Measures the average annual growth of your investment, including both price changes, net (after-tax) dividends, and margin interest costs."
                            : "Price CAGR: Measures the average annual growth of your investment based on price changes only. This does NOT include dividends or margin costs.";
                        return (
                            <div
                                onClick={() => setIncludeDividendsInCagr(prev => !prev)}
                                style={{ cursor: 'pointer' }}
                                title="Click to toggle between Price CAGR and Total Return CAGR"
                            >
                                <span className="portfolio-summary-label flex-align-center">
                                    {cagrLabel}
                                    <UITooltip content={tooltipContent}>
                                        <Info size={14} style={{ marginLeft: 'var(--spacing-2)', color: 'var(--color-gray-400)' }} />
                                    </UITooltip>
                                </span>
                                <span className={`portfolio-summary-value ${displayedCagr === null ? '' : (displayedCagr >= 0 ? 'green' : 'red')}`}>
                                    {cagrText}
                                </span>
                            </div>
                        );
                    })()}

                </div>
                <div className="margin-top-8" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 'var(--spacing-4)' }}>
                    <button onClick={downloadPortfolioSummary} className="btn-expressive-primary" disabled={monthlyStocks.length === 0}><Download size={18} /><span>Download Summary</span></button>
                    <button onClick={() => setShowMonthlyMarginCalc(prev => !prev)} className="btn-expressive-secondary"><GitCompare size={18} /><span>{showMonthlyMarginCalc ? 'Hide Margin Adj.' : 'Show Margin Adj.'}</span></button>
                </div>
                {showMonthlyMarginCalc && (
                    <div className="margin-adjust-section">
                        <h4 className="margin-adjust-title">Margin Adjusted Dividends (Illustrative)</h4>
                        <p className="margin-adjust-description">This section illustrates how margin interest might reduce your effective annual/monthly dividends. **This is a simplified calculation and not financial advice.**</p>
                        <div className="margin-adjust-grid">
                            <div className="margin-adjust-input-group">
                                <label htmlFor="marginBalanceMonthly" className="margin-adjust-input-label">Margin Balance Used:</label>
                                <input
                                    type="text"
                                    id="marginBalanceMonthly"
                                    value={marginBalance === 0 && !String(marginBalance).includes('.') ? '' : marginBalance}
                                    onChange={(e) => {
                                        const filteredValue = e.target.value.replace(/[^0-9.]/g, '');
                                        setMarginBalance(filteredValue === '' ? 0 : parseFloat(filteredValue));
                                    }}
                                    className="input-expressive"
                                    placeholder="0"
                                    disabled={!isAuthReady}
                                />
                            </div>
                            <div className="flex-align-center"><label htmlFor="goldMemberMonthly" className="mu-switch-wrapper"><input type="checkbox" id={`goldMemberMonthly`} checked={isGoldMember} onChange={(e) => setIsGoldMember(e.target.checked)} className="mu-switch-input" disabled={!isAuthReady} /><span className="mu-switch-track"><span className="mu-switch-thumb"></span></span><span className="margin-left-3 block text-base text-gray-700 select-none">Robinhood Gold Member?</span></label></div>
                        </div>
                        <div className="margin-top-6" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                            <p><span className="label">Annual Margin Interest: </span><span className="value red">{formatCurrency(calculatedInterest.annual)}</span></p>
                            <p><span className="label">Monthly Margin Interest: </span><span className="value red">{formatCurrency(calculatedInterest.monthly)}</span></p>
                        </div>
                    </div>
                )}
            </div>

            {monthlyStocks.length === 0 && (<p className="empty-message stock-card-green" style={{ opacity: 0.7 }}>No stocks added yet. Click "Add Stock" to begin!</p>)}

            {monthlyStocks.length > 0 && (
                <div className="spacing-y-8">
                    {monthlyStocks.map((stock) => {
                        const metrics = calculateStockMetrics(stock);
                        const netAnnualDividends = (metrics.dividends || 0) * (1 - (stock.taxRate || 0));

                        const threeYearsAgo = new Date();
                        threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
                        const oneYearAgo = new Date();
                        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

                        let cagr = null;
                        let cagrTimeframe = null;

                        if (stock.historicalPrices && stock.historicalPrices.length > 0 && stock.price > 0) {
                            const sortedPrices = [...stock.historicalPrices].sort((a, b) => new Date(a.date) - new Date(b.date));
                            const price3YrData = sortedPrices.find(p => new Date(p.date) >= threeYearsAgo);
                            const price1YrData = sortedPrices.find(p => new Date(p.date) >= oneYearAgo);
                            const endingPrice = stock.price;

                            if (price3YrData && price3YrData.close > 0) {
                                const startingPrice = price3YrData.close;
                                cagr = Math.pow(endingPrice / startingPrice, 1 / 3) - 1;
                                cagrTimeframe = 3;
                            } else if (price1YrData && price1YrData.close > 0) {
                                const startingPrice = price1YrData.close;
                                cagr = Math.pow(endingPrice / startingPrice, 1 / 1) - 1;
                                cagrTimeframe = 1;
                            }
                        }

                        const cagrLabel = `Price CAGR (${cagrTimeframe ? `${cagrTimeframe}-Yr` : '...'})`;
                        let cagrText;
                        if (cagrTimeframe === null) {
                            cagrText = "Too New";
                        } else {
                            cagrText = formatPercentage(cagr);
                        }

                        const twelveMonthsAgo = new Date();
                        twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
                        const last12MonthDividends = stock.historicalDividends.filter(d => new Date(d.date) >= twelveMonthsAgo);
                        const dividendTrend = calculateDividendTrendPercentage(last12MonthDividends);
                        return (
                            <div key={stock.id} className="card-expressive stock-card-green stock-card-container">
                                <div className="stock-info-grid">
                                    <div className="stock-info-item"><label className="stock-label">Ticker</label><input type="text" value={stock.ticker || ''} onChange={(e) => handleStockInputChange(stock.id, 'ticker', e.target.value, 'monthly')} onKeyDown={(e) => { if (e.key === 'Enter') handleTickerEnter(stock.id, e.target.value, 'monthly'); }} onBlur={(e) => handleTickerEnter(stock.id, e.target.value, 'monthly')} className="input-expressive" placeholder="e.g., SPG" disabled={!isAuthReady || isLoading} /><span className="stock-company-name">{stock.companyName || 'Company Name'}</span></div>
                                    <div className="stock-info-item">
                                        <label className="stock-label">Amount Invested</label>
                                        <input
                                            type="text"
                                            value={stock.amountInvested === null ? '' : stock.amountInvested}
                                            onChange={(e) => {
                                                const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                                                handleStockInputChange(stock.id, 'amountInvested', numericValue, 'monthly');
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleAmountInvestedSave(stock.id, e.target.value, 'monthly', stock.ticker);
                                            }}
                                            onBlur={(e) => handleAmountInvestedSave(stock.id, e.target.value, 'monthly', stock.ticker)}
                                            className="input-expressive"
                                            placeholder="e.g., 2000"
                                            disabled={!isAuthReady || isLoading || isOptimizeOn || isOptimize2Loading}
                                        />
                                    </div>
                                    <div className="stock-info-item"><span className="stock-label">Price</span><span className="stock-value blue">{formatCurrency(stock.price)}</span></div>
                                    <div className="stock-info-item"><span className="stock-label flex-align-center">Last Dividend<button onClick={() => showHistoricalDividendsGraph(stock.ticker)} className="border-radius-full bg-gray-200 hover-bg-gray-300 transition-all-200 transform-hover-scale-110 active-scale-90 disabled-opacity-50 disabled-cursor-not-allowed" style={{ marginLeft: 'var(--spacing-2)', padding: 'var(--spacing-2)' }} title="View Historical Dividends Graph" disabled={!stock.historicalDividends || stock.historicalDividends.length === 0}><BarChart2 size={18} /></button></span><span className="stock-value purple">{formatCurrency(stock.lastDividend)}</span></div>
                                    <div className="stock-info-item"><span className="stock-label">Shares</span><span className="stock-value">{metrics.shares !== null ? metrics.shares.toFixed(4) : 'N/A'}</span></div>
                                    <div className="stock-info-item"><span className="stock-label">Net Annual Dividends</span><span className="stock-value green">{formatCurrency(netAnnualDividends)}</span></div>
                                    <div className="stock-info-item"><span className="stock-label">Net Monthly Dividends</span><span className="stock-value green">{formatCurrency(netAnnualDividends / 12)}</span></div>
                                    <div className="stock-info-item"><span className="stock-label">Yield</span><span className="stock-value">{metrics.yieldValue !== null ? formatPercentage(metrics.yieldValue) : 'N/A'}</span></div>
                                    <div className="stock-info-item">
                                        <span className="stock-label">{cagrLabel}</span>
                                        <span className={`stock-value ${cagr === null ? '' : (cagr >= 0 ? 'green' : 'red')}`}>
                                            {cagrText}
                                        </span>
                                    </div>
                                    <div className="stock-info-item">
                                        <span className="stock-label flex-align-center">
                                            Dividend Trend (12-Mo)
                                            <UITooltip content="Calculates the percentage change of a trend line fitted to the last 12 months of dividend payments. This shows the general direction of dividend growth or decline.">
                                                <Info size={14} style={{ marginLeft: 'var(--spacing-2)', color: 'var(--color-gray-400)' }} />
                                            </UITooltip>
                                        </span>
                                        <span className={`stock-value ${dividendTrend === null ? '' : (dividendTrend >= 0 ? 'green' : 'red')}`}>
                                            {dividendTrend !== null ? formatPercentage(dividendTrend) : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                                <div className="action-buttons-group">
                                    <div className="tax-dropdown-wrapper" ref={openTaxDropdown === stock.id ? taxDropdownRef : null}>
                                        <button onClick={() => setOpenTaxDropdown(openTaxDropdown === stock.id ? null : stock.id)} className="action-button button-tax-selector">
                                            <Percent size={16} className="button-icon-margin-right-sm" />
                                            <span>{`${(stock.taxRate || 0) * 100}% Tax`}</span>
                                        </button>
                                        {openTaxDropdown === stock.id && (
                                            <div className="tax-dropdown-panel">
                                                {[0, 0.15, 0.20].map(rate => (
                                                    <div
                                                        key={rate}
                                                        className="tax-dropdown-option"
                                                        onClick={() => {
                                                            handleStockInputChange(stock.id, 'taxRate', rate, 'monthly');
                                                            setOpenTaxDropdown(null);
                                                        }}
                                                    >
                                                        {`${rate * 100}%`}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="rc-toggle-group">
                                        {['Weekly', 'Monthly', 'Quarterly'].map(freq => {
                                            const frequencyValue = freq.toLowerCase();
                                            const isActive = stock.dividendFrequency === frequencyValue;
                                            return (
                                                <button
                                                    key={frequencyValue}
                                                    onClick={() => {
                                                        const updatedStocks = monthlyStocks.map(s => s.id === stock.id ? { ...s, dividendFrequency: frequencyValue } : s); updateStocksInLocalStorage('monthly', updatedStocks);
                                                    }}
                                                    className={`rc-toggle-button ${isActive ? 'active' : ''}`}
                                                    style={isActive ? { backgroundColor: 'var(--color-green-600)', color: 'white' } : {}}
                                                >
                                                    {freq}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <button onClick={() => deleteStockRow(stock.id, 'monthly')} className="button-delete" disabled={!isAuthReady} title="Delete Stock"><Trash2 size={20} /></button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="global-action-buttons">
                <button onClick={() => addStockRow('monthly')} className="btn-expressive-accent" disabled={!isAuthReady || monthlyStocks.length >= 10}><PlusCircle size={20} /><span>Add Stock</span></button>
                <button onClick={() => resetTabData('monthly')} className="btn-expressive-danger" disabled={!isAuthReady || monthlyStocks.length === 0}><RotateCcw size={20} /><span>Row Reset</span></button>
            </div>
        </div>
    );
};



// --- YoY Compound Growth Calculator Component ---
const YoYCompoundGrowthCalculator = ({ isAuthReady, userId, appId }) => {
    const [startingAmount, setStartingAmount] = useState(() => loadFromLocalStorage(`${appId}_users_${userId}_yoy_startingAmount`, ''));
    const [annualPercentage, setAnnualPercentage] = useState(() => loadFromLocalStorage(`${appId}_users_${userId}_yoy_annualPercentage`, ''));
    const [annualContribution, setAnnualContribution] = useState(() => loadFromLocalStorage(`${appId}_users_${userId}_yoy_annualContribution`, ''));
    const [numberOfYears, setNumberOfYears] = useState(() => loadFromLocalStorage(`${appId}_users_${userId}_yoy_numberOfYears`, ''));
    const [results, setResults] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (isAuthReady && userId) {
            saveToLocalStorage(`${appId}_users_${userId}_yoy_startingAmount`, startingAmount);
            saveToLocalStorage(`${appId}_users_${userId}_yoy_annualPercentage`, annualPercentage);
            saveToLocalStorage(`${appId}_users_${userId}_yoy_annualContribution`, annualContribution);
            saveToLocalStorage(`${appId}_users_${userId}_yoy_numberOfYears`, numberOfYears);
        }
    }, [startingAmount, annualPercentage, annualContribution, numberOfYears, isAuthReady, userId, appId]);

    const handleCalculate = useCallback(() => {
        setErrorMessage('');
        setResults([]);

        const start = parseFloat(startingAmount);
        const rate = parseFloat(annualPercentage);
        const contribution = parseFloat(annualContribution);
        const years = parseInt(numberOfYears, 10);

        if (isNaN(start) || isNaN(rate) || isNaN(contribution) || isNaN(years) || start < 0 || rate < 0 || contribution < 0 || years < 0) {
            setErrorMessage('Please enter valid, non-negative numbers for all fields.');
            return;
        }
        if (years > 50) {
            setErrorMessage('Number of years cannot exceed 50 for performance reasons.');
            return;
        }

        const calculatedResults = calculateCompoundGrowth(start, rate, contribution, years);
        setResults(calculatedResults);
    }, [startingAmount, annualPercentage, annualContribution, numberOfYears]);

    return (
        <div className="yoy-container">
            <h2 className="section-title">YoY Compound Growth Calculator</h2>
            <div className="yoy-card">
                <div className="yoy-form-container">
                    <div className="yoy-form-group">
                        <label htmlFor="startingAmount" className="yoy-label">Starting Amount:</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            id="startingAmount"
                            value={startingAmount}
                            onChange={(e) => setStartingAmount(e.target.value)}
                            className="input-expressive"
                            placeholder="e.g., 100000"
                        />
                    </div>
                    <div className="yoy-form-group">
                        <label htmlFor="annualPercentage" className="yoy-label">Annual Percentage (%):</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            id="annualPercentage"
                            value={annualPercentage}
                            onChange={(e) => setAnnualPercentage(e.target.value)}
                            className="input-expressive"
                            placeholder="e.g., 7"
                        />
                    </div>
                    <div className="yoy-form-group">
                        <label htmlFor="annualContribution" className="yoy-label">Annual Contribution:</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            id="annualContribution"
                            value={annualContribution}
                            onChange={(e) => setAnnualContribution(e.target.value)}
                            className="input-expressive"
                            placeholder="e.g., 5000"
                        />
                    </div>
                    <div className="yoy-form-group">
                        <label htmlFor="numberOfYears" className="yoy-label">Number of Years:</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            id="numberOfYears"
                            value={numberOfYears}
                            onChange={(e) => setNumberOfYears(e.target.value)}
                            className="input-expressive"
                            placeholder="e.g., 20"
                        />
                    </div>
                </div>

                {errorMessage && (
                    <p className="yoy-error-message">{errorMessage}</p>
                )}

                <div className="yoy-button-container">
                    <button
                        onClick={handleCalculate}
                        className="m3-button filled-emerald"
                    >
                        <Calculator size={20} />
                        <span>Calculate Growth</span>
                    </button>
                </div>

                {results.length > 0 && (
                    <div className="yoy-results-container">
                        <table className="yoy-table">
                            <thead className="yoy-table-head">
                                <tr>
                                    <th scope="col" className="yoy-table-header">Year</th>
                                    <th scope="col" className="yoy-table-header">Starting Amount</th>
                                    <th scope="col" className="yoy-table-header">Growth Amount</th>
                                    <th scope="col" className="yoy-table-header">Annual Contribution</th>
                                    <th scope="col" className="yoy-table-header">Ending Amount</th>
                                </tr>
                            </thead>
                            <tbody className="yoy-table-body">
                                {results.map((row) => (
                                    <tr key={row.year} className={row.highlightStartingAmount ? 'yoy-table-row--highlight' : ''}>
                                        <td className="yoy-table-cell yoy-table-cell--year">{row.year}</td>
                                        <td className="yoy-table-cell">{formatCurrency(row.startingAmount)}</td>
                                        <td className="yoy-table-cell">{formatCurrency(row.growthAmount)}</td>
                                        <td className="yoy-table-cell">{formatCurrency(row.annualContribution)}</td>
                                        <td className="yoy-table-cell yoy-table-cell--ending">{formatCurrency(row.endingAmount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Investment Payback Calculator Component ---
const InvestmentPaybackCalculator = ({ isAuthReady, userId, appId, fetchFmpData }) => {
    const lsKey = (field) => `${appId}_users_${userId}_payback_${field}`;

    // --- State Management ---
    const [ticker, setTicker] = useState(() => loadFromLocalStorage(lsKey('ticker'), ''));
    const [initialInvestment, setInitialInvestment] = useState(() => loadFromLocalStorage(lsKey('initialInvestment'), ''));
    const [taxRate, setTaxRate] = useState(() => loadFromLocalStorage(lsKey('taxRate'), 0.15));
    const [manualFrequency, setManualFrequency] = useState(() => loadFromLocalStorage(lsKey('manualFrequency'), null));
    const [stockData, setStockData] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // --- Save state to Local Storage ---
    useEffect(() => {
        if (isAuthReady && userId) {
            saveToLocalStorage(lsKey('ticker'), ticker);
            saveToLocalStorage(lsKey('initialInvestment'), initialInvestment);
            saveToLocalStorage(lsKey('taxRate'), taxRate);
            saveToLocalStorage(lsKey('manualFrequency'), manualFrequency);
        }
    }, [ticker, initialInvestment, taxRate, manualFrequency, isAuthReady, userId]);

    // --- Data Fetching ---
    const handleFetchData = useCallback(async () => {
        if (!ticker) {
            setError("Please enter a ticker symbol.");
            return;
        }
        setIsLoading(true);
        setError('');
        setResults(null);
        setStockData(null);
        setAnalysisData(null);
        setManualFrequency(null); // Reset manual override on new fetch

        const data = await fetchFmpData(ticker);

        if (data && data.price && data.historicalDividends && data.historicalDividends.length > 1) {
            setStockData(data);
        } else {
            setError(`Could not fetch sufficient data for "${ticker}". A price and at least two dividend payments are required.`);
            setStockData(null);
            setIsLoading(false);
        }
    }, [ticker, fetchFmpData]);

    // --- Helper to calculate annualized growth rate from historical data ---
    const calculateAnnualizedGrowth = (data) => {
        if (!data || data.length < 2) return 0;
        const sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));
        const first = sortedData[0];
        const last = sortedData[sortedData.length - 1];
        const years = (new Date(last.date) - new Date(first.date)) / (1000 * 60 * 60 * 24 * 365.25);
        if (years <= 0 || first.value <= 0) return 0;
        return Math.pow(last.value / first.value, 1 / years) - 1;
    };

    // --- Perform initial analysis when stock data arrives ---
    useEffect(() => {
        if (!stockData) return;

        // 1. Determine Dividend Frequency
        const sortedDividends = [...stockData.historicalDividends].sort((a, b) => new Date(a.date) - new Date(b.date));
        const lastYearDividends = sortedDividends.filter(d => new Date(d.date) > new Date(new Date().setFullYear(new Date().getFullYear() - 1)));

        let detectedFrequency = 'quarterly';
        if (lastYearDividends.length > 8) {
            if (lastYearDividends.length > 20) {
                detectedFrequency = 'weekly';
            } else {
                detectedFrequency = 'monthly';
            }
        }

        // 2. Calculate TTM Average Dividend & Growth Rate
        const ttmSum = lastYearDividends.reduce((sum, d) => sum + d.dividend, 0);
        const ttmAverage = ttmSum / lastYearDividends.length || 0;
        const dividendGrowth = calculateDividendTrendPercentage(stockData.historicalDividends) || 0;

        // 3. Calculate Stock Price Growth Rate
        const priceGrowth = calculateAnnualizedGrowth(stockData.historicalPrices.map(p => ({ date: p.date, value: p.close }))) || 0;

        setAnalysisData({
            detectedFrequency,
            ttmAverageDividend: ttmAverage,
            dividendGrowthRate: dividendGrowth,
            priceGrowthRate: priceGrowth
        });

    }, [stockData]);

    // --- Run payback simulation when inputs change ---
    useEffect(() => {
        if (!analysisData || !initialInvestment || initialInvestment <= 0) {
            setResults(null);
            return;
        }

        const runSimulationEngine = () => {
            setIsLoading(true);

            const frequencyToUse = manualFrequency || analysisData.detectedFrequency;
            const multiplier = frequencyToUse === 'weekly' ? 52 : frequencyToUse === 'monthly' ? 12 : 4;

            const runSimulation = (investment) => {
                let currentShares = investment / stockData.price;
                let cumulativeNetDividends = 0;
                let month = 0;
                let currentDividend = analysisData.ttmAverageDividend;
                let currentPrice = stockData.price;

                const monthlyDivGrowth = Math.pow(1 + analysisData.dividendGrowthRate, 1 / 12) - 1;
                const monthlyPriceGrowth = Math.pow(1 + analysisData.priceGrowthRate, 1 / 12) - 1;

                while (cumulativeNetDividends < investment && month < 600) {
                    month++;
                    currentPrice *= (1 + monthlyPriceGrowth);
                    currentDividend *= (1 + monthlyDivGrowth);

                    if (month % (12 / multiplier) === 0) {
                        const grossDividend = currentShares * currentDividend;
                        const netDividend = grossDividend * (1 - taxRate);
                        cumulativeNetDividends += netDividend;
                        const newShares = netDividend / currentPrice;
                        currentShares += newShares;
                    }
                }
                return month;
            };

            const baseMonths = runSimulation(initialInvestment);
            let bestMonths = baseMonths;
            let suggestedAddition = 0;
            const maxAdditional = initialInvestment * 2;

            for (let i = 1; i <= 10; i++) {
                const additional = (maxAdditional / 10) * i;
                const newMonths = runSimulation(initialInvestment + additional);
                if (newMonths < bestMonths) {
                    bestMonths = newMonths;
                    suggestedAddition = additional;
                }
            }

            setResults({
                ...analysisData,
                activeFrequency: frequencyToUse,
                basePaybackMonths: baseMonths,
                suggestedAdditionalInvestment: suggestedAddition,
                optimizedPaybackMonths: bestMonths,
            });
            setIsLoading(false);
        };

        runSimulationEngine();

    }, [analysisData, initialInvestment, taxRate, manualFrequency, stockData]);

    return (
        <div className="spacing-y-8">
            <h2 className="section-title">Investment Payback Calculator</h2>
            <div className="card-expressive" style={{ maxWidth: '800px', margin: '0 auto' }}>
                {/* START: Corrected Layout Container */}
                <div className="rc-grid-container-2-col" style={{ alignItems: 'end' }}>
                    <div>
                        <label htmlFor="payback-ticker" className="rc-label">Stock Ticker</label>
                        <div className="rc-input-with-button">
                            <input
                                type="text"
                                id="payback-ticker"
                                className="input-expressive"
                                value={ticker}
                                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                                onKeyPress={(e) => e.key === 'Enter' && handleFetchData()}
                                placeholder="e.g., AAPL"
                                disabled={isLoading}
                            />
                            <button onClick={handleFetchData} className="rc-fetch-button" disabled={isLoading}>
                                {isLoading ? <span className="rc-spinner"></span> : "Fetch"}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="initial-investment" className="rc-label">Initial Investment Amount ($)</label>
                        <input
                            type="number"
                            id="initial-investment"
                            className="input-expressive"
                            style={{ width: '100%' }}
                            value={initialInvestment}
                            onChange={(e) => setInitialInvestment(Number(e.target.value))}
                            placeholder="e.g., 10000"
                            disabled={isLoading}
                        />
                    </div>
                </div>
                {/* END: Corrected Layout Container */}

                {error && <p className="rc-error-message" style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>{error}</p>}

                {results && !isLoading && (
                    <div style={{ marginTop: 'var(--spacing-8)', paddingTop: 'var(--spacing-6)', borderTop: '1px solid var(--color-gray-200)' }}>
                        <div className="rc-grid-container-3-col" style={{ textAlign: 'center', marginBottom: 'var(--spacing-8)' }}>
                            <div className="stock-info-item">
                                <span className="stock-label">Current Price</span>
                                <span className="stock-value blue">{formatCurrency(stockData.price)}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">TTM Avg. Dividend</span>
                                <span className="stock-value purple">{formatCurrency(results.ttmAverageDividend)}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Active Frequency</span>
                                <span className="stock-value" style={{ textTransform: 'capitalize' }}>{results.activeFrequency}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Dividend Growth Rate</span>
                                <span className={`stock-value ${results.dividendGrowthRate >= 0 ? 'green' : 'red'}`}>{formatPercentage(results.dividendGrowthRate)}</span>
                            </div>
                            <div className="stock-info-item">
                                <span className="stock-label">Stock Price Growth Rate</span>
                                <span className={`stock-value ${results.priceGrowthRate >= 0 ? 'green' : 'red'}`}>{formatPercentage(results.priceGrowthRate)}</span>
                            </div>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--spacing-12)', flexWrap: 'wrap', marginBottom: 'var(--spacing-8)' }}>
                                <div>
                                    <label className="rc-label" style={{ justifyContent: 'center', fontSize: 'var(--font-size-base)' }}>Dividend Tax Rate</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 'var(--spacing-2)' }}>
                                        {[0, 0.15, 0.20].map(rate => (
                                            <button
                                                key={rate}
                                                onClick={() => setTaxRate(rate)}
                                                className={`button-tax ${taxRate === rate ? 'button-tax-active' : ''}`}
                                            >
                                                {`${rate * 100}% Tax`}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="rc-label" style={{ justifyContent: 'center', fontSize: 'var(--font-size-base)' }}>Manual Frequency Override</label>
                                    <div className="rc-toggle-group" style={{ margin: '0 auto' }}>
                                        {['weekly', 'monthly', 'quarterly'].map(freq => {
                                            const isActive = results.activeFrequency === freq;
                                            return (
                                                <button
                                                    key={freq}
                                                    onClick={() => setManualFrequency(freq)}
                                                    className={`rc-toggle-button ${isActive ? 'active' : ''}`}
                                                    style={isActive ? { backgroundColor: 'var(--color-purple-600)', color: 'white' } : {}}
                                                >
                                                    {freq.charAt(0).toUpperCase() + freq.slice(1)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="rc-grid-container-2-col">
                                <div className="rc-section-card" style={{ backgroundColor: 'var(--color-blue-50)' }}>
                                    <h3 className="rc-section-title" style={{ justifyContent: 'center' }}>Standard Payback</h3>
                                    <p style={{ fontSize: 'var(--font-size-4xl)', fontWeight: '800', color: 'var(--color-blue-700)' }}>
                                        {results.basePaybackMonths < 600 ? `${results.basePaybackMonths} Months` : '> 50 Years'}
                                    </p>
                                    <p style={{ color: 'var(--color-gray-600)' }}>to recoup {formatCurrency(initialInvestment)}</p>
                                </div>
                                <div className="rc-section-card" style={{ backgroundColor: 'var(--color-emerald-50)' }}>
                                    <h3 className="rc-section-title" style={{ justifyContent: 'center' }}>Optimized Payback</h3>
                                    <p style={{ fontSize: 'var(--font-size-4xl)', fontWeight: '800', color: 'var(--color-emerald-700)' }}>
                                        {results.optimizedPaybackMonths < 600 ? `${results.optimizedPaybackMonths} Months` : '> 50 Years'}
                                    </p>
                                    <p style={{ color: 'var(--color-gray-600)' }}>by adding {formatCurrency(results.suggestedAdditionalInvestment)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};



// --- Main Application Component ---
function App() {
    // --- State Variables ---
    // userId is now generated and stored in localStorage
    const [userId, setUserId] = useState(null);
    // isAuthReady is simplified to just indicate if userId has been loaded/generated
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [activeTab, setActiveTab] = useState('select'); // Set default tab to Price Graph

    // --- NEW API KEYS STATE (Fixed) ---
    // We initialize these directly from LocalStorage to prevent errors
    const [massiveApiKey, setMassiveApiKey] = useState(() =>
        loadFromLocalStorage(`${appId}_massive_api_key`, MASSIVE_API_KEY_DEFAULT)
    );
    const [finnhubApiKey, setFinnhubApiKey] = useState(() =>
        loadFromLocalStorage(`${appId}_finnhub_api_key`, FINNHUB_API_KEY_DEFAULT)
    );
    const [geminiApiKey, setGeminiApiKey] = useState(() =>
        loadFromLocalStorage(`${appId}_gemini_api_key`, GEMINI_API_KEY_DEFAULT)
    );

    // Stock data for each tab, loaded from localStorage
    const [selectStocks, setSelectStocks] = useState([]);
    const [monthlyStocks, setMonthlyStocks] = useState([]);

    // API Counter and Reset Timestamp, loaded from localStorage

    // API Counter and Reset Timestamp, loaded from localStorage
    const [apiCallCount, setApiCallCount] = useState(0);
    const [apiResetTimestamp, setApiResetTimestamp] = useState(0); // UTC ms of last reset 00:00 PST

    const [apiResetCountdown, setApiResetCountdown] = useState('');
    // Stock cache for FMP data (in-memory)
    const [stockCache, setStockCache] = useState({});
    // { [ticker]: { data: StockObject, timestamp: number } }
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [modalContent, setModalContent] = useState(null);
    // Custom ReactNode for modal content

    // API logs, loaded from localStorage
    const [apiLogsData, setApiLogsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // General loading state for API calls
    const [isAiSummaryLoading, setIsAiSummaryLoading] = useState(false);
    // Specific for AI summary

    // --- Margin Calculator State (ensure this is in App) ---
    const [marginBalance, setMarginBalance] = useState(0);
    const [isGoldMember, setIsGoldMember] = useState(false);
    const [calculatedInterest, setCalculatedInterest] = useState({ daily: 0, annual: 0, monthly: 0, effectiveAnnualRate: 0, interestPerTier: [] });


    // Monthly tab specific state:
    const [showMonthlyMarginCalc, setShowMonthlyMarginCalc] = useState(false);
    // Refs for triple-click feature
    const apiClickCountRef = useRef(0);
    // Default visibility for API counter changed from true to false
    const apiClickTimeoutRef = useRef(null);
    const [showApiCounter, setShowApiCounter] = useState(false); // Changed to false for default non-visibility
    const [localStorageSize, setLocalStorageSize] = useState('0 Bytes');
    // New state for local storage size

    // --- STEP 1: MODIFY THE STATE VARIABLES ---
    // Keep this state variable
    const [isOptimizeOn, setIsOptimizeOn] = useState(false); // Renamed from isAiFactorOn
    const aiJustRan = useRef(false);


    // --- STEP 2: REPLACE THE AI TOGGLE HANDLER ---
    // This function becomes much simpler.
    const handleOptimizeToggle = () => { // Renamed from handleAiFactorToggle
        setIsOptimizeOn(prev => !prev);
    };




    // --- STEP 4: REPLACE THE useEffect FOR THE AI CALCULATION ---
    useEffect(() => {
        if (!isOptimizeOn) return; // Use renamed state

        // Exit if the last change was made by the AI itself, to prevent an infinite loop.
        if (aiJustRan.current) {
            aiJustRan.current = false;
            return;
        }

        const runAiOptimization = () => {
            const totalInvestment = monthlyStocks.reduce((sum, stock) => sum + (Number(stock.amountInvested) || 0), 0);
            if (totalInvestment === 0) return;

            // 1. Calculate a composite "score" for each stock
            const stocksWithMetrics = monthlyStocks.map(stock => {
                const twelveMonthsAgo = new Date();
                twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
                const sortedPrices = [...(stock.historicalPrices || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
                const priceData12MonthsAgo = sortedPrices.find(p => new Date(p.date) >= twelveMonthsAgo);
                if (!priceData12MonthsAgo || !stock.price || !stock.lastDividend) {
                    return { ...stock, score: 0 };
                }

                const price12MonthsAgo = priceData12MonthsAgo.close;
                // Calculate the three components of the score
                const gainPotential = (stock.price / price12MonthsAgo) - 1;
                let dividendMultiplier = 12;
                if (stock.dividendFrequency === 'quarterly') dividendMultiplier = 4;
                if (stock.dividendFrequency === 'weekly') dividendMultiplier = 52;
                const incomePotential = (stock.lastDividend * dividendMultiplier) / stock.price;

                // Give equal weight to gain and income potential
                const score = gainPotential + incomePotential;
                return { ...stock, score };
            }).filter(s => s.score > 0); // Only consider stocks with a positive score

            // 2. Calculate the total score of all eligible stocks
            const totalScore = stocksWithMetrics.reduce((sum, s) => sum + s.score, 0);
            if (totalScore === 0) return; // Cannot allocate if no stocks are viable

            // 3. Create new allocations based on each stock's proportional score
            const optimizedStocks = monthlyStocks.map(originalStock => {
                const ratedStock = stocksWithMetrics.find(s => s.id === originalStock.id);
                if (ratedStock) {
                    const proportion = ratedStock.score / totalScore;
                    return {
                        ...originalStock,
                        amountInvested: (totalInvestment * proportion).toFixed(2)
                    };
                }
                // If stock was not eligible, its allocation becomes 0
                return { ...originalStock, amountInvested: 0 };
            });

            // 4. Set the flag and update the state
            aiJustRan.current = true;
            setMonthlyStocks(optimizedStocks);
        };

        runAiOptimization();
    }, [isOptimizeOn, monthlyStocks]); // This now runs whenever the stocks change while AI is on



    useEffect(() => {
        let currentUserId = loadFromLocalStorage(`${appId}_user_id`, null);
        if (!currentUserId) {
            currentUserId = generateUniqueId();
            saveToLocalStorage(`${appId}_user_id`, currentUserId);
        }
        setUserId(currentUserId);
        setIsAuthReady(true);

        const loadedSelectStocks = loadFromLocalStorage(`${appId}_users_${currentUserId}_select_stocks`, []);
        loadedSelectStocks.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        setSelectStocks(loadedSelectStocks);

        const loadedMonthlyStocks = loadFromLocalStorage(`${appId}_users_${currentUserId}_monthly_stocks`, []);
        loadedMonthlyStocks.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        setMonthlyStocks(loadedMonthlyStocks);

        const loadedApiMeta = loadFromLocalStorage(`${appId}_users_${currentUserId}_api_meta_current`, {
            count: 0,
            lastResetTimestamp: getStartOfDayPST(),
            createdAt: Date.now()
        });
        setApiCallCount(loadedApiMeta.count);
        setApiResetTimestamp(loadedApiMeta.lastResetTimestamp);

        const loadedApiLogs = loadFromLocalStorage(`${appId}_users_${currentUserId}_api_logs`, []);
        loadedApiLogs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setApiLogsData(loadedApiLogs);
        const loadedPublicStockCache = loadFromLocalStorage(`${appId}_public_stock_cache`, {});
        setStockCache(loadedPublicStockCache);

        // API Keys are loaded in useState above.

        updateLocalStorageSize();

    }, []);
















    useEffect(() => {
        if (!isAuthReady || !userId) return;
















        const checkAndResetApiCounter = () => {
            const currentStartOfDayPST = getStartOfDayPST();
            if (currentStartOfDayPST > apiResetTimestamp) {
                const newApiMeta = {
                    count: 0,
                    lastResetTimestamp: currentStartOfDayPST,
                    createdAt: Date.now()
                };
                saveToLocalStorage(`${appId}_users_${userId}_api_meta_current`, newApiMeta);
                setApiCallCount(0);
                setApiResetTimestamp(currentStartOfDayPST);
                const newLog = {
                    timestamp: Date.now(),
                    type: 'API Counter Reset',
                    ticker: 'N/A',
                    status: 'success',
                    details: `Daily API counter reset from ${apiCallCount} to 0.`,
                };
                const currentLogs = loadFromLocalStorage(`${appId}_users_${userId}_api_logs`, []);
                saveToLocalStorage(`${appId}_users_${userId}_api_logs`, [newLog, ...currentLogs].slice(0, 50));
                setApiLogsData(prevLogs => [newLog, ...prevLogs].slice(0, 50));
            }
        };
















        checkAndResetApiCounter();
        const interval = setInterval(checkAndResetApiCounter, 60 * 60 * 1000);
















        return () => clearInterval(interval);
    }, [isAuthReady, userId, apiResetTimestamp, apiCallCount]);
















    useEffect(() => {
        if (!isAuthReady || !userId) {
            setApiResetCountdown('Loading...');
            return;
        }
















        const updateCountdown = () => {
            const now = new Date();
















            const formatter = new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                timeZone: 'America/Los_Angeles',
            });
            const parts = formatter.formatToParts(now);
            let year, month, day;
            for (const part of parts) {
                if (part.type === 'year') year = parseInt(part.value);
                if (part.type === 'month') month = parseInt(part.value);
                if (part.type === 'day') day = parseInt(part.value);
            }
















            const todayPSTMidnight = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
            todayPSTMidnight.setHours(0, 0, 0, 0);
            const todayMidnightPST_Timestamp = todayPSTMidnight.getTime();
































            let nextResetTimeUtc;
            if (now.getTime() < todayMidnightPST_Timestamp) {
                nextResetTimeUtc = todayMidnightPST_Timestamp;
            } else {
                const tomorrowPSTMidnight = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
                tomorrowPSTMidnight.setDate(tomorrowPSTMidnight.getDate() + 1);
                tomorrowPSTMidnight.setHours(0, 0, 0, 0);
                nextResetTimeUtc = tomorrowPSTMidnight.getTime();
            }
















            const timeLeft = nextResetTimeUtc - now.getTime();
















            if (timeLeft <= 0) {
                setApiResetCountdown('Resetting...');
            } else {
                const hours = Math.floor(timeLeft / (1000 * 60 * 60));
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
















                const formatTwoDigits = (num) => String(num).padStart(2, '0');
















                setApiResetCountdown(`${formatTwoDigits(hours)}h ${formatTwoDigits(minutes)}m ${formatTwoDigits(seconds)}s`);
            }
        };
















        const countdownInterval = setInterval(updateCountdown, 1000);
        updateCountdown();
















        return () => clearInterval(countdownInterval);
    }, [isAuthReady, userId]);
















    const logApiCall = useCallback((type, ticker, status, details) => {
        if (!userId) return;
        const newLogEntry = {
            timestamp: Date.now(),
            type,
            ticker: ticker || 'N/A',
            status,
            details
        };
        const currentLogs = loadFromLocalStorage(`${appId}_users_${userId}_api_logs`, []);
        const updatedLogs = [newLogEntry, ...currentLogs].slice(0, 50);
        saveToLocalStorage(`${appId}_users_${userId}_api_logs`, updatedLogs);
        setApiLogsData(updatedLogs);
        updateLocalStorageSize();
    }, [userId]);
















    const adjustApiCounter = useCallback((delta) => {
        if (!userId) return;
        const apiMeta = loadFromLocalStorage(`${appId}_users_${userId}_api_meta_current`, { count: 0, lastResetTimestamp: getStartOfDayPST() });
        const newCount = Math.max(0, apiMeta.count + delta);
        const updatedApiMeta = { ...apiMeta, count: newCount };
















        saveToLocalStorage(`${appId}_users_${userId}_api_meta_current`, updatedApiMeta);
        setApiCallCount(newCount);
        logApiCall('Manual API Adjustment', 'N/A', 'success', `API counter adjusted by ${delta}. New count: ${newCount}`);
    }, [userId, logApiCall]);
















    // --- NEW DATA FETCHING FUNCTION (Resilient + Robust Market Cap Fallback) ---
    const fetchStockData = useCallback(async (ticker) => {
        if (!ticker) return { price: null, companyName: '', lastDividend: null, historicalDividends: [], historicalPrices: [], marketCap: null, yearHigh: null, yearLow: null };

        const normalizedTicker = ticker.toUpperCase();
        setIsLoading(true);

        // 1. CHECK CACHE
        if (stockCache[normalizedTicker] && (Date.now() - stockCache[normalizedTicker].timestamp < CACHE_DURATION_MS)) {
            setIsLoading(false);
            return stockCache[normalizedTicker].data;
        }

        // Initialize standard object structure
        let fetchedData = {
            ticker: normalizedTicker,
            price: null,
            companyName: normalizedTicker, // Default to Ticker to avoid blank name
            lastDividend: null,
            historicalDividends: [],
            historicalPrices: [],
            marketCap: null,
            yearHigh: null,
            yearLow: null
        };

        try {
            // --- 2. FETCH FINNHUB DATA (Price & Profile) ---
            const quoteRes = await fetch(`${FINNHUB_BASE_URL}/quote?symbol=${normalizedTicker}&token=${finnhubApiKey}`);
            if (quoteRes.ok) {
                const quoteData = await quoteRes.json();
                if (quoteData.c) {
                    fetchedData.price = quoteData.c;
                    fetchedData.yearHigh = quoteData.h;
                    fetchedData.yearLow = quoteData.l;
                }
            }

            const profileRes = await fetch(`${FINNHUB_BASE_URL}/stock/profile2?symbol=${normalizedTicker}&token=${finnhubApiKey}`);
            if (profileRes.ok) {
                const profileData = await profileRes.json();
                if (profileData.name) fetchedData.companyName = profileData.name;
                // Finnhub returns market capitalization in Millions
                if (profileData.marketCapitalization) fetchedData.marketCap = profileData.marketCapitalization * 1000000;
            }

            // --- 3. FETCH MASSIVE (POLYGON) DATA (Dividends & History) ---
            const endDate = new Date().toISOString().split('T')[0];
            const startDateObj = new Date();
            startDateObj.setFullYear(startDateObj.getFullYear() - 2);
            const startDate = startDateObj.toISOString().split('T')[0];

            // Dividends - Force Sort DESC to get latest first
            const divRes = await fetch(`${MASSIVE_BASE_URL}/v3/reference/dividends?ticker=${normalizedTicker}&limit=50&sort=ex_dividend_date&order=desc&apiKey=${massiveApiKey}`);
            if (divRes.ok) {
                const divData = await divRes.json();
                const rawDividends = divData.results || [];

                if (Array.isArray(rawDividends)) {
                    fetchedData.historicalDividends = rawDividends.map(d => ({
                        date: d.pay_date || d.ex_dividend_date,
                        dividend: d.cash_amount
                    })).filter(d => d.date && d.dividend > 0);

                    // First item is latest due to sort order
                    if (fetchedData.historicalDividends.length > 0) {
                        fetchedData.lastDividend = fetchedData.historicalDividends[0].dividend;
                    }

                    // Re-sort Ascending for Charts
                    fetchedData.historicalDividends.sort((a, b) => new Date(a.date) - new Date(b.date));
                }
            }

            // History
            const histRes = await fetch(`${MASSIVE_BASE_URL}/v2/aggs/ticker/${normalizedTicker}/range/1/day/${startDate}/${endDate}?adjusted=true&sort=asc&limit=5000&apiKey=${massiveApiKey}`);
            if (histRes.ok) {
                const histData = await histRes.json();
                const rawHistory = histData.results || [];
                if (Array.isArray(rawHistory)) {
                    fetchedData.historicalPrices = rawHistory.map(p => ({
                        date: new Date(p.t),
                        close: p.c
                    }));
                }
            }

            // --- 4. ROBUST FALLBACK: FETCH MARKET CAP FROM MASSIVE ---
            // If Finnhub failed to provide Market Cap (common for ETFs like FEPI)
            if (!fetchedData.marketCap) {
                try {
                    // Use Ticker Details v3 Endpoint
                    const detailsRes = await fetch(`${MASSIVE_BASE_URL}/v3/reference/tickers/${normalizedTicker}?apiKey=${massiveApiKey}`);

                    if (detailsRes.ok) {
                        const detailsData = await detailsRes.json();
                        if (detailsData.results) {
                            // Check specifically for 'market_cap'
                            if (detailsData.results.market_cap) {
                                fetchedData.marketCap = detailsData.results.market_cap;
                            }
                            // Also update name if we still just have the ticker symbol
                            if (detailsData.results.name && fetchedData.companyName === normalizedTicker) {
                                fetchedData.companyName = detailsData.results.name;
                            }
                        }
                    } else {
                        console.warn(`Massive Details Fallback Failed: ${detailsRes.status}`);
                    }
                } catch (err) {
                    console.warn("Massive Details Network Error", err);
                }
            }

        } catch (error) {
            console.error("API Network Error:", error);
        }

        // 5. UPDATE CACHE & RETURN
        setStockCache(prev => ({
            ...prev,
            [normalizedTicker]: { data: fetchedData, timestamp: Date.now() }
        }));

        setIsLoading(false);
        return fetchedData;

    }, [stockCache, finnhubApiKey, massiveApiKey]);
















    const updateStocksInLocalStorage = useCallback((tabType, updatedStocks) => {
        saveToLocalStorage(`${appId}_users_${userId}_${tabType}_stocks`, updatedStocks);
        if (tabType === 'select') {
            setSelectStocks(updatedStocks);
        } else {
            setMonthlyStocks(updatedStocks);
        }
        updateLocalStorageSize();
    }, [userId, appId]);
















    const addStockRow = useCallback(async (tabType) => {
        if (!isAuthReady || !userId) {
            setModalTitle('App Not Ready');
            setModalMessage('Please wait for the app to finish loading.');
            setShowModal(true);
            return;
        }
        const currentStocks = tabType === 'select' ? selectStocks : monthlyStocks;
        if (currentStocks.length >= 10) {
            setModalTitle('Limit Reached');
            setModalMessage('You can add a maximum of 10 stocks per tab.');
            setShowModal(true);
            return;
        }
















        const newStock = {
            id: generateUniqueId(),
            ticker: '',
            amountInvested: null,
            price: null,
            lastDividend: null,
            companyName: '',
            historicalDividends: [],
            historicalPrices: [],
            safetyScore: null,
            riskMetrics: {},
            marketCap: null,
            yearHigh: null,
            yearLow: null,
            isQuarterlyDividendsOn: false,
            createdAt: Date.now(),
        };
















        const updatedStocks = [...currentStocks, newStock];
        updatedStocks.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        updateStocksInLocalStorage(tabType, updatedStocks);
















    }, [isAuthReady, userId, selectStocks, monthlyStocks, updateStocksInLocalStorage]);
















    const handleStockInputChange = useCallback((id, field, value, tabType) => {
        const updater = (prevStocks) => {
            return prevStocks.map(stock =>
                stock.id === id ? { ...stock, [field]: value } : stock
            );
        };
        const updatedStocks = updater(tabType === 'select' ? selectStocks : monthlyStocks);
        updateStocksInLocalStorage(tabType, updatedStocks);
    }, [selectStocks, monthlyStocks, updateStocksInLocalStorage]);
















    const handleTickerEnter = useCallback(async (stockId, tickerValue, tabType, forceRefresh = false) => {
        if (!isAuthReady || !userId) {
            setModalTitle('App Not Ready');
            setModalMessage('Please wait for the app to finish loading.');
            setShowModal(true);
            return;
        }
        const normalizedTicker = tickerValue.toUpperCase().trim();

        const currentStocks = tabType === 'select' ? [...selectStocks] : [...monthlyStocks];
        const stockIndex = currentStocks.findIndex(s => s.id === stockId);

        if (stockIndex === -1) return;

        const existingStock = currentStocks[stockIndex];

        if (existingStock.ticker === normalizedTicker && !forceRefresh && normalizedTicker !== '') {
            return;
        }

        if (!normalizedTicker) {
            const clearedStock = {
                ...existingStock,
                ticker: '',
                amountInvested: null,
                price: null,
                lastDividend: null,
                companyName: '',
                historicalDividends: [],
                historicalPrices: [],
                marketCap: null,
                yearHigh: null,
                yearLow: null,
                isQuarterlyDividendsOn: false,
            };
            currentStocks[stockIndex] = clearedStock;
            updateStocksInLocalStorage(tabType, currentStocks);
            return;
        }

        // *** FIX IS HERE: Changed fetchFmpData to fetchStockData ***
        const fetchedData = await fetchStockData(normalizedTicker);

        const updatedStock = {
            ...existingStock,
            ticker: normalizedTicker,
            price: fetchedData.price,
            lastDividend: fetchedData.lastDividend,
            companyName: fetchedData.companyName,
            historicalDividends: fetchedData.historicalDividends || [],
            historicalPrices: fetchedData.historicalPrices || [],
            marketCap: fetchedData.marketCap || null,
            yearHigh: fetchedData.yearHigh || null,
            yearLow: fetchedData.yearLow || null,
        };

        currentStocks[stockIndex] = updatedStock;
        updateStocksInLocalStorage(tabType, currentStocks);
    }, [isAuthReady, userId, fetchStockData, selectStocks, monthlyStocks, updateStocksInLocalStorage]);































    const handleAmountInvestedSave = useCallback(async (id, value, tabType, currentTicker) => {
        if (!isAuthReady || !userId) {
            setModalTitle('App Not Ready');
            setModalMessage('Please wait for the app to finish loading.');
            setShowModal(true);
            return;
        }
        const numericValue = value === '' ? null : parseFloat(value);
















        const currentStocks = tabType === 'select' ? [...selectStocks] : [...monthlyStocks];
        const stockIndex = currentStocks.findIndex(s => s.id === id);
















        if (stockIndex === -1) return;
















        currentStocks[stockIndex].amountInvested = numericValue;
        updateStocksInLocalStorage(tabType, currentStocks);
















        if (currentTicker) {
            await handleTickerEnter(id, currentTicker, tabType, true);
        }
    }, [isAuthReady, userId, selectStocks, monthlyStocks, updateStocksInLocalStorage, handleTickerEnter]);
































    const deleteStockRow = useCallback(async (id, tabType) => {
        if (!isAuthReady || !userId) return;
        const currentStocks = tabType === 'select' ? selectStocks : monthlyStocks;
        const updatedStocks = currentStocks.filter(stock => stock.id !== id);
        updateStocksInLocalStorage(tabType, updatedStocks);
    }, [isAuthReady, userId, selectStocks, monthlyStocks, updateStocksInLocalStorage]);
















    // --- STEP 3: MODIFY THE resetTabData FUNCTION ---
    const resetTabData = useCallback(async (tabType) => {
        if (!isAuthReady || !userId) {
            setModalTitle('App Not Ready');
            setModalMessage('Please wait for the app to finish loading.');
            setShowModal(true);
            return;
        }
        updateStocksInLocalStorage(tabType, []);

        // Add this line to also turn off the AI Factor
        setIsOptimizeOn(false);

        setModalTitle('Success');
        setModalMessage(`${tabType} tab data has been reset.`);
        setShowModal(true);
    }, [isAuthReady, userId, updateStocksInLocalStorage]);
















    const getAiSummary = useCallback(async (data, isTsrSummary = false, isPortfolioSummary = false, isOptimization = false, showResultInModal = true) => {
        if (!userId) {
            if (showResultInModal) {
                setModalTitle('App Not Ready');
                setModalMessage('Please wait for the app to finish loading.');
                setShowModal(true);
            }
            return null;
        }

        setIsAiSummaryLoading(true);
        let prompt = '';
        let modalTitleText = 'AI Summary';

        if (isOptimization) {
            modalTitleText = 'Optimization Analysis';
            // --- NEW: Objectives-Based Prompt ---
            prompt = `You are a financial analyst explaining the results of a portfolio optimization to a user.
            
            The user's specific objectives were:
            1. Overall Yield: 13%+
            2. Non-Dividend TTM Total Return: No less than -1% (Strict Floor)
            3. Blended Tax Rate: 7-9% (Preferred)

            Here is the outcome of the optimization:
            ${data.analysisReport}

            Please provide a brief summary (3-4 sentences):
            - Confirm which objectives were met.
            - If an objective was MISSED (especially Yield or Tax), explain EXACTLY why based on the available stocks (e.g., "Could not reach 13% yield because the highest yielding stock available is only 5%").
            - Explain which stocks were rejected due to the -1% TTM return rule.
            - Use a professional, helpful tone.`;

        } else if (isPortfolioSummary) {
            modalTitleText = 'AI Portfolio Analysis';
            prompt = `Act as a financial analyst and provide a brief, insightful summary (around 4-5 sentences) of the following dividend investment portfolio.
            
            Key Metrics:
            - Number of Stocks: ${data.numberOfStocks}
            - Tickers: ${data.tickers}
            - Total Amount Invested: ${formatCurrency(data.totalAmountInvested)}
            - Net Estimated Annual Income (after tax): ${formatCurrency(data.netAnnualIncome)}
            - Blended Tax Rate: ${formatPercentage(data.blendedTaxRate)}

            Your summary should touch upon:
            1. The relationship between the total investment and the net annual income.
            2. What the blended tax rate suggests about the portfolio's tax efficiency.
            3. Use all metrics in portfolio summary to build a fiscal narrative.
            4. A concluding sentence about the portfolio's concentration given the number of stocks.
            Do not give any financial advice.`;
        } else if (isTsrSummary) {
            modalTitleText = 'Total Shareholder Return Analysis';
            prompt = `Analyze the Total Shareholder Return (TSR) for ${data.companyName} (${data.ticker}).
            - Initial Investment: ${formatCurrency(data.initialInvestment)}
            - Time Period: ${data.timePeriod}
            - Total Return %: ${data.totalReturnPercentage.toFixed(2)}%
            - Total Return $: ${formatCurrency(data.totalReturnInUSD)}
            
            Explain what this return signifies regarding the stock's performance (Growth vs Income) and how it compares to a general benchmark concept (like "safe assets"). Keep it brief.`;
        } else {
            prompt = `Provide a very brief financial summary for ${data.companyName} (${data.ticker}). 
            Focus on its dividend sustainability, recent price trend, and primary business sector. 
            Max 3 sentences.`;
        }

        const chatHistory = [{ role: "user", parts: [{ text: prompt }] }];
        const payload = { contents: chatHistory };
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${geminiApiKey}`;

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();

            if (result.candidates && result.candidates.length > 0 &&
                result.candidates[0].content && result.candidates[0].content.parts &&
                result.candidates[0].content.parts.length > 0) {
                const text = result.candidates[0].content.parts[0].text;
                if (showResultInModal) {
                    setModalTitle(modalTitleText);
                    setModalMessage(text);
                    setShowModal(true);
                }
                logApiCall('Gemini AI Summary', data.ticker || 'Portfolio', 'success', 'AI summary generated successfully.');
                return text;
            } else {
                const errorMsg = result.error?.message || 'Could not get AI summary. Unexpected response structure.';
                if (showResultInModal) {
                    setModalTitle('AI Summary Error');
                    setModalMessage(errorMsg);
                    setShowModal(true);
                }
                logApiCall('Gemini AI Summary', data.ticker || 'Portfolio', 'error', `Failed to generate: ${errorMsg}`);
            }
        } catch (error) {
            console.error("Error generating AI summary:", error);
            if (showResultInModal) {
                setModalTitle('AI Summary Error');
                setModalMessage(`Failed to generate AI summary: ${error.message}`);
                setShowModal(true);
            }
            logApiCall('Gemini AI Summary', data.ticker || 'Portfolio', 'error', `Fetch failed: ${error.message}`);
        } finally {
            setIsLoading(false);
            setIsAiSummaryLoading(false);
        }
        return null;
    }, [logApiCall, userId, geminiApiKey]);




















    const showHistoricalDividendsGraph = useCallback((ticker) => {
        const stock = monthlyStocks.find(s => s.ticker === ticker) || selectStocks.find(s => s.ticker === ticker);
        if (!stock || !stock.historicalDividends || stock.historicalDividends.length === 0) {
            setModalTitle('No Dividend Data');
            setModalMessage(`No historical dividend data available for ${ticker}.`);
            setShowModal(true);
            return;
        }
















        setModalTitle(`Historical Dividends for ${stock.companyName} (${ticker})`);
        setModalMessage(null);
        setModalContent(
            <div className="relative-chart-container">
                <HistoricalDividendsChart data={stock.historicalDividends} />
            </div>
        );
        setShowModal(true);
    }, [monthlyStocks, selectStocks]);
































    const showHistoricalPriceGraph = useCallback((ticker) => {
        const stock = monthlyStocks.find(s => s.ticker === ticker) || selectStocks.find(s => s.ticker === ticker);
        if (!stock || !stock.historicalPrices || stock.historicalPrices.length === 0) {
            setModalTitle('No Historical Price Data');
            setModalMessage(`No historical price data available for ${ticker}.`);
            setShowModal(true);
            return;
        }
















        setModalTitle(`Historical Prices for ${stock.companyName} (${ticker})`);
        setModalMessage(null);
        setModalContent(
            <div className="relative-chart-container">
                <HistoricalPriceChart data={stock.historicalPrices} />
            </div>
        );
        setShowModal(true);
    }, [monthlyStocks, selectStocks]);
































    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };
















    const updateLocalStorageSize = useCallback(() => {
        let totalBytes = 0;
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.startsWith(`${appId}_`)) {
                const value = localStorage.getItem(key);
                totalBytes += (value.length * 2);
            }
        }
        setLocalStorageSize(formatBytes(totalBytes));
    }, [appId]);
















    const handleApiWordClick = useCallback(() => {
        clearTimeout(apiClickTimeoutRef.current);
        apiClickCountRef.current += 1;

        if (apiClickCountRef.current === 3) {
            apiClickCountRef.current = 0; // Reset counter

            const ApiKeyEditor = () => {
                const [massiveInput, setMassiveInput] = useState(massiveApiKey);
                const [finnhubInput, setFinnhubInput] = useState(finnhubApiKey);
                const [geminiKeyInput, setGeminiKeyInput] = useState(geminiApiKey);

                const handleUpdate = () => {
                    setMassiveApiKey(massiveInput);
                    setFinnhubApiKey(finnhubInput);
                    setGeminiApiKey(geminiKeyInput);

                    saveToLocalStorage(`${appId}_massive_api_key`, massiveInput);
                    saveToLocalStorage(`${appId}_finnhub_api_key`, finnhubInput);
                    saveToLocalStorage(`${appId}_gemini_api_key`, geminiKeyInput);

                    setModalTitle('Success');
                    setModalMessage('API Keys have been updated and saved.');
                    setModalContent(null);
                    setTimeout(() => setShowModal(false), 2000);
                };

                return (
                    <div className="text-gray-700 api-info-modal-content">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Massive API Key:</label>
                                <input
                                    type="text"
                                    value={massiveInput}
                                    onChange={(e) => setMassiveInput(e.target.value)}
                                    className="input-expressive w-full"
                                    placeholder="Enter Massive Key"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Finnhub API Key:</label>
                                <input
                                    type="text"
                                    value={finnhubInput}
                                    onChange={(e) => setFinnhubInput(e.target.value)}
                                    className="input-expressive w-full"
                                    placeholder="Enter Finnhub Key"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Gemini API Key:</label>
                                <input
                                    type="text"
                                    value={geminiKeyInput}
                                    onChange={(e) => setGeminiKeyInput(e.target.value)}
                                    className="input-expressive w-full"
                                    placeholder="Enter Gemini API Key"
                                />
                            </div>
                        </div>
                        <div className="margin-top-6 flex-wrap-center">
                            <button onClick={handleUpdate} className="m3-button filled-blue">
                                <RotateCcw size={20} />
                                <span>Update Keys</span>
                            </button>
                        </div>
                    </div>
                );
            };

            setModalTitle('API Key Management');
            setModalMessage(null);
            setModalContent(<ApiKeyEditor />);
            setShowModal(true);
        } else {
            apiClickTimeoutRef.current = setTimeout(() => {
                apiClickCountRef.current = 0;
            }, 500);
        }
    }, [userId, localStorageSize, massiveApiKey, finnhubApiKey, geminiApiKey, appId, setModalTitle, setModalMessage, setModalContent, setShowModal]);































    const handleMarginCalc = useCallback(() => {
        const interest = calculateMarginInterest(marginBalance, isGoldMember);
        setCalculatedInterest(interest);
    }, [marginBalance, isGoldMember]);
















    useEffect(() => {
        handleMarginCalc();
    }, [marginBalance, isGoldMember, handleMarginCalc]);

































    const appFeaturesList = (
        <div className="features-modal-content">
            <h3>User Data Persistence (Local Storage):</h3>
            <ul>
                <li>Unique User ID: Auto-generates and stores ID in localStorage.</li>
                <li>Local Storage: Saves/loads data, using ~<span className="font-semibold">{localStorageSize}</span> space.</li>
            </ul>
















            <h3>Stock Management (Select & Monthly Tabs):</h3>
            <ul>
                <li>Add Rows: Up to 10 stocks per tab.</li>
                <li>Input/Fetch: Ticker/investment; gets price, name, dividend, history, market cap, 52-wk range.</li>
                <li>Metrics: Calculates shares, annual/monthly dividends, div/$ invested, yield.</li>
                <li>Quarterly Toggle: Annualize as quarterly (x4) or monthly (x12) in Monthly tab.</li>
                <li>Delete/Reset: Remove rows or clear tab.</li>
            </ul>
















            <h3>Portfolio Summary (Monthly Tab):</h3>
            <ul>
                <li>Aggregates: Total invested, 12-mo dividends, value, yield.</li>
                <li>Download: Text summary export.</li>
            </ul>
















            <h3>Margin Interest Calculator:</h3>
            <ul>
                <li>Inputs: Balance; Gold toggle.</li>
                <li>Calculations: Daily/monthly/annual interest; tiered breakdown.</li>
                <li>Net Dividends: Adjusted after interest subtraction.</li>
                <li>Tax Toggle: 0%/15%/20% for net after-tax dividends.</li>
            </ul>
















            <h3>Data Visualization (D3.js Charts):</h3>
            <ul>
                <li>Dividends: Bar chart of history.</li>
                <li>Prices: Line chart of closing prices.</li>
            </ul>
















            <h3>AI Features (Gemini API):</h3>
            <ul>
                <li>Company Summary: AI-generated per stock.</li>
                <li>TSR Explanation: AI analysis of returns.</li>
                <li>Loaders: Spinners during processing.</li>
                <li>API: Hardcoded key for client-side calls.</li>
            </ul>
















            <h3>API Monitoring & Tools:</h3>
            <ul>
                <li>Counter: FMP calls (250/day limit); countdown; toggle visibility.</li>
                <li>Adjust: Manual inc/dec count.</li>
                <li>Logs: Modal view/download of FMP/Gemini calls.</li>
                <li>Info: Triple-click for FMP key/docs.</li>
                <li>Caching: FMP data cached 6 hours.</li>
            </ul>
















            <h3>UI & Experience:</h3>
            <ul>
                <li>Responsive: Adapts to devices.</li>
                <li>Modals: For charts, summaries, info.</li>
                <li>Styling: Consistent theme, CSS.</li>
                <li>Formatting: Readable elements.</li>
            </ul>
        </div>
    );
















    const handleFeaturesClick = useCallback(() => {
        setModalTitle('Dividend Researcher App Features');
        setModalMessage(null);
        setModalContent(appFeaturesList);
        setShowModal(true);
    }, [setModalTitle, setModalMessage, setModalContent, setShowModal, appFeaturesList]);
















    const clearAllAppData = useCallback(() => {
        if (!isAuthReady || !userId) {
            setModalTitle('App Not Ready');
            setModalMessage('Please wait for the app to finish loading.');
            setShowModal(true);
            return;
        }
















        setModalTitle('Confirm Data Deletion');
        setModalMessage('Are you sure you want to delete ALL application data from your browser\'s local storage? This action cannot be undone.');
        setModalContent(
            <div className="flex-wrap-center gap-4 margin-top-4">
                <button
                    onClick={() => {
                        const keysToRemove = [];
                        for (let i = 0; i < localStorage.length; i++) {
                            const key = localStorage.key(i);
                            if (key.startsWith(`${appId}_`)) {
                                keysToRemove.push(key);
                            }
                        }
















                        keysToRemove.forEach(key => {
                            localStorage.removeItem(key);
                        });
















                        setSelectStocks([]);
                        setMonthlyStocks([]);
                        setApiCallCount(0);
                        setApiResetTimestamp(getStartOfDayPST());
                        setApiLogsData([]);
                        setStockCache({});
                        setUserId(null);
                        updateLocalStorageSize();
















                        setModalTitle('App Data Cleared');
                        setModalMessage('All application data has been removed from your browser\'s local storage. A new user ID will be generated on next load.');
                        setShowModal(true);
                    }}
                    className="btn-expressive-danger button-padding-sm"
                >
                    <Trash2 size={16} className="button-icon-margin-right-sm" /> Yes, Delete All Data
                </button>
                <button
                    onClick={() => setShowModal(false)}
                    className="btn-expressive-secondary button-padding-sm"
                >
                    Cancel
                </button>
            </div>
        );
        setShowModal(true);
    }, [isAuthReady, userId, appId, updateLocalStorageSize, setModalTitle, setModalMessage, setModalContent, setShowModal]);
















































    // --- UI Layout ---
    return (
        <div className="min-height-screen bg-gradient-br flex-col-center padding-base responsive-padding">
            {/* Custom Global CSS Styles */}
            <style>
                {`
                :root {
                    /* Colors */
                    --color-blue-50: #eff6ff;
        --color-blue-100: #dbeafe;
        --color-blue-200: #bfdbfe;
        --color-blue-500: #3b82f6;
        --color-blue-600: #2563eb;
        --color-blue-700: #1d4ed8; /* Active 'Select Stocks' blue */
        --color-blue-800: #1e40af; /* Inactive 'Select Stocks' text blue */
















        --color-indigo-600: #4f46e5; /* Active 'Dividend History' indigo */
        --color-indigo-800: #3730a3; /* Inactive 'Dividend History' text indigo */
















        --color-purple-600: #7c3aed; /* Active 'Margin Calculator' purple */
        --color-purple-800: #5b21b6; /* Inactive 'Margin Calculator' text purple */
















        --color-green-700: #15803d; /* Active 'Monthly Income' green */
        --color-green-800: #14532d; /* Inactive 'Monthly Income' text green */
















        --color-emerald-700: #047857; /* Active 'YoY Growth' emerald */
        --color-emerald-800: #065f46; /* Inactive 'YoY Growth' text emerald */
















        --color-red-600: #dc2626; /* Active 'Risk Calculator' red */
        --color-red-800: #991b1b; /* Inactive 'Risk Calculator' text red */
        
        --color-orange-600: #ea580c; /* Active 'Price Graph' orange */
        --color-orange-700: #c2410c; /* Inactive 'Price Graph' text orange */
















                    --color-blue-50: #eff6ff;
                    --color-blue-100: #dbeafe;
                    --color-blue-200: #bfdbfe;
                    --color-blue-500: #3b82f6;
                    --color-blue-600: #2563eb;
                    --color-blue-700: #1d4ed8;
                    --color-blue-800: #1e40af;
















                    --color-indigo-50: #eef2ff;
                    --color-indigo-100: #e0e7ff;
                    --color-indigo-200: #c7d2fe;
                    --color-indigo-600: #4f46e5;
                    --color-indigo-700: #4338ca;
                    --color-indigo-800: #3730a3;
















                    --color-purple-50: #faf5ff;
                    --color-purple-100: #f3e8ff;
                    --color-purple-200: #e9d5ff;
                    --color-purple-400: #a78bfa;
                    --color-purple-500: #8b5cf6;
                    --color-purple-600: #7c3aed;
                    --color-purple-700: #6d28d9;
                    --color-purple-800: #5b21b6;
















                    --color-green-50: #f0fdf4;
                    --color-green-100: #dcfce7;
                    --color-green-200: #bbf7d0;
                    --color-green-600: #16a34a;
                    --color-green-700: #15803d;
                    --color-green-800: #14532d;
















                    --color-emerald-100: #d1fae5; /* Tailwind emerald-100 */
                    --color-emerald-200: #a7f3d0; /* Tailwind emerald-200 */
                    --color-emerald-700: #047857; /* Tailwind emerald-700 */
                    --color-emerald-400: #34d399; /* Added for infographic text */
                    --color-emerald-500: #10b981; /* Added for infographic chart */
                    --color-emerald-800: #065f46; /* Added for infographic item */
















                    --color-red-100: #fee2e2;
                    --color-red-500: #ef4444;
                    --color-red-600: #dc2626;
                    --color-red-700: #b91c1c;
                    --color-red-400: #f87171; /* Added for infographic text */
                    --color-red-800: #991b1b; /* Added for infographic item */
















                    --color-yellow-100: #fef9c3;
                    --color-yellow-700: #a16207;
















                    --color-pink-100: #fce7f3;
                    --color-pink-700: #be185d;
















                    --color-orange-100: #ffedd5; /* Tailwind orange-100 */
                    --color-orange-200: #fed7aa; /* Tailwind orange-200 */
                    --color-orange-600: #ea580c; /* Tailwind orange-600 */
                    --color-orange-700: #c2410c; /* Tailwind orange-700 */
                    --color-orange-300: #fdba74; /* Added for infographic text */
                    --color-orange-400: #fb923c; /* Added for infographic text */
                    --color-orange-500: #f97316; /* Added for infographic border */
                    --color-orange-800: #9a3412; /* Added for infographic item */
















                    --color-fuchsia-300: #f0abfc; /* Added for infographic text */
                    --color-fuchsia-400: #e879f9; /* Added for infographic text */
                    --color-fuchsia-500: #d946ef; /* Added for infographic border */
















                    --color-gray-50: #f9fafb;
                    --color-gray-100: #f3f4f6;
                    --color-gray-200: #e5e7eb;
                    --color-gray-300: #d1d5db;
                    --color-gray-400: #9ca3af;
                    --color-gray-500: #6b7280;
                    --color-gray-600: #4b5563;
                    --color-gray-700: #374151;
                    --color-gray-800: #1f2937;
                    --color-gray-900: #111827; /* Added for infographic title */
















                    --color-gold: #FFD700;
                    --color-e2e8f0: #e2e8f0; /* For infographic default text */
















                    /* Spacing */
                    --spacing-px: 1px;
                    --spacing-0: 0px;
                    --spacing-0-5: 0.125rem; /* 2px */
                    --spacing-1: 0.25rem;    /* 4px */
                    --spacing-1-5: 0.375rem; /* 6px */
                    --spacing-2: 0.5rem;    /* 8px */
                    --spacing-2-5: 0.625rem; /* 10px */
                    --spacing-3: 0.75rem;    /* 12px */
                    --spacing-3-5: 0.875rem; /* 14px */
                    --spacing-4: 1rem;       /* 16px */
                    --spacing-5: 1.25rem;  /* 20px */
                    --spacing-6: 1.5rem;    /* 24px */
                    --spacing-8: 2rem;       /* 32px */
                    --spacing-10: 2.5rem;  /* 40px */
                    --spacing-12: 3rem;    /* 48px */
                    --spacing-16: 4rem;    /* 64px */
                    --spacing-20: 5rem;    /* 80px */
                    --spacing-24: 6rem;    /* 96px */
                    --spacing-32: 8rem;    /* 128px */
                    --spacing-40: 10rem;    /* 160px */
                    --spacing-48: 12rem;    /* 192px */
                    --spacing-52: 13rem;    /* 208px */
                    --spacing-56: 14rem;    /* 224px */
                    --spacing-60: 15rem;    /* 240px */
                    --spacing-64: 16rem;    /* 256px */
                    --spacing-72: 18rem;    /* 288px */
                    --spacing-80: 20rem;    /* 320px */
                    --spacing-96: 24rem;    /* 384px */

                    /* --- Add these styles for the Price Graph tab --- */
                    .price-graph-input-group {
                        display: flex;
                        gap: var(--spacing-2);
                        margin-bottom: var(--spacing-4);
                    }
                    .price-graph-input-group > .input-expressive {
                        flex-grow: 1;
                    }
                    .ticker-list {
                        display: flex;
                        flex-wrap: wrap;
                        gap: var(--spacing-2);
                    }
                    .ticker-badge {
                        display: flex;
                        align-items: center;
                        background-color: var(--color-blue-100);
                        color: var(--color-blue-800);
                        font-size: var(--font-size-sm);
                        font-weight: 500;
                        padding: var(--spacing-1-5) var(--spacing-3);
                        border-radius: var(--rounded-full);
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                    }
                    .ticker-badge button {
                        margin-left: var(--spacing-2);
                        color: var(--color-blue-600);
                        background: none;
                        border: none;
                        padding: 0;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                    }
                    .ticker-badge button:hover {
                        color: var(--color-blue-900);
                    }
                    .timeframe-buttons {
                        display: flex;
                        justify-content: center;
                        flex-wrap: wrap;
                        gap: var(--spacing-2);
                        margin-bottom: var(--spacing-6);
                    }
                    .timeframe-button, .timeframe-button-active {
                        padding: var(--spacing-2) var(--spacing-4);
                        border-radius: var(--rounded-lg);
                        font-size: var(--font-size-sm);
                        font-weight: 500;
                        transition: all 0.15s ease-in-out;
                        border: none;
                        cursor: pointer;
                    }
                    .timeframe-button {
                        background-color: var(--color-gray-200);
                        color: var(--color-gray-700);
                    }
                    .timeframe-button:hover {
                        background-color: var(--color-gray-300);
                    }
                    .timeframe-button-active {
                        background-color: var(--color-blue-500);
                        color: white;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    }
                    .stats-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: var(--spacing-4);
                        margin-bottom: var(--spacing-6);
                    }
                    .stat-card {
                        padding: var(--spacing-3);
                        border: 1px solid var(--color-gray-200);
                        border-radius: var(--rounded-lg);
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                        background-color: white;
                    }
                    .stat-card h3 {
                        font-size: var(--font-size-xl);
                        font-weight: 600;
                        color: var(--color-gray-800);
                        margin-bottom: var(--spacing-1);
                    }
                    .stat-card p:nth-of-type(1) {
                        font-size: var(--font-size-2xl);
                        font-weight: 700;
                        color: var(--color-gray-900);
                    }
                    .stat-card p:nth-of-type(2) {
                        font-size: var(--font-size-base);
                        font-weight: 500;
                    }














                    /* Font Sizes */
                    --font-size-xs: 0.75rem; /* 12px */
                    --font-size-sm: 0.875rem; /* 14px */
                    --font-size-base: 1rem;    /* 16px */
                    --font-size-lg: 1.125rem; /* 18px */
                    --font-size-xl: 1.25rem;  /* 20px */
                    --font-size-2xl: 1.5rem;  /* 24px */
                    --font-size-3xl: 1.875rem;/* 30px */
                    --font-size-4xl: 2.25rem; /* 36px */
                    --font-size-5xl: 3rem;    /* 48px */
                    --font-size-6xl: 3.75rem; /* 60px */
















                    /* Border Radius */
                    --rounded-none: 0px;
                    --rounded-sm: 0.125rem; /* 2px */
                    --rounded: 0.25rem;    /* 4px */
                    --rounded-md: 0.375rem; /* 6px */
                    --rounded-lg: 0.5rem;    /* 8px */
                    --rounded-xl: 0.75rem;  /* 12px */
                    --rounded-2xl: 1rem;    /* 16px */
                    --rounded-3xl: 1.5rem;  /* 24px */
                    --rounded-full: 9999px;
                }
















                /* --- Add these styles for the input icon and helper text --- */
                    .input-wrapper {
                        position: relative;
                        width: 100%;
                    }
















                    .input-icon {
                        position: absolute;
                        top: 50%;
                        right: var(--spacing-3);
                        transform: translateY(-50%);
                        pointer-events: none; /* Allows clicking through the icon */
                    }
















                    .helper-text {
                        font-size: var(--font-size-xs);
                        margin-top: var(--spacing-1-5);
                        padding-left: var(--spacing-1);
                    }








                /* --- High-Fidelity Calculated Risk Calculator Styles --- */
                    .rc-container {
                        font-family: 'Inter', sans-serif;
                        -webkit-font-smoothing: antialiased;
                        -moz-osx-font-smoothing: grayscale;
                        width: 100%;
                    }
                    .rc-layout-container {
                        background-color: white;
                        padding: var(--spacing-6);
                        border-radius: var(--rounded-xl);
                        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                        width: 100%;
                        max-width: 80rem; /* max-w-7xl */
                        margin: 0 auto;
                        display: flex;
                        flex-direction: column;
                        gap: var(--spacing-8);
                    }
                    .rc-left-column { flex: 1; }
                    .rc-right-column {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                    }
                    .rc-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-bottom: var(--spacing-8);
                    }
                    .rc-title {
                        font-size: var(--font-size-4xl);
                        font-weight: 800; /* extrabold */
                        color: var(--color-gray-900);
                        display: flex;
                        align-items: center;
                        gap: var(--spacing-3);
                    }
                    .rc-title-icon {
                        width: 40px;
                        height: 40px;
                        color: var(--color-red-600);
                    }
                    .rc-header-actions {
                        display: flex;
                        gap: var(--spacing-4);
                    }
                    .rc-infographic-button {
                        padding: var(--spacing-3);
                        border-radius: var(--rounded-full);
                        background-color: var(--color-red-500);
                        color: white;
                        border: none;
                        cursor: pointer;
                        box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1);
                        transition: all 0.2s ease-in-out;
                    }
                    .rc-infographic-button:hover { background-color: var(--color-red-600); }
                    .rc-infographic-button-icon { width: 24px; height: 24px; }
                    .rc-section-card {
                        background-color: white;
                        padding: var(--spacing-6);
                        border-radius: var(--rounded-xl);
                        box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1);
                        margin-bottom: var(--spacing-6);
                        border: 1px solid var(--color-gray-200);
                    }
                    .rc-section-title {
                        font-size: var(--font-size-xl);
                        font-weight: 700;
                        color: var(--color-gray-800);
                        margin-bottom: var(--spacing-4);
                        display: flex;
                        align-items: center;
                        gap: var(--spacing-2);
                    }
                    .rc-section-icon {
                        width: 24px;
                        height: 24px;
                        color: var(--color-blue-500);
                    }
                    .rc-icon-positive { color: var(--color-green-500); }
                    .rc-icon-negative { color: var(--color-red-500); }
                    .rc-ticker-group { margin-bottom: var(--spacing-6); }
                    .rc-input-with-button { display: flex; align-items: center; gap: var(--spacing-2); }
                    .rc-fetch-button {
                        padding: 0.75rem 1rem; /* px-4 py-3 */
                        background-color: var(--color-blue-600);
                        color: white;
                        border-radius: var(--rounded-lg);
                        border: none;
                        cursor: pointer;
                        box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1);
                        display: flex; align-items: center; justify-content: center;
                        transition: background-color 0.15s ease-in-out;
                    }
                    .rc-fetch-button:hover { background-color: var(--color-blue-700); }
                    .rc-fetch-button:disabled { opacity: 0.5; cursor: not-allowed; }
                    .rc-spinner {
                        animation: spin 1s linear infinite;
                        height: 20px; width: 20px; border: 2px solid white;
                        border-top-color: transparent; border-radius: 50%;
                    }
                    .rc-error-message { color: var(--color-red-500); font-size: var(--font-size-sm); margin-top: var(--spacing-2); }
                    .w-full { width: 100%; }
                    .rc-grid-container-2-col, .rc-grid-container-3-col { display: grid; gap: var(--spacing-6); grid-template-columns: repeat(1, 1fr); }
                    .rc-result-box {
                        padding: var(--spacing-3);
                        background-color: var(--color-blue-50);
                        border: 1px solid var(--color-blue-200);
                        border-radius: var(--rounded-lg);
                        color: var(--color-gray-800);
                        font-weight: 600;
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                    }
                    .rc-result-box-value { font-weight: 800; }
                    .rc-label { font-size: var(--font-size-sm); font-weight: 500; color: var(--color-gray-700); margin-bottom: var(--spacing-1); display: flex; align-items: center; gap: var(--spacing-1); }
                    .rc-info-icon { width: 16px; height: 16px; color: var(--color-gray-400); }
                    .rc-info-icon:hover { color: var(--color-blue-500); }
                    .rc-dividend-controls { grid-column: 1 / -1; display: flex; align-items: center; gap: var(--spacing-8); }
                    .rc-ttm-control { margin-top: var(--spacing-4); } /* Adjust alignment */
                    .rc-toggle-group { display: flex; background-color: var(--color-gray-200); border-radius: var(--rounded-lg); padding: var(--spacing-1); }
                    .rc-toggle-button {
                        width: 100%;
                        padding: 0.375rem 0; /* py-1.5 */
                        font-size: var(--font-size-sm);
                        font-weight: 600;
                        border-radius: var(--rounded-md);
                        transition: all 0.2s ease;
                        border: none;
                        background: transparent;
                        color: var(--color-gray-600);
                        cursor: pointer;
                    }
                    .rc-toggle-button:hover { background-color: var(--color-gray-300); }
                    .rc-toggle-button.active { background-color: var(--color-blue-600); color: white; box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05); }
                    .rc-switch-container { position: relative; display: inline-flex; align-items: center; height: 32px; width: 80px; cursor: pointer; }
                    .rc-switch-container.disabled { cursor: not-allowed; opacity: 0.5; }
                    .rc-switch-track { width: 50px; height: 28px; background-color: var(--color-gray-300); border-radius: 9999px; transition: background-color 0.2s ease-in-out; }
                    .rc-switch-track.active { background-color: var(--color-blue-600); }
                    .rc-switch-thumb { position: absolute; left: 2px; top: 2px; width: 24px; height: 24px; background-color: white; border-radius: 9999px; box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05); transition: transform 0.2s ease-in-out; }
                    .rc-switch-thumb.active { transform: translateX(22px); }
                    .rc-switch-label { display: none; } /* Simpler switch without text */
                    .rc-helper-text { font-size: var(--font-size-xs); margin-top: 0.375rem; padding-left: 0.25rem; }
                    .rc-helper-text-positive { color: var(--color-green-600); }
                    .rc-helper-text-negative { color: var(--color-red-600); }
                    .rc-summary-card { flex: 1; display: flex; flex-direction: column; }
                    .rc-v-stack { display: flex; flex-direction: column; gap: var(--spacing-4); }
                    .rc-confidence-text { font-size: var(--font-size-xs); color: var(--color-gray-500); text-align: center; margin-top: var(--spacing-2); }
                    .rc-value-positive { color: var(--color-green-600); }
                    .rc-value-negative { color: var(--color-red-600); }
                    .rc-value-neutral { color: var(--color-gray-900); }
                    .rc-totals-card { margin-top: auto; }
                    .rc-totals-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--spacing-4); }
                    .rc-totals-value { font-size: var(--font-size-3xl); font-weight: 800; }
                    @media (min-width: 640px) {
                        .rc-layout-container { padding: var(--spacing-8); }
                        .rc-grid-container-2-col { grid-template-columns: repeat(2, 1fr); }
                        .rc-grid-container-3-col { grid-template-columns: repeat(2, 1fr); }
                    }
                    @media (min-width: 768px) {
                        .rc-grid-container-3-col { grid-template-columns: repeat(3, 1fr); }
                    }
                    @media (min-width: 1024px) {
                        .rc-layout-container { flex-direction: row; }
                        .rc-left-column { padding-right: var(--spacing-4); }
                        .rc-right-column { padding-left: var(--spacing-4); }
                    }














                /* Base Styles */
                body {
                    margin: 0;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
                        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
                        sans-serif;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    color: var(--color-gray-800);
                }
















                code {
                    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
                }








                /* --- Add these styles for the compact tax dropdown --- */
                .tax-dropdown-wrapper {
                    position: relative;
                }




                .action-button.button-tax-selector {
                    background-color: var(--color-orange-100);
                    color: var(--color-orange-700);
                }
                .action-button.button-tax-selector:hover {
                    background-color: var(--color-orange-200);
                }




                .tax-dropdown-panel {
                    position: absolute;
                    bottom: 100%;
                    right: 0;
                    margin-bottom: var(--spacing-2);
                    background-color: white;
                    border-radius: var(--rounded-lg);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    z-index: 10;
                    width: 120px;
                    overflow: hidden;
                    border: 1px solid var(--color-gray-200);
                }




                .tax-dropdown-option {
                    padding: var(--spacing-2) var(--spacing-3);
                    font-size: var(--font-size-sm);
                    cursor: pointer;
                    transition: background-color 0.2s;
                }




                .tax-dropdown-option:hover {
                    background-color: var(--color-gray-100);
                }




                /* Utility Class Translations */
                .min-height-screen { min-height: 100vh; }
                .bg-gradient-br { background: linear-gradient(to bottom right, var(--color-blue-50), var(--color-indigo-100)); }
                .font-inter { font-family: 'Inter', sans-serif; }
                .text-gray-800 { color: var(--color-gray-800); }
                .flex-col-center { display: flex; flex-direction: column; align-items: center; justify-content: flex-start; }
                .padding-base { padding: var(--spacing-4); }
















                @media (min-width: 640px) { /* sm breakpoint */
                    .responsive-padding { padding: var(--spacing-6); }
                }
                @media (min-width: 1024px) { /* lg breakpoint */
                    .responsive-padding { padding: var(--spacing-8); }
                }
















                /* Main Title and Google Login Button Container */
                .main-title-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: var(--spacing-8);
                }
                @media (min-width: 640px) { /* sm breakpoint */
                    .main-title-container {
                        flex-direction: row;
                        justify-content: center;
                        gap: var(--spacing-4); /* Add space between title and button */
                    }
                }
















                /* Main Title */
                .main-title {
                    font-size: var(--font-size-4xl);
                    font-weight: 800; /* extrabold */
                    color: var(--color-blue-800);
                    letter-spacing: -0.025em; /* tracking-tight */
                    text-align: center;
                    filter: drop-shadow(0 4px 3px rgba(0, 0, 0, 0.07)) drop-shadow(0 2px 2px rgba(0, 0, 0, 0.06)); /* drop-shadow-md */
                    margin-bottom: var(--spacing-4); /* Space below title on small screens */
                }
                @media (min-width: 640px) { /* sm breakpoint */
                    .main-title {
                        font-size: var(--font-size-5xl);
                        margin-bottom: 0; /* Remove bottom margin when in row layout */
                    }
                }
                @media (min-width: 1024px) { /* lg breakpoint */
                    .main-title { font-size: var(--font-size-6xl); }
                }
















                /* Google Login Button Style */
                .google-login-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: white;
                    color: var(--color-gray-700);
                    font-weight: 600;
                    padding: var(--spacing-2) var(--spacing-4);
                    border-radius: var(--rounded-lg);
                    border: 1px solid var(--color-gray-300);
                    cursor: pointer;
                    transition: background-color 0.2s, box-shadow 0.2s;
                    white-space: nowrap;
                    font-size: var(--font-size-base);
                }
                .google-login-button:hover {
                    background-color: var(--color-gray-50);
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                }
                .google-login-button:active {
                    background-color: var(--color-gray-100);
                }
                .google-icon {
                    width: 20px; /* Adjust size as needed */
                    height: 20px;
                    margin-right: var(--spacing-2);
                }
































                /* Features Button Specific Styles */
                .features-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: var(--color-blue-600);
                    color: white;
                    font-weight: 600;
                    padding: var(--spacing-2) var(--spacing-4);
                    border-radius: var(--rounded-lg);
                    border: 1px solid var(--color-blue-700);
                    cursor: pointer;
                    transition: background-color 0.2s, box-shadow 0.2s;
                    white-space: nowrap;
                    font-size: var(--font-size-base);
                }
                .features-button:hover {
                    background-color: var(--color-blue-700);
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                }
                .features-button:active {
                    background-color: var(--color-blue-800);
                }
                .features-icon {
                    margin-right: var(--spacing-2);
                }
















                /* Features Modal Content Styling */
                .features-modal-content h3 {
                    font-size: var(--font-size-lg); /* Roughly text-lg */
                    font-weight: 700; /* font-bold */
                    margin-top: var(--spacing-4); /* mt-4 */
                    margin-bottom: var(--spacing-2); /* mb-2 */
                    color: var(--color-gray-800); /* text-gray-800 */
                }
                .features-modal-content h3:first-child {
                    margin-top: 0;
                }
                .features-modal-content ul {
                    list-style-type: disc;
                    padding-left: var(--spacing-5); /* pl-5 */
                    margin-bottom: var(--spacing-4); /* mb-4 */
                }
                .features-modal-content li {
                    font-size: var(--font-size-sm); /* text-sm */
                    color: var(--color-gray-700); /* text-gray-700 */
                    margin-bottom: var(--spacing-1); /* mb-1 */
                    line-height: 1.4; /* leading-tight */
                }
                .features-modal-content li strong {
                    font-weight: 600; /* font-semibold */
                    color: var(--color-gray-800);
                }
                /* --- M3 Button Styles --- */
                .m3-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-2);
                    padding: var(--spacing-2-5) var(--spacing-6);
                    border-radius: var(--rounded-full);
                    font-weight: 600;
                    font-size: var(--font-size-base);
                    transition: background-color 0.3s, box-shadow 0.3s, transform 0.1s;
                    border: 1px solid transparent;
                    cursor: pointer;
                    transform: scale(1);
                }
                .m3-button:hover {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }
                .m3-button:active {
                    transform: scale(0.98);
                }
                .m3-button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    box-shadow: none;
                }
                .m3-button.filled-blue {
                    background-color: var(--color-blue-600);
                    color: white;
                }
                .m3-button.filled-blue:hover {
                    background-color: var(--color-blue-700);
                }
                .m3-button.filled-emerald {
                    background-color: var(--color-emerald-700);
                    color: white;
                }
                .m3-button.filled-emerald:hover {
                    background-color: var(--color-emerald-800);
                }
                .m3-button.filled-danger {
                    background-color: var(--color-red-600);
                    color: white;
                }
                .m3-button.filled-danger:hover {
                    background-color: var(--color-red-700);
                }
















                /* --- STYLING FOR SELECT STOCKS TAB --- */
                .stock-card-blue { 
                    background-color: var(--color-blue-50); 
                    border: 1px solid var(--color-blue-100); 
                }
















                /* Card Styles */
                .card-expressive {
                    background-color: white;
                    border-radius: var(--rounded-3xl);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); /* shadow-xl */
                    border: 1px solid var(--color-gray-100);
                    padding: var(--spacing-6);
                }
                @media (min-width: 640px) { .card-expressive { padding: var(--spacing-8); } }
















                .card-header-flex {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    margin-bottom: var(--spacing-4); /* Reduced margin-bottom */
                    padding-bottom: var(--spacing-6); /* Kept for padding */
                    border-bottom: none; /* Removed border-bottom */
                }
                @media (min-width: 640px) { .card-header-flex { flex-direction: row; justify-content: center; align-items: center; } }
































                /* Tab Buttons */
                .tab-button-group {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: var(--spacing-3);
        padding: var(--spacing-2);
        background-color: var(--color-blue-100);
        border-radius: var(--rounded-2xl);
        box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
    }
















                .tab-button {
        padding: var(--spacing-2-5) var(--spacing-5);
        border-radius: var(--rounded-2xl);
        font-weight: 600;
        font-size: var(--font-size-base);
        transition: all 0.3s ease;
        cursor: pointer;
        border: none;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }
















    /* --- Styles for each tab --- */
















    /* Select Stocks Tab */
    .tab-button.tab-button-select-active { background-color: var(--color-blue-700); color: white; /* <-- Change Active Text Color Here */ }
    .tab-button.tab-button-select { color: var(--color-blue-800); background-color: transparent; /* <-- Change Inactive Text Color Here */ }
    
    /* Monthly Income Tab */
    .tab-button.tab-button-monthly-active { background-color: var(--color-green-700); color: white; /* <-- Change Active Text Color Here */ }
    .tab-button.tab-button-monthly { color: var(--color-green-800); background-color: transparent; /* <-- Change Inactive Text Color Here */ }
















    /* Margin Calculator Tab */
    .tab-button.tab-button-margin-active { background-color: var(--color-purple-600); color: white; /* <-- Change Active Text Color Here */ }
    .tab-button.tab-button-margin { color: var(--color-purple-800); background-color: transparent; /* <-- Change Inactive Text Color Here */ }
















    /* Price Graph Tab */
    .tab-button.tab-price-graph-active { background-color: var(--color-orange-600); color: white; /* <-- Change Active Text Color Here */ }
    .tab-button.tab-price-graph { color: var(--color-orange-700); background-color: transparent; /* <-- Change Inactive Text Color Here */ }
    
    /* Dividend History Tab */
    .tab-button.tab-dividend-history-active { background-color: var(--color-indigo-600); color: white; /* <-- Change Active Text Color Here */ }
    .tab-button.tab-dividend-history { color: var(--color-indigo-800); background-color: transparent; /* <-- Change Inactive Text Color Here */ }
















    /* Risk Calculator Tab */
    .tab-button.tab-risk-calculator-active { background-color: var(--color-red-600); color: white; /* <-- Change Active Text Color Here */ }
    .tab-button.tab-risk-calculator { color: var(--color-red-800); background-color: transparent; /* <-- Change Inactive Text Color Here */ }
















    /* YoY Compound Growth Tab */
    .tab-button.tab-yoy-growth-active { background-color: var(--color-emerald-700); color: white; /* <-- Change Active Text Color Here */ }
    .tab-button.tab-yoy-growth { color: var(--color-emerald-800); background-color: transparent; /* <-- Change Inactive Text Color Here */ }
















                .tab-button:hover { transform: scale(1.05); }
                .tab-button:active { transform: scale(0.95); }
















                /* Specific Tab Button Colors */
                .tab-button.tab-button-select-active { background-color: var(--color-blue-700); color: white; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
                .tab-button.tab-button-select { color: var(--color-blue-800); background-color: transparent; }
                .tab-button.tab-button-select:hover { background-color: var(--color-blue-200); }
















                .tab-button.tab-button-monthly-active { background-color: var(--color-green-700); color: white; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
                .tab-button.tab-button-monthly { color: var(--color-green-800); background-color: transparent; }
                .tab-button.tab-button-monthly:hover { background-color: var(--color-green-200); }
















                .tab-button.tab-button-margin-active { background-color: var(--color-purple-700); color: white; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
                .tab-button.tab-button-margin { color: var(--color-purple-800); background-color: transparent; }
                .tab-button.tab-button-margin:hover { background-color: var(--color-purple-200); }
































                /* Sub-Tab Buttons */
                .sub-tab-button-group { /* This class is now merged into .tab-button-group */
                    /* Removed specific styles that are now handled by .tab-button-group */
                }
















                .sub-tab-button { /* This class is now merged into .tab-button */
                    /* Removed specific styles that are now handled by .tab-button */
                }
                /* Specific Sub-Tab Button Colors */
                .tab-button.sub-tab-button-active { background-color: var(--color-orange-600); color: white; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
                .tab-button.sub-tab-button { color: var(--color-orange-700); background-color: transparent; }
                .tab-button.sub-tab-button:hover { background-color: var(--color-orange-200); }
































                /* Floating API Counter */
                .api-counter-container {
                    position: fixed;
                    bottom: var(--spacing-6);
                    left: var(--spacing-6);
                    z-index: 40;
                }
                .api-info-panel {
                    position: absolute;
                    bottom: 100%;
                    left: 0;
                    margin-bottom: var(--spacing-2);
                    background-color: white;
                    padding: var(--spacing-4);
                    border-radius: var(--rounded-2xl);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-4);
                    font-size: var(--font-size-base);
                    color: var(--color-gray-700);
                    border: 1px solid var(--color-gray-200);
                    transition: opacity 0.3s ease, visibility 0.3s ease;
                    white-space: nowrap;
                }
                @media (max-width: 767px) { 
                    .api-info-panel { 
                        flex-direction: column; 
                        gap: var(--spacing-3);
                        align-items: flex-start;
                    } 
                }

                .api-info-panel.hidden { opacity: 0; visibility: hidden; pointer-events: none; }
                .api-info-panel.visible { opacity: 1; visibility: visible; }
















                .api-calls-text {
                    font-weight: 600; /* font-semibold */
                    transition: color 0.3s;
                    cursor: pointer;
                }
                .api-calls-text.limit-warning { color: var(--color-red-600); animation: pulse 2s infinite; } /* animate-pulse */
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: .5; }
                }
















                .api-divider {
                    color: var(--color-gray-400);
                }
                @media (max-width: 767px) { .api-divider { display: none; } } /* hidden md:inline */
















                .api-button-group {
                    display: flex;
                    gap: var(--spacing-2);
                }
                .api-adjust-button {
                    padding: var(--spacing-2);
                    border-radius: var(--rounded-full);
                    background-color: var(--color-gray-100);
                    transition: all 0.2s ease;
                    transform: scale(1);
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
                    border: none;
                    outline: none;
                    cursor: pointer;
                }
                .api-adjust-button:hover { background-color: var(--color-gray-200); transform: scale(1.1); }
                .api-adjust-button:active { transform: scale(0.9); }
                .api-adjust-button:disabled { opacity: 0.5; cursor: not-allowed; }
































                /* Tab Content Area */
                .tab-content-area {
                    padding: var(--spacing-4) var(--spacing-6) var(--spacing-8) var(--spacing-6); /* p-4 sm:p-6 lg:p-8 pt-0 */
                    padding-top: 0;
                }
                @media (min-width: 640px) { .tab-content-area { padding-left: var(--spacing-6); padding-right: var(--spacing-6); } }
                @media (min-width: 1024px) { .tab-content-area { padding-left: var(--spacing-8); padding-right: var(--spacing-8); } }
































                /* Section Titles */
                .section-title {
                    font-size: var(--font-size-3xl);
                    font-weight: 700; /* font-bold */
                    color: var(--color-gray-700);
                    margin-bottom: var(--spacing-6);
                }
















                /* Empty State Message */
                .empty-message {
                    text-align: center;
                    color: var(--color-gray-500);
                    font-size: var(--font-size-lg);
                    padding: var(--spacing-8) var(--spacing-6);
                    border-radius: var(--rounded-2xl);
                    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06); /* shadow-inner */
                }
















                /* Individual Stock Card */
                .stock-card-container {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-4);
                }
                @media (min-width: 1024px) { /* lg breakpoint */
                    .stock-card-container { flex-direction: row; align-items: center; justify-content: space-between; gap: var(--spacing-6); }
                }
















                /* Specific Card Backgrounds */
                .stock-card-blue { background-color: var(--color-blue-50); border: 1px solid var(--color-blue-100); }
                .stock-card-green { background-color: var(--color-green-50); border: 1px solid var(--color-green-100); }
                .stock-card-purple { background-color: var(--color-purple-50); border: 1px solid var(--color-purple-100); }
                .stock-card-indigo { background-color: var(--color-indigo-50); border: 1px solid var(--color-indigo-100); }
































                /* Stock Info Grid */
                .stock-info-grid {
                    flex-grow: 1;
                    display: grid;
                    grid-template-columns: 1fr; /* Default to single column */
                    gap: var(--spacing-6);
                }
                @media (min-width: 640px) { /* sm breakpoint */
                    .stock-info-grid { grid-template-columns: repeat(2, 1fr); }
                }
                @media (min-width: 1024px) { /* lg breakpoint */
                    .stock-info-grid { grid-template-columns: repeat(3, 1fr); }
                }
                @media (min-width: 1280px) { /* xl breakpoint */
                    .stock-info-grid { grid-template-columns: repeat(4, 1fr); }
                }
















                .stock-info-item {
                    display: flex;
                    flex-direction: column;
                }
                .stock-label {
                    font-size: var(--font-size-sm);
                    font-weight: 500; /* font-medium */
                    color: var(--color-gray-600);
                    margin-bottom: var(--spacing-1);
                }
                .stock-value {
                    font-weight: 600; /* font-semibold */
                    font-size: var(--font-size-lg);
                    color: var(--color-gray-800);
                }
                .stock-value.blue { color: var(--color-blue-800); }
                .stock-value.purple { color: var(--color-purple-800); }
                .stock-value.green { color: var(--color-green-800); }
                .stock-value.red { color: var(--color-red-600); }
































                /* Input Styles */
                .input-expressive {
                    padding: var(--spacing-3);
                    border: 1px solid var(--color-gray-300);
                    border-radius: var(--rounded-xl);
                    transition: all 0.2s ease;
                }
                .input-expressive:focus {
                    outline: none;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2); /* ring-4 blue-200 */
                    border-color: var(--color-blue-500); /* border-blue-400 */
                }
                .input-expressive:disabled { opacity: 0.5; cursor: not-allowed; }
















                .stock-company-name {
                    font-size: var(--font-size-sm);
                    color: var(--color-gray-500);
                    margin-top: var(--spacing-1-5);
                    font-weight: 500; /* font-medium */
                }
















                /* Action Buttons Group */
                .action-buttons-group {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-3);
                    justify-content: flex-end;
                    align-items: flex-end;
                }
                @media (min-width: 640px) { /* sm breakpoint */
                    .action-buttons-group { flex-direction: row; gap: var(--spacing-3); }
                }
                @media (min-width: 1024px) { /* lg breakpoint */
                    .action-buttons-group { flex-direction: column; gap: var(--spacing-3); }
                }
















                .action-button {
                    padding: var(--spacing-2) var(--spacing-4);
                    font-size: var(--font-size-sm);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: var(--rounded-full);
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
                    font-weight: 500; /* font-medium */
                    border: none;
                    outline: none;
                    cursor: pointer;
                }
                .action-button:disabled { opacity: 0.5; cursor: not-allowed; }
















                .action-button-icon {
                    margin-right: var(--spacing-1-5);
                }
















                /* Specific Action Button Colors */
                .button-blue { background-color: var(--color-blue-100); color: var(--color-blue-700); }
                .button-blue:hover { background-color: var(--color-blue-200); }
                .button-purple { background-color: var(--color-purple-100); color: var(--color-purple-700); }
                .button-purple:hover { background-color: var(--color-purple-200); }
                .button-yellow { background-color: var(--color-yellow-100); color: var(--color-yellow-700); }
                .button-yellow:hover { background-color: var(--color-yellow-100); } /* yellow-200 */
                .button-pink { background-color: var(--color-pink-100); color: var(--color-pink-700); }
                .button-pink:hover { background-color: var(--color-pink-100); } /* pink-200 */
















                /* Delete button specific */
                .button-delete {
                    padding: var(--spacing-3); /* p-3 */
                    border-radius: var(--rounded-full);
                    background-color: var(--color-red-100);
                    color: var(--color-red-700);
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); /* shadow-md */
                    transform: scale(1);
                    border: none;
                    outline: none;
                    cursor: pointer;
                }
                .button-delete:hover { background-color: var(--color-red-200); transform: scale(1.1); }
                .button-delete:active { transform: scale(0.9); }
                .button-delete:disabled { opacity: 0.5; cursor: not-allowed; }
































                /* Global Call to Action Buttons */
                .global-action-buttons {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: var(--spacing-6);
                    margin-top: var(--spacing-10);
                }
















                .btn-expressive-primary {
                    background-color: var(--color-blue-600);
                    color: white;
                    font-weight: 600;
                    padding: var(--spacing-3) var(--spacing-6);
                    border-radius: var(--rounded-3xl);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    transform: scale(1);
                    border: none;
                    outline: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    white-space: nowrap; /* Prevent text wrapping */
                }
                .btn-expressive-primary:hover { background-color: var(--color-blue-700); transform: scale(1.05); }
                .btn-expressive-primary:active { transform: scale(0.95); }
                .btn-expressive-primary:disabled { opacity: 0.5; cursor: not-allowed; }
















                .btn-expressive-secondary {
                    background-color: var(--color-purple-600);
                    color: white;
                    font-weight: 600;
                    padding: var(--spacing-3) var(--spacing-6);
                    border-radius: var(--rounded-3xl);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    transform: scale(1);
                    border: none;
                    outline: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    white-space: nowrap; /* Prevent text wrapping */
                }
                .btn-expressive-secondary:hover { background-color: var(--color-purple-700); transform: scale(1.05); }
                .btn-expressive-secondary:active { transform: scale(0.95); }
                .btn-expressive-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
































                .btn-expressive-accent {
                    background-color: var(--color-green-600);
                    color: white;
                    font-weight: 600;
                    padding: var(--spacing-3) var(--spacing-6);
                    border-radius: var(--rounded-3xl);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    transform: scale(1);
                    border: none;
                    outline: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    white-space: nowrap; /* Prevent text wrapping */
                }
                .btn-expressive-accent:hover { background-color: var(--color-green-700); transform: scale(1.05); }
                .btn-expressive-accent:active { transform: scale(0.95); }
                .btn-expressive-accent:disabled { opacity: 0.5; cursor: not-allowed; }
































                .btn-expressive-danger {
                    background-color: var(--color-red-600);
                    color: white;
                    font-weight: 600;
                    padding: var(--spacing-3) var(--spacing-6);
                    border-radius: var(--rounded-3xl);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    transform: scale(1);
                    border: none;
                    outline: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    white-space: nowrap; /* Prevent text wrapping */
                }
                .btn-expressive-danger:hover { background-color: var(--color-red-700); transform: scale(1.05); }
                .btn-expressive-danger:active { transform: scale(0.95); }
                .btn-expressive-danger:disabled { opacity: 0.5; cursor: not-allowed; }
































                /* Icon spacing for buttons */
                .button-icon-margin-right { margin-right: var(--spacing-2); }
                .button-icon-margin-right-sm { margin-right: var(--spacing-1-5); }
                .button-icon-margin-top-negative { margin-top: -0.5px; }
































                /* Portfolio Summary */
                .portfolio-summary-card {
                    background-color: var(--color-purple-50);
                    border: 1px solid var(--color-purple-100);
                    margin-bottom: var(--spacing-8);
                }
                .portfolio-summary-title {
                    font-size: var(--font-size-2xl);
                    font-weight: 700;
                    color: var(--color-purple-700);
                    margin-bottom: var(--spacing-6);
                }
                .portfolio-summary-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: var(--spacing-4); /* gap-x-8 gap-y-4 */
                    font-size: var(--font-size-lg);
                }
                @media (min-width: 768px) { .portfolio-summary-grid { grid-template-columns: repeat(2, 1fr); gap: var(--spacing-4) var(--spacing-8); } }
















                .portfolio-summary-label { font-weight: 500; color: var(--color-gray-600); }
                .portfolio-summary-value { font-weight: 600; color: var(--color-gray-800); }
                .portfolio-summary-value.green { color: var(--color-green-800); }
                .portfolio-summary-value.red { color: var(--color-red-600); }
































                /* Margin Adjusted Dividends */
                .margin-adjust-section {
                    margin-top: var(--spacing-8);
                    padding-top: var(--spacing-6);
                    border-top: 1px solid var(--color-purple-200);
                }
                .margin-adjust-title {
                    font-size: var(--font-size-xl);
                    font-weight: 700;
                    color: var(--color-purple-600);
                    margin-bottom: var(--spacing-4);
                }
                .margin-adjust-description {
                    color: var(--color-gray-700);
                    margin-bottom: var(--spacing-4);
                    font-size: var(--font-size-sm);
                }
                .margin-adjust-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: var(--spacing-6);
                }
                @media (min-width: 768px) { .margin-adjust-grid { grid-template-columns: repeat(2, 1fr); } }
















                .margin-adjust-input-group {
                    display: flex;
                    flex-direction: column;
                }
                .margin-adjust-input-label {
                    display: block;
                    font-size: var(--font-size-sm);
                    font-weight: 500;
                    color: var(--color-gray-600);
                    margin-bottom: var(--spacing-1);
                }
















                /* Tax Rate Toggle */
                .tax-toggle-group {
                    margin-top: var(--spacing-6);
                }
                .tax-toggle-label {
                    display: block;
                    font-size: var(--font-size-lg);
                    font-weight: 500;
                    color: var(--color-gray-700);
                    margin-bottom: var(--spacing-2);
                }
                .tax-toggle-buttons {
                    display: flex;
                    flex-wrap: wrap;
                    gap: var(--spacing-2);
                }
                .button-tax-option {
                    padding: var(--spacing-2) var(--spacing-4);
                    border-radius: var(--rounded-lg);
                    font-weight: 500;
                    transition: all 0.2s ease;
                    border: none;
                    outline: none;
                    cursor: pointer;
                    background-color: var(--color-orange-100);
                    color: var(--color-orange-700);
                }
                .button-tax-option:hover { background-color: var(--color-orange-200); }
                .button-tax-option.active { background-color: var(--color-orange-600); color: var(--color-gray-800); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
































                .margin-adjust-results {
                    margin-top: var(--spacing-6);
                    gap: var(--spacing-3);
                    display: flex;
                    flex-direction: column;
                }
                .margin-adjust-results p {
                    font-size: var(--font-size-base);
                }
                .margin-adjust-results .label { font-weight: 500; color: var(--color-gray-600); }
                .margin-adjust-results .value { font-weight: 600; }
                .margin-adjust-results .value.red { color: var(--color-red-600); }
                .margin-adjust-results .value.green { color: var(--color-green-800); }
































                /* Detailed Tiered Interest Rates */
                .tiered-interest-section {
                    margin-top: var(--spacing-8);
                    padding-top: var(--spacing-6);
                    border-top: 1px solid var(--color-indigo-200);
                }
                .tiered-interest-title {
                    font-size: var(--font-size-xl);
                    font-weight: 700;
                    color: var(--color-indigo-600);
                    margin-bottom: var(--spacing-4);
                }
                .tiered-interest-list {
                    gap: var(--spacing-3);
                    display: flex;
                    flex-direction: column;
                }
                .tiered-interest-item {
                    display: flex;
                    flex-direction: column;
                    padding: var(--spacing-3);
                    background-color: var(--color-indigo-100);
                    border-radius: var(--rounded-lg);
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
                    border: 1px solid var(--color-indigo-200);
                }
                .tiered-interest-range {
                    font-weight: 600;
                    color: var(--color-gray-700);
                    font-size: var(--font-size-base);
                    margin-bottom: var(--spacing-px); /* mb-1 */
                }
                .tiered-interest-detail {
                    margin-left: var(--spacing-4);
                    font-size: var(--font-size-sm);
                    list-style-type: disc;
                    list-style-position: inside;
                    gap: var(--spacing-0-5);
                    display: flex;
                    flex-direction: column;
                }
                .tiered-interest-detail li {
                    margin-bottom: 0.125rem; /* For some vertical spacing */
                }
                .tiered-interest-detail .label { font-weight: 500; }
                .tiered-interest-detail .value { color: var(--color-gray-600); font-weight: 500; margin-left: var(--spacing-2); }
                .tiered-interest-note {
                    font-size: var(--font-size-xs);
                    color: var(--color-gray-500);
                    margin-top: var(--spacing-4);
                }
















                /* D3 Chart Specific Styles */
                .relative-chart-container {
                    position: relative;
                    width: 100%;
                    height: 500px; /* Increased height */
                    background-color: var(--color-gray-50);
                    padding: var(--spacing-2);
                    border-radius: var(--rounded-lg);
                }
                .chart-svg-full { width: 100%; height: 100%; }
                .bar-chart-bar { fill: var(--color-blue-500); transition: fill 0.2s; }
                .bar-chart-bar:hover { fill: var(--color-blue-700); }
                .price-chart-line { fill: none; stroke: steelblue; stroke-width: 1.5px; }
                .axis line, .axis path { stroke: #d4d4d4; }
                .axis text { fill: #6b7280; } /* text-gray-500 */
































                /* Modal Specific Styles (previously Tailwind) */
                .fixed-overlay {
                    position: fixed;
                    inset: 0; /* top:0; right:0; bottom:0; left:0; */
                    z-index: 50;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: rgba(75, 85, 99, 0.75); /* bg-gray-600 bg-opacity-75 */
                    padding: var(--spacing-4);
                }
















                .modal-content {
                    max-width: 500px; /* max-w-xl */
                    width: 100%;
                    padding: var(--spacing-6);
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    max-height: 90vh; /* max-h-[90vh] */
                }
















                .modal-close-button {
                    position: absolute;
                    top: var(--spacing-3);
                    right: var(--spacing-3);
                    color: var(--color-gray-500);
                    transition: color 0.3s, background-color 0.3s;
                    border-radius: var(--rounded-full);
                    padding: var(--spacing-px);
                    border: none;
                    outline: none;
                    cursor: pointer;
                    background-color: transparent;
                }
                .modal-close-button:hover {
                    color: var(--color-gray-700);
                    background-color: var(--color-gray-100);
                }
















                .modal-title {
                    font-size: var(--font-size-xl);
                    font-weight: 700;
                    color: var(--color-gray-800);
                    margin-bottom: var(--spacing-4);
                    padding-right: var(--spacing-10);
                }
















                .modal-message {
                    color: var(--color-gray-600);
                    margin-bottom: var(--spacing-4);
                    overflow-y: auto;
                }
















                .modal-body {
                    flex-grow: 1;
                    overflow-y: auto;
                    padding-right: var(--spacing-2);
                    color: var(--color-gray-700);
                }
                .modal-body p { margin-bottom: var(--spacing-2); }
                .modal-body .font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
                .modal-body .text-blue-600 { color: var(--color-blue-600); }
                .modal-body .break-all { word-break: break-all; }
                .modal-body .margin-bottom-2 { margin-bottom: var(--spacing-2); }
                .modal-body .margin-bottom-4 { margin-bottom: var(--spacing-4); }
                .modal-body a { color: var(--color-blue-500); text-decoration: none; }
                .modal-body a:hover { text-decoration: underline; }
















                /* API Logs specific modal styles */
                .api-logs-container {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-3);
                    max-height: 384px; /* max-h-96 */
                    overflow-y: auto;
                    font-size: var(--font-size-sm);
                }
















                .api-logs-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: var(--spacing-4);
                }
                @media (min-width: 768px) { .api-logs-grid { grid-template-columns: repeat(2, 1fr); } }
















                .api-log-item {
                    padding: var(--spacing-3);
                    background-color: var(--color-gray-50);
                    border-radius: var(--rounded-lg);
                    border: 1px solid var(--color-gray-100);
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }
                .api-log-item p { margin: 0; } /* Override default p margin in modal-body */
                .api-log-item .font-semibold { font-weight: 600; }
                .api-log-item .text-gray-700 { color: var(--color-gray-700); }
                .api-log-item .text-green-600 { color: var(--color-green-600); }
                .api-log-item .text-red-600 { color: var(--color-red-600); }
                .api-log-item .font-bold { font-weight: 700; }
                .api-log-item .text-xs { font-size: var(--font-size-xs); }
                .api-log-item .text-gray-600 { color: var(--color-gray-600); }
                /* Removed overflow-hidden, text-ellipsis, and whitespace-nowrap */
                /* to allow text to wrap and remove horizontal scrollbar */
                // .api-log-item .overflow-hidden { overflow: hidden; }
                // .api-log-item .text-ellipsis { text-overflow: ellipsis; }
                // .api-log-item .whitespace-nowrap { white-space: nowrap; }
















                .api-logs-buttons-container {
                    margin-top: var(--spacing-4);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: var(--spacing-3);
                }
                .api-logs-buttons-container .btn-expressive-primary,
                .api-logs-buttons-container .btn-expressive-secondary {
                    padding: var(--spacing-2) var(--spacing-4);
                    font-size: var(--font-size-sm);
                }
































                /* TSR Calculator Specific Styles */
                .spacing-y-6 > * + * { margin-top: var(--spacing-6); } /* Replicates Tailwind's space-y-6 */
                .text-sm { font-size: var(--font-size-sm); }
                .font-semibold { font-weight: 600; }
                .text-gray-700 { color: var(--color-gray-700); }
















                .flex-wrap-center { display: flex; flex-wrap: wrap; justify-content: center; }
                .gap-2 { gap: var(--spacing-2); }
















                .button-period, .button-reinvest, .button-tax {
                    padding: var(--spacing-2) var(--spacing-4);
                    border-radius: var(--rounded-lg);
                    font-weight: 500;
                    transition: all 0.2s ease;
                    border: none;
                    outline: none;
                    cursor: pointer;
                }
















                .button-period { background-color: var(--color-indigo-100); color: var(--color-indigo-700); }
                .button-period:hover { background-color: var(--color-indigo-200); }
                .button-period-active { background-color: var(--color-indigo-600); color: var(--color-gray-800); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
















                .margin-top-4 { margin-top: var(--spacing-4); }
















                .button-reinvest { background-color: var(--color-emerald-100); color: var(--color-emerald-700); } /* emerald-100 */
                .button-reinvest:hover { background-color: var(--color-emerald-200); }
                .button-reinvest-active { background-color: var(--color-green-600); color: var(--color-gray-800); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
















                .button-tax { background-color: var(--color-orange-100); color: var(--color-orange-700); }
                .button-tax:hover { background-color: var(--color-orange-200); }
                .button-tax-active { background-color: var(--color-orange-600); color: var(--color-gray-800); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
















                .margin-top-8 { margin-top: var(--spacing-8); }
                .text-center { text-align: center; }
                .text-red-500 { color: var(--color-red-500); }
                .margin-bottom-4 { margin-bottom: var(--spacing-4); }
                .text-2xl { font-size: var(--font-size-2xl); }
                .font-bold { font-weight: 700; }
                .margin-left-2 { margin-left: var(--spacing-2); }
                .text-green-600 { color: var(--color-green-600); }
                .text-red-600 { color: var(--color-red-600); }
                .text-lg { font-size: var(--font-size-lg); }
                .text-md { font-size: var(--font-size-base); }
































                /* Material-UI-like Toggle Switch Styles */
                .mu-switch-wrapper {
                    display: inline-flex;
                    align-items: center;
                    position: relative;
                    vertical-align: middle;
                    cursor: pointer;
                }
















                .mu-switch-input {
                    position: absolute;
                    width: 38px; /* Standard Mui switch track width */
                    height: 20px; /* Standard Mui switch track height */
                    top: 0;
                    left: 0;
                    opacity: 0;
                    z-index: 1; /* To make it clickable */
                    cursor: inherit;
                }
















                .mu-switch-track {
                    width: 38px;
                    height: 20px;
                    border-radius: 10px;
                    background-color: #bdbdbd; /* Light grey for off state */
                    transition: background-color 0.15s ease-in-out;
                    display: block; /* Ensure it takes up space */
                    position: relative; /* Needed for thumb positioning */
                }
















                .mu-switch-thumb {
                    position: absolute;
                    top: 2px;
                    left: 2px;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background-color: white;
                    box-shadow: 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12);
                    transition: transform 0.15s ease-in-out;
                }
















                /* When the actual (hidden) input is checked */
                .mu-switch-input:checked + .mu-switch-track {
                    background-color: var(--color-gold); /* Gold color when on */
                }
                .mu-switch-input:checked + .mu-switch-track .mu-switch-thumb {
                    transform: translateX(18px); /* Move thumb to the right */
                }

                /* --- Add these styles for the Dividend History tab --- */
                    .dividend-table-wrapper {
                        margin-top: var(--spacing-8);
                        padding: var(--spacing-4);
                        border: 1px solid var(--color-gray-200);
                        border-radius: var(--rounded-lg);
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                        background-color: white;
                    }
                    .dividend-table-title {
                        font-size: var(--font-size-xl);
                        font-weight: 600; /* semibold */
                        color: var(--color-gray-800);
                        margin-bottom: var(--spacing-4);
                        text-align: center;
                    }
                    .dividend-table-selector-group {
                        margin-bottom: var(--spacing-4);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: var(--spacing-2);
                    }
                    .dividend-table-selector-group label {
                        color: var(--color-gray-700);
                        font-weight: 500;
                    }
                    .dividend-table-selector-group select {
                        padding: var(--spacing-2);
                        border: 1px solid var(--color-gray-300);
                        border-radius: var(--rounded-md);
                        background-color: white;
                        color: var(--color-gray-800);
                    }
                    .dividend-table-container {
                        overflow-x: auto;
                    }
                    .dividend-table {
                        min-width: 100%;
                        border-collapse: collapse;
                    }
                    .dividend-table thead {
                        background-color: var(--color-gray-100);
                    }
                    .dividend-table th {
                        padding: var(--spacing-2) var(--spacing-4);
                        text-align: left;
                        font-size: var(--font-size-xs);
                        font-weight: 500;
                        color: var(--color-gray-500);
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }
                    .dividend-table tbody {
                        background-color: white;
                        border-top: 1px solid var(--color-gray-200);
                    }
                    .dividend-table td {
                        padding: var(--spacing-2) var(--spacing-4);
                        white-space: nowrap;
                        font-size: var(--font-size-sm);
                        color: var(--color-gray-500);
                        border-top: 1px solid var(--color-gray-200);
                    }
                    .dividend-table td:first-child {
                        color: var(--color-gray-900);
                    }


                /* --- YoY Compound Growth Calculator Styles --- */
                    .yoy-container {
                        /* spacing-y-8 */
                    }
                    .yoy-container > * + * {
                        margin-top: var(--spacing-8);
                    }
                    .yoy-card {
                        /* card-expressive max-w-xl mx-auto */
                        max-width: 48rem; /* 768px */
                        margin-left: auto;
                        margin-right: auto;
                    }
                    .yoy-form-container {
                        /* space-y-4 mb-6 */
                        margin-bottom: var(--spacing-6);
                    }
                    .yoy-form-container > * + * {
                        margin-top: var(--spacing-4);
                    }
                    .yoy-form-group {
                        /* No direct equivalent, but used for structure */
                    }
                    .yoy-label {
                        /* block text-gray-700 font-medium mb-1 */
                        display: block;
                        color: var(--color-gray-700);
                        font-weight: 500;
                        margin-bottom: var(--spacing-1);
                    }
                    .yoy-error-message {
                        /* text-red-500 text-sm mb-4 text-center */
                        color: var(--color-red-500);
                        font-size: var(--font-size-sm);
                        margin-bottom: var(--spacing-4);
                        text-align: center;
                    }
                    .yoy-button-container {
                        /* flex justify-center mb-8 */
                        display: flex;
                        justify-content: center;
                        margin-bottom: var(--spacing-8);
                    }
                    .yoy-results-container {
                        /* overflow-x-auto border border-gray-200 rounded-lg shadow-sm */
                        overflow-x: auto;
                        border: 1px solid var(--color-gray-200);
                        border-radius: var(--rounded-lg);
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                    }
                    .yoy-table {
                        /* min-w-full divide-y divide-gray-200 */
                        min-width: 100%;
                        border-collapse: separate;
                        border-spacing: 0;
                    }
                    .yoy-table-head {
                        /* bg-gray-50 */
                        background-color: var(--color-gray-50);
                    }
                    .yoy-table-header {
                        /* px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider */
                        padding: var(--spacing-2) var(--spacing-4);
                        text-align: left;
                        font-size: var(--font-size-xs);
                        font-weight: 500;
                        color: var(--color-gray-500);
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }
                    .yoy-table-body {
                        /* bg-white divide-y divide-gray-200 */
                        background-color: white;
                    }
                    .yoy-table-body tr + tr {
                        border-top: 1px solid var(--color-gray-200);
                    }
                    .yoy-table-row--highlight {
                        background-color: #FEF08A; /* Tailwind yellow-200 */
                    }
                    .yoy-table-cell {
                        /* px-4 py-2 whitespace-nowrap text-sm text-gray-500 */
                        padding: var(--spacing-2) var(--spacing-4);
                        white-space: nowrap;
                        font-size: var(--font-size-sm);
                        color: var(--color-gray-500);
                    }
                    .yoy-table-cell--year {
                        /* text-gray-900 font-medium */
                        color: var(--color-gray-900);
                        font-weight: 500;
                    }
                    .yoy-table-cell--ending {
                        /* font-bold */
                        font-weight: 700;
                    }










                /* Investment Payback Tab */
                .tab-button.tab-button-payback-active { background-color: var(--color-fuchsia-500); color: white; }
                .tab-button.tab-button-payback { color: var(--color-purple-800); background-color: transparent; }


                /* Focus styles for accessibility */
                .mu-switch-input:focus-visible + .mu-switch-track {
                    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.5); /* purple-500 with opacity */
                }
                /* Additional classes for styling not present in variables */
                .full-width { width: 100%; }
                .max-w-6xl { max-width: 72rem; } /* Assuming 1rem = 16px, 72*16 = 1152px */
                .padding-top-0 { padding-top: 0; }
                .flex-between { display: flex; justify-content: space-between; align-items: center; }
                .margin-x-auto { margin-left: auto; margin-right: auto; }
                .max-w-lg { max-width: 32rem; } /* 512px */
                .text-base { font-size: var(--font-size-base); }
                .text-xl { font-size: var(--font-size-xl); }
                .font-medium { font-weight: 500; }
                .margin-left-3 { margin-left: var(--spacing-3); }
                .block { display: block; }
                .select-none { user-select: none; }
                .list-inside { list-style-position: inside; }
                .overflow-x-auto { overflow-x: auto; }
                .rounded-md { border-radius: var(--rounded-md); }
                .padding-3 { padding: var(--spacing-3); }
                .bg-gray-100 { background-color: var(--color-gray-100); }
                .hover-bg-gray-200:hover { background-color: var(--color-gray-200); }
                .hover-bg-gray-300:hover { background-color: var(--color-gray-300); }
                .transition-all-200 { transition: all 0.2s ease; }
                .transform-hover-scale-110:hover { transform: scale(1.1); }
                .active-scale-90:active { transform: scale(0.9); }
                .flex-align-center { display: flex; align-items: center; }
                .disabled-opacity-50:disabled { opacity: 0.5; }
                .disabled-cursor-not-allowed:disabled { cursor: not-allowed; }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: var(--color-gray-400) var(--color-gray-100);
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: var(--color-gray-100);
                    border-radius: var(--rounded-full);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: var(--color-gray-400);
                    border-radius: var(--rounded-full);
                    border: 2px solid var(--color-gray-100);
                }
                `}
            </style>
















            {/* Main Title and Buttons Container */}
            <div className="main-title-container">
                <h1 className="main-title">Dividend Researcher</h1>
















                {/* NEW: Features Button */}
                <button
                    onClick={handleFeaturesClick}
                    className="features-button"
                    title="View App Features"
                >
                    <Info size={16} className="features-icon" />
                    Features
                </button>
            </div>
































            <div className="full-width max-w-6xl card-expressive responsive-padding padding-top-0">
                <div className="card-header-flex">
                    <div className="tab-button-group">
                        <button
                            onClick={() => setActiveTab('select')}
                            className={`tab-button tab-button-select ${activeTab === 'select' ? 'tab-button-select-active' : ''}`}
                        >
                            <Rocket size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            Select Stocks
                        </button>
                        <button
                            onClick={() => setActiveTab('monthly')}
                            className={`tab-button tab-button-monthly ${activeTab === 'monthly' ? 'tab-button-monthly-active' : ''}`}
                        >
                            <LayoutDashboard size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            Monthly Income
                        </button>
                        <button
                            onClick={() => setActiveTab('marginCalc')}
                            className={`tab-button tab-button-margin ${activeTab === 'marginCalc' ? 'tab-button-margin-active' : ''}`}
                        >
                            <Info size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            Margin Calculator
                        </button>
                        <button
                            onClick={() => setActiveTab('priceGraph')}
                            className={`tab-button tab-price-graph ${activeTab === 'priceGraph' ? 'tab-price-graph-active' : ''}`}
                        >
                            <ChartColumn size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            Price Graph
                        </button>
                        <button
                            onClick={() => setActiveTab('dividendHistory')}
                            className={`tab-button tab-dividend-history ${activeTab === 'dividendHistory' ? 'tab-dividend-history-active' : ''}`}
                        >
                            <Receipt size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            Dividend History
                        </button>
                        <button
                            onClick={() => setActiveTab('riskCalculator')}
                            className={`tab-button tab-risk-calculator ${activeTab === 'riskCalculator' ? 'tab-risk-calculator-active' : ''}`}
                        >
                            <OctagonAlert size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            Risk Calculator
                        </button>
                        <button
                            onClick={() => setActiveTab('yoyCompoundGrowth')}
                            className={`tab-button tab-yoy-growth ${activeTab === 'yoyCompoundGrowth' ? 'tab-yoy-growth-active' : ''}`}
                        >
                            <BanknoteArrowUp size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            YoY Compound Growth
                        </button>
                        <button
                            onClick={() => setActiveTab('investmentPayback')}
                            className={`tab-button tab-button-payback ${activeTab === 'investmentPayback' ? 'tab-button-payback-active' : ''}`}
                        >
                            <Wallet size={18} className="button-icon-margin-right button-icon-margin-top-negative" />
                            Investment Payback
                        </button>
                    </div>
                </div>
































                {/* Floating API Counter - Always Visible */}
                <div className="api-counter-container">
                    <button
                        onClick={() => setShowApiCounter(prev => !prev)}
                        className="api-toggle-button"
                        title={showApiCounter ? "Hide API Counter" : "Show API Counter"}
                    >
                        {showApiCounter ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                    <div className={`api-info-panel ${showApiCounter ? 'visible' : 'hidden'}`}>
                        <span className="api-calls-text" onClick={handleApiWordClick}>
                            API Settings (Triple Click)
                        </span>
                        <span className="api-divider">|</span>
                        <span className="font-semibold text-gray-700">Reset in: {apiResetCountdown}</span>

                        <div className="api-button-group">
                            <button
                                onClick={async () => {
                                    if (!isAuthReady || !userId) {
                                        setModalTitle('App Not Ready');
                                        setModalMessage('Please wait for the app to finish loading.');
                                        setShowModal(true);
                                        return;
                                    }
                                    // Load API logs from localStorage
                                    const logs = loadFromLocalStorage(`${appId}_users_${userId}_api_logs`, []);
                                    logs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
                                    setApiLogsData(logs);
                                    setModalTitle('API Call Logs');
                                    setModalMessage(null);
                                    setModalContent(
                                        <div className="api-logs-container custom-scrollbar">
                                            {apiLogsData.length > 0 ? (
                                                <div className="api-logs-grid">
                                                    {apiLogsData.map((log, index) => (
                                                        <div key={index} className="api-log-item">
                                                            <div>
                                                                <p><span className="font-semibold text-gray-700">Time:</span> {new Date(log.timestamp).toLocaleString()}</p>
                                                                <p><span className="font-semibold text-gray-700">Type:</span> {log.type}</p>
                                                                <p><span className="font-semibold text-gray-700">Ticker:</span> {log.ticker}</p>
                                                                <p><span className="font-semibold text-gray-700">Status:</span> <span className={`${log.status === 'success' ? 'text-green-600' : 'text-red-600'} font-bold`}>{log.status.toUpperCase()}</span></p>
                                                            </div>
                                                            <p className="text-gray-600 margin-top-2 text-xs"><span className="font-semibold">Details:</span> {log.details}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-center text-gray-500 padding-y-4">No API logs available.</p>
                                            )}
                                            <div className="api-logs-buttons-container">
                                                <button
                                                    onClick={() => {
                                                        const logText = apiLogsData.map(log => `Time: ${new Date(log.timestamp).toLocaleString()}\nType: ${log.type}\nTicker: ${log.ticker}\nStatus: ${log.status.toUpperCase()}\nDetails: ${log.details}\n---`).join('\n');
                                                        const blob = new Blob([logText], { type: 'text/plain;charset=utf-8' });
                                                        const url = URL.createObjectURL(blob);
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.download = 'api_logs.txt';
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                        URL.revokeObjectURL(url);
                                                    }}
                                                    className="btn-expressive-primary button-padding-sm disabled-opacity-50 disabled-cursor-not-allowed"
                                                    disabled={apiLogsData.length === 0}
                                                >
                                                    <Download size={16} className="button-icon-margin-right-sm" />
                                                    <span>Download Log</span>
                                                </button>

                                                <button
                                                    onClick={clearAllAppData}
                                                    className="btn-expressive-danger button-padding-sm"
                                                    disabled={!isAuthReady}
                                                >
                                                    <Trash2 size={16} className="button-icon-margin-right-sm" /> Clear All App Data
                                                </button>

                                                <p className="text-sm text-gray-600 text-center margin-top-4">
                                                    Local Storage Usage: <span className="font-semibold">{localStorageSize}</span>
                                                </p>
                                            </div>
                                        </div>
                                    );
                                    setShowModal(true);
                                }}
                                className="api-adjust-button"
                                disabled={!isAuthReady}
                                title="View API Logs"
                            >
                                <Info size={18} />
                            </button>
                        </div>
                    </div>
                </div>
















































                {/* Tab Content */}
                <div className="tab-content-area">
                    {activeTab === 'select' && (
                        <SelectStocks
                            selectStocks={selectStocks}
                            isAuthReady={isAuthReady}
                            isLoading={isLoading}
                            isAiSummaryLoading={isAiSummaryLoading}
                            handleTickerEnter={handleTickerEnter}
                            handleStockInputChange={handleStockInputChange}
                            handleAmountInvestedSave={handleAmountInvestedSave}
                            showHistoricalPriceGraph={showHistoricalPriceGraph}
                            showHistoricalDividendsGraph={showHistoricalDividendsGraph}
                            getAiSummary={getAiSummary}
                            deleteStockRow={deleteStockRow}
                            addStockRow={addStockRow}
                            resetTabData={resetTabData}
                            setModalTitle={setModalTitle}
                            setModalMessage={setModalMessage}
                            setModalContent={setModalContent}
                            setShowModal={setShowModal}
                        />
                    )}








                    {activeTab === 'monthly' && (
                        <MonthlyIncomeCalculator
                            isAuthReady={isAuthReady}
                            userId={userId}
                            monthlyStocks={monthlyStocks}
                            showMonthlyMarginCalc={showMonthlyMarginCalc}
                            setShowMonthlyMarginCalc={setShowMonthlyMarginCalc}
                            marginBalance={marginBalance}
                            setMarginBalance={setMarginBalance}
                            isGoldMember={isGoldMember}
                            setIsGoldMember={setIsGoldMember}
                            calculatedInterest={calculatedInterest}
                            isLoading={isLoading}
                            handleStockInputChange={handleStockInputChange}
                            handleTickerEnter={handleTickerEnter}
                            handleAmountInvestedSave={handleAmountInvestedSave}
                            showHistoricalDividendsGraph={showHistoricalDividendsGraph}
                            deleteStockRow={deleteStockRow}
                            addStockRow={addStockRow}
                            resetTabData={resetTabData}
                            updateStocksInLocalStorage={updateStocksInLocalStorage}
                            getAiSummary={getAiSummary}
                            isOptimizeOn={isOptimizeOn}                 // Pass renamed state
                            handleOptimizeToggle={handleOptimizeToggle} // Pass renamed handler
                            // NEW: Pass modal functions
                            setShowModal={setShowModal}
                            setModalTitle={setModalTitle}
                            setModalMessage={setModalMessage}
                        />
                    )}




                    {activeTab === 'marginCalc' && (
                        <MarginCalculator
                            isAuthReady={isAuthReady}
                            marginBalance={marginBalance}
                            setMarginBalance={setMarginBalance}
                            isGoldMember={isGoldMember}
                            setIsGoldMember={setIsGoldMember}
                            calculatedInterest={calculatedInterest}
                            setModalTitle={setModalTitle}
                            setModalMessage={setModalMessage}
                            setModalContent={setModalContent}
                            setShowModal={setShowModal}
                        />
                    )}

                    {activeTab === 'priceGraph' && (
                        <PriceGraph
                            isAuthReady={isAuthReady}
                            userId={userId}
                            fetchFmpData={fetchStockData} // Updated prop value
                            stockCache={stockCache}
                        />
                    )}
                    {activeTab === 'dividendHistory' && (
                        <DividendHistoryGraph
                            isAuthReady={isAuthReady}
                            userId={userId}
                            fetchFmpData={fetchStockData} // Updated prop value
                            stockCache={stockCache}
                        />
                    )}
                    {activeTab === 'riskCalculator' && (
                        <CalculatedRiskCalculator
                            isAuthReady={isAuthReady}
                            userId={userId}
                            appId={appId}
                            fetchFmpData={fetchStockData} // Updated prop value
                        />
                    )}
                    {activeTab === 'yoyCompoundGrowth' && (
                        <YoYCompoundGrowthCalculator
                            isAuthReady={isAuthReady}
                            userId={userId}
                            appId={appId}
                        />
                    )}
                    {activeTab === 'investmentPayback' && (
                        <InvestmentPaybackCalculator
                            isAuthReady={isAuthReady}
                            userId={userId}
                            appId={appId}
                            fetchFmpData={fetchStockData} // Updated prop value
                        />
                    )}
                </div>
            </div>
















            <Modal
                show={showModal}
                title={modalTitle}
                message={modalMessage}
                onClose={() => {
                    setShowModal(false);
                    setModalContent(null); // Clear custom content when modal closes
                }}
            >
                {modalContent}
            </Modal>
        </div>
    );
}
















export default App;